// script.js - Facio MGA Developer Portal & Agent Gateway Engine
import {
  ACCESS_HIERARCHY,
  checkAccess,
  getAccessBadgeHTML,
  getGatedOverlayHTML,
  getItemAccess as resolveItemAccess,
  normalizeAccessTier
} from "./src/access";
import { createIdempotencyKey, runSandboxRequest } from "./src/console";
import {
  sicClassificationEndpoints,
  sicClassificationGuide
} from "./src/data/sic-classification";

let activeAccessLevel = "public"; // "public", "sandbox", "partner", "production"

window.elevateRole = function(role) {
  if (window.openDevAuthModal) {
    window.openDevAuthModal(role);
  }
};

function getItemAccess(type, id) {
  return resolveItemAccess(type, id, { apiEndpoints, mcpTools });
}

const dataModels = [
  {
    id: "policy",
    title: "Policy",
    description: "A Policy represents bound risk coverage underwritten by the MGA. It contains inception dates, active coverage components, carrier details, and links to bound transaction ledgers and documents.",
    fields: [
      { name: "policyId", type: "string (UUID)", nullable: false, description: "The globally unique identifier for this policy." },
      { name: "policyNumber", type: "string", nullable: false, lloydsCRCode: "CR0029", description: "Unique human-readable identifier (e.g. <code>pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02</code>)." },
      { name: "certificateNumber", type: "string", nullable: true, lloydsCRCode: "CR0029", description: "Immutable certificate compliance number generated at binding." },
      { name: "greenCardSerial", type: "string", nullable: true, description: "Green card international serial compliance sequence, required for motor coverages in European jurisdictions." },
      { name: "programId", type: "string (UUID)", nullable: false, lloydsCRCode: "CR0005", description: "The UUID of the program under which the policy was written." },
      { name: "binderId", type: "string (UUID)", nullable: false, lloydsCRCode: "CR0006", description: "The associated delegated underwriting authority binder ID." },
      { name: "status", type: "string (Enum)", nullable: false, enum: ["DRAFT", "ACTIVE", "CANCELLED", "EXPIRED", "LAPSED"], description: "The customer-facing operational state of the policy." },
      { name: "bo_status", type: "string (Enum)", nullable: false, enum: ["REFERRAL", "APPROVED", "BOUND"], description: "The internal back-office/underwriting status of the policy." },
      { name: "effectiveDate", type: "string (ISO 8601)", nullable: false, lloydsCRCode: "CR0030", description: "The inception date and time when coverage begins." },
      { name: "expiryDate", type: "string (ISO 8601)", nullable: false, lloydsCRCode: "CR0031", description: "The termination date and time when coverage expires." },
      { name: "insuredEntityId", type: "string (UUID)", nullable: false, description: "References the normalized insured Entity profile containing name and risk characteristics." },
      { name: "policyHolderId", type: "string (UUID)", nullable: false, description: "References the PolicyHolder account details responsible for invoicing/payments." },
      { name: "renewalFamilyId", type: "string (UUID)", nullable: true, description: "Renewal chain identifier tracking this policy across multi-year cycles." },
      { name: "renewalSequence", type: "integer", nullable: false, description: "Monotonic sequence number of the policy term (e.g., 0 for original policy, 1 for first renewal)." },
      { name: "priorTermPolicyId", type: "string (UUID)", nullable: true, description: "The policy ID of the preceding term, forming a renewal chain." },
      { name: "premiumBreakdown", type: "object", nullable: false, description: "Detailed premium financial breakdown (see below for nested fields)." },
      { name: "premiumBreakdown.grossPremium", type: "number", nullable: false, lloydsCRCode: "CR0021", description: "Total premium charged to the insured including taxes, fees, and commission." },
      { name: "premiumBreakdown.netPremium", type: "number", nullable: false, lloydsCRCode: "CR1280", description: "Premium payable to the carrier, net of coverholder commissions." },
      { name: "premiumBreakdown.ipt", type: "number", nullable: false, lloydsCRCode: "CR0083", description: "Insurance Premium Tax amount charged based on jurisdiction." },
      { name: "premiumBreakdown.commission", type: "number", nullable: false, lloydsCRCode: "CR0235", description: "MGA or broker commission amount deducted from gross." },
      { name: "premiumBreakdown.stampDuty", type: "number", nullable: true, lloydsCRCode: "CR0087", description: "Regulatory stamp duty tax, where applicable (e.g., in Cyprus)." },
      { name: "selectedCoverages", type: "array (string)", nullable: false, lloydsCRCode: "CR0017", description: "List of coverage codes bound under this policy." },
      { name: "documents", type: "array (object)", nullable: false, description: "Array of compiled PDF documents (e.g., schedule, certificate of insurance)." }
    ]
  },
  {
    id: "quote",
    title: "Quote",
    description: "A Quote represents a pricing proposal generated by the Rating Engine based on submitted proposer and risk factors. In the underlying database schema, Quotes are represented as Policy objects at a <code>DRAFT</code> status.",
    fields: [
      { name: "quoteId", type: "string (UUID)", nullable: false, description: "The unique quote proposal identifier (maps directly to the policy ID at quote stage)." },
      { name: "quoteToken", type: "string (JWT)", nullable: false, description: "Cryptographically signed JWT containing rate locks for binding." },
      { name: "programId", type: "string (UUID)", nullable: false, lloydsCRCode: "CR0005", description: "The associated underwriting program ID." },
      { name: "status", type: "string (Enum)", nullable: false, enum: ["QUOTED", "REFERRED", "DECLINED", "EXPIRED"], description: "The evaluation state of the quote." },
      { name: "effectiveDate", type: "string (ISO 8601)", nullable: false, lloydsCRCode: "CR0030", description: "The proposed coverage start date." },
      { name: "premiumBreakdown", type: "object", nullable: true, description: "Premium cost details (populated only when status is <code>QUOTED</code> or <code>REFERRED</code>)." },
      { name: "premiumBreakdown.grossPremium", type: "number", nullable: false, lloydsCRCode: "CR0021", description: "Gross premium proposed to the customer." },
      { name: "premiumBreakdown.netPremium", type: "number", nullable: false, lloydsCRCode: "CR1280", description: "Carrier-only premium allocation." },
      { name: "premiumBreakdown.ipt", type: "number", nullable: false, lloydsCRCode: "CR0083", description: "Applicable local Insurance Premium Tax." },
      { name: "premiumBreakdown.commission", type: "number", nullable: false, lloydsCRCode: "CR0235", description: "Calculated coverholder/broker commission." },
      { name: "premiumBreakdown.stampDuty", type: "number", nullable: true, lloydsCRCode: "CR0087", description: "Stamp duty tax amount, if applicable." },
      { name: "quoteData", type: "object", nullable: false, description: "The user-supplied questionnaire answers validated against the program schema." },
      { name: "selectedCoverages", type: "array (string)", nullable: false, description: "The selected coverage codes included in rating." },
      { name: "expiryDate", type: "string (ISO 8601)", nullable: false, description: "Timestamp when this quote's rate locks expire." }
    ]
  },
  {
    id: "claim",
    title: "Claim",
    description: "A Claim tracks a notified incident of loss reported against an active policy, managing loss reserves and settlement payouts.",
    fields: [
      { name: "claimId", type: "string (UUID)", nullable: false, description: "Unique claim tracker ID." },
      { name: "claimNumber", type: "string", nullable: false, lloydsCRCode: "CR0104", description: "Unique human-readable claim number (e.g. <code>clm-2026-00102</code>)." },
      { name: "policyId", type: "string (UUID)", nullable: false, lloydsCRCode: "CR0029", description: "The UUID of the policy coverage applies to." },
      { name: "status", type: "string (Enum)", nullable: false, enum: ["OPEN", "REFERRED", "UNDER_INVESTIGATION", "SETTLED", "CLOSED", "REOPENED"], description: "The current workflow status of the claim." },
      { name: "claimType", type: "string", nullable: true, description: "Category of loss (e.g., ACCIDENT, THEFT, WINDSTORM)." },
      { name: "lossDate", type: "string (ISO 8601)", nullable: false, lloydsCRCode: "CR0119", description: "The date and time when the loss incident occurred." },
      { name: "reportedDate", type: "string (ISO 8601)", nullable: false, lloydsCRCode: "CR0372", description: "The date and time when the claim was first notified (FNOL)." },
      { name: "reserves", type: "object", nullable: false, description: "Claim accounting reserve allocations." },
      { name: "reserves.outstandingLoss", type: "number", nullable: false, lloydsCRCode: "CR0130", description: "Reserve set aside for expected indemnity payouts." },
      { name: "reserves.outstandingExpense", type: "number", nullable: false, lloydsCRCode: "CR0131", description: "Reserve set aside for claim handling/legal expenses." },
      { name: "payments", type: "array (object)", nullable: false, description: "List of transaction payments disbursed against this claim." },
      { name: "description", type: "string", nullable: false, lloydsCRCode: "CR0118", description: "Description of the loss event." }
    ]
  },
  {
    id: "endorsement",
    title: "Endorsement",
    description: "An Endorsement (MTA) represents a mid-term adjustment to an active policy, updating risk answers and recalculating pro-rata premiums using the template-based Magic B engine (EndorsementTemplate and EndorsementInstance).",
    fields: [
      { name: "endorsementId", type: "string (UUID)", nullable: false, lloydsCRCode: "CR0230", description: "Unique endorsement proposal/bound ID." },
      { name: "policyId", type: "string (UUID)", nullable: false, lloydsCRCode: "CR0029", description: "The target policy ID to adjust." },
      { name: "templateId", type: "string (UUID)", nullable: false, description: "Reference to the EndorsementTemplate governing validation parameters and underwriting approval requirements." },
      { name: "status", type: "string (Enum)", nullable: false, enum: ["DRAFT", "QUOTED", "REFERRED", "BOUND", "DECLINED"], description: "The lifecycle status of the endorsement." },
      { name: "effectiveDate", type: "string (ISO 8601)", nullable: false, lloydsCRCode: "CR0057", description: "The date when the adjustments become active on the policy." },
      { name: "premiumDelta", type: "object", nullable: false, description: "The pro-rata premium difference resulting from the risk changes." },
      { name: "premiumDelta.grossPremiumDelta", type: "number", nullable: false, lloydsCRCode: "CR0059", description: "The gross premium change (positive for AP, negative for RP)." },
      { name: "premiumDelta.netPremiumDelta", type: "number", nullable: false, lloydsCRCode: "CR0065", description: "The net premium payable change." },
      { name: "premiumDelta.iptDelta", type: "number", nullable: false, lloydsCRCode: "CR0270", description: "The tax change component." },
      { name: "changes", type: "object", nullable: false, description: "The modified risk questionnaire fields (the delta)." },
      { name: "documents", type: "array (object)", nullable: false, description: "Endorsement schedules and modified certificates issued." }
    ]
  },
  {
    id: "binder",
    title: "Binder",
    description: "A Binder represents a Delegated Underwriting Authority Agreement (DUA) contract defining carrier capacity, coverholder PIN, UMR, commission rates, and product authorization lines.",
    fields: [
      { name: "binderId", type: "string (UUID)", nullable: false, description: "The unique identifier of the binder contract." },
      { name: "coverholderName", type: "string", nullable: false, lloydsCRCode: "CR0013", description: "The registered name of the appointed coverholder." },
      { name: "coverholderPin", type: "string", nullable: false, lloydsCRCode: "CR0014", description: "Lloyd's 6+3 formatted branch identifier (e.g. <code>123456XTD</code>)." },
      { name: "umr", type: "string", nullable: false, lloydsCRCode: "CR0005", description: "17-character Unique Market Reference allocated by Lloyd's (e.g. <code>B1262847291039482</code>)." },
      { name: "agreementNumber", type: "string", nullable: false, lloydsCRCode: "CR0006", description: "Delegated Coverholder Agreement reference number." },
      { name: "lloydsReportingVer", type: "string", nullable: false, description: "The Lloyd's Coverholder Reporting Standards version (e.g., <code>V5.2</code>)." },
      { name: "defaultCurrency", type: "string", nullable: false, description: "Default currency for binder transactions (ISO 3-letter code)." },
      { name: "settlementCurrency", type: "string", nullable: false, description: "Currency used for BDX net premium remittance." },
      { name: "startDate", type: "string (ISO 8601)", nullable: false, lloydsCRCode: "CR0008", description: "Inception date when delegated underwriting authority becomes active." },
      { name: "endDate", type: "string (ISO 8601)", nullable: false, lloydsCRCode: "CR0009", description: "Termination date when binder capacity expires." },
      { name: "status", type: "string (Enum)", nullable: false, enum: ["DRAFT", "ACTIVE", "SUSPENDED", "EXHAUSTED"], description: "Current capacity availability state." },
      { name: "grossPremiumLimit", type: "number", nullable: false, description: "The maximum annual gross premium authorized under the binder financials." },
      { name: "commissionRate", type: "number", nullable: false, lloydsCRCode: "CR0061", description: "Default coverholder commission rate percentage." }
    ]
  },
  {
    id: "risk-transaction",
    title: "Risk Transaction",
    description: "A Risk Transaction represents an immutable entry in the policy lifecycle ledger (INCEPTION, ENDORSEMENT, CANCELLATION, REINSTATEMENT). It aggregates premium transactions and tax lines.",
    fields: [
      { name: "transactionId", type: "string (UUID)", nullable: false, lloydsCRCode: "CR0056", description: "Unique transaction identifier." },
      { name: "policyId", type: "string (UUID)", nullable: false, lloydsCRCode: "CR0029", description: "Linked policy reference." },
      { name: "transactionNumber", type: "integer", nullable: false, description: "Monotonic sequence number per policy." },
      { name: "transactionType", type: "string (Enum)", nullable: false, enum: ["INCEPTION", "ENDORSEMENT", "CANCELLATION", "REINSTATEMENT"], description: "Nature of lifecycle movement." },
      { name: "status", type: "string (Enum)", nullable: false, enum: ["DRAFT", "REFERRED", "APPROVED", "BOUND"], description: "The transaction state." },
      { name: "effectiveDate", type: "string (ISO 8601)", nullable: false, lloydsCRCode: "CR0057", description: "The date transaction takes effect." },
      { name: "paymentMode", type: "string (Enum)", nullable: false, enum: ["CUSTOMER_CARD", "OFFLINE", "INVOICE", "WAIVED"], description: "The payment collection method." },
      { name: "premiumTransactions", type: "array (object)", nullable: false, description: "Financial breakdown lines associated with this transaction." },
      { name: "premiumTransactions.grossPremium", type: "number", nullable: false, lloydsCRCode: "CR0059", description: "Gross premium booking." },
      { name: "premiumTransactions.commissionAmount", type: "number", nullable: false, lloydsCRCode: "CR0062", description: "Deducted commission amount." },
      { name: "premiumTransactions.taxesTotal", type: "number", nullable: false, lloydsCRCode: "CR0064", description: "Total tax components." },
      { name: "premiumTransactions.feesTotal", type: "number", nullable: false, lloydsCRCode: "CR0087", description: "Total fees components." },
      { name: "premiumTransactions.netToLondon", type: "number", nullable: false, lloydsCRCode: "CR1280", description: "Net premium payable to underwriters (Original Currency)." }
    ]
  },
  {
    id: "entity",
    title: "Entity",
    description: "An Entity profile represents a normalized insured subject (Person, Corporate Organization, Vehicle, or physical Asset) linked to policies.",
    fields: [
      { name: "entityId", type: "string (UUID)", nullable: false, description: "The globally unique identifier for this entity." },
      { name: "type", type: "string (Enum)", nullable: false, enum: ["PERSON", "ORGANIZATION", "VEHICLE", "ASSET"], description: "The logical classification of the insured risk subject." },
      { name: "data", type: "object", nullable: false, description: "Questionnaire-aligned dynamic properties containing specific risk answers (e.g. driver license details, vehicle registration)." }
    ]
  },
  {
    id: "policyholder",
    title: "PolicyHolder",
    description: "A PolicyHolder represents the contracting party/premium payer responsible for invoices and premium payments, separate from the insured risk subjects.",
    fields: [
      { name: "policyHolderId", type: "string (UUID)", nullable: false, description: "Unique identifier for this policyholder." },
      { name: "name", type: "string", nullable: false, lloydsCRCode: "CR0035", description: "Contract party full name or company name." },
      { name: "type", type: "string (Enum)", nullable: false, enum: ["INDIVIDUAL", "CORPORATE"], description: "The customer registration tier." },
      { name: "taxId", type: "string", nullable: true, description: "Local tax identifier or national code (e.g. fiscal code)." },
      { name: "bankAccounts", type: "array (object)", nullable: true, description: "Securely tokenized settlement bank details for refunds." },
      { name: "email", type: "string", nullable: false, description: "Primary email address for communications and renewal invitations." },
      { name: "phone", type: "string", nullable: true, description: "Contact phone number." },
      { name: "physicalAddress", type: "string", nullable: false, lloydsCRCode: "CR0038", description: "Verified physical billing address." }
    ]
  },
  {
    id: "sanctions-run",
    title: "Sanctions Screening Run",
    description: "A Sanctions Screening Run records compliance screens executed against international watchlists (OFAC, EU, HMT) at quote/bind checkpoints.",
    fields: [
      { name: "id", type: "string (UUID)", nullable: false, description: "Screening execution ID." },
      { name: "provider", type: "string", nullable: false, description: "Watchlist screening supplier (e.g., <code>CREDITSAFE</code>)." },
      { name: "outcome", type: "string (Enum)", nullable: false, enum: ["CLEAR", "HIT", "REFERRED"], description: "Screening matching outcome status." },
      { name: "hitCount", type: "integer", nullable: false, description: "Number of matching watchlist records identified." },
      { name: "providerRiskRating", type: "string", nullable: true, description: "Supplied risk scoring label." },
      { name: "firstHitJson", type: "object", nullable: true, description: "First hit details containing match percentage, PEP tier, matching list, and name details." },
      { name: "executedAt", type: "string (ISO 8601)", nullable: false, description: "Screening execution timestamp." }
    ]
  },
  {
    id: "reporting-period",
    title: "Reporting Period",
    description: "A Reporting Period manages BDX compliance cycles. Submissions can only occur when a period has been locked and validated.",
    fields: [
      { name: "reportingPeriodId", type: "string", nullable: false, description: "Calendar identifier (e.g., <code>rp_2026_03</code>)." },
      { name: "binderId", type: "string (UUID)", nullable: false, description: "Binder ID this reporting cycle is bound to." },
      { name: "year", type: "integer", nullable: false, description: "Calendar year." },
      { name: "month", type: "integer", nullable: false, description: "Calendar month (1-12)." },
      { name: "startDate", type: "string (ISO 8601)", nullable: false, lloydsCRCode: "CR0001", description: "Bordereau report start date." },
      { name: "endDate", type: "string (ISO 8601)", nullable: false, lloydsCRCode: "CR0002", description: "Bordereau report end date." },
      { name: "status", type: "string (Enum)", nullable: false, enum: ["OPEN", "LOCKED", "SUBMITTED"], description: "Current reporting status of the period." }
    ]
  }
];

const conceptPages = [
  sicClassificationGuide,
  {
    id: "overview",
    title: "Overview",
    group: "Getting Started",
    description: `
      <h2>Facio MGA API Reference</h2>
      <p style="font-size: 15px; color: var(--text-secondary); line-height: 1.7; margin-bottom: 24px;">
        The Facio API is organized around the full MGA lifecycle: programs, dynamic question contracts, quotes, policies, endorsements, renewals, claims, ledgers, binder validations, bordereaux, and webhooks.
      </p>
      <p style="font-size: 14.5px; line-height: 1.7; color: var(--text-secondary); margin-bottom: 24px;">
        The API uses predictable resource-oriented URLs, JSON request and response bodies, API-key authentication, idempotency keys for safe retries, and tenant context headers for multi-jurisdiction operation.
      </p>
    `
  },
  {
    id: "environments",
    title: "Environments & Dedicated Sites",
    group: "Getting Started",
    description: `
      <h2>Environments & Dedicated Sites</h2>
      <p>Facio operates on a dedicated-instance deployment model. <strong>Each customer has their own isolated site and dedicated database environment</strong>.</p>
      <p>This means your API base URLs are mapped to your company's specific subdomain (e.g. <code>https://your-company.facio.io/api</code>).</p>
      
      <table class="parameter-table" style="margin-top: 20px;">
        <thead>
          <tr style="border-bottom: 2px solid var(--border-color); text-align: left; padding: 10px 0;">
            <th style="padding: 10px;">Environment Type</th>
            <th style="padding: 10px;">Base URL Format</th>
            <th style="padding: 10px;">Security Model</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 12px; font-weight: 600;">Dedicated Sandbox</td>
            <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">https://sandbox-{your-company}.facio.io/api</td>
            <td style="padding: 12px; color: var(--text-secondary);">Test keys, simulated rating, and risk rule validations.</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 12px; font-weight: 600;">Dedicated Production</td>
            <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">https://{your-company}.facio.io/api</td>
            <td style="padding: 12px; color: var(--text-secondary);">Live keys, real binders, double-entry financial ledger execution.</td>
          </tr>
        </tbody>
      </table>
      
      <div class="glass-card" style="margin-top: 20px;">
        <div class="glass-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path></svg>
          Dedicated Tenant Context
        </div>
        <p>Ensure that you replace the placeholder subdomains in your API client SDKs with the dedicated environment domain URLs provided during your onboarding kickoff.</p>
      </div>
    `
  },
  {
    id: "authentication",
    title: "Authentication",
    group: "Getting Started",
    description: `
      <h2>Authentication</h2>
      <p>Authentication is handled globally using custom HTTP headers. All API requests must include a valid API key. Access is restricted to resources linked to your organizational tier.</p>
      
      <h3>Header Specification</h3>
      <table class="parameter-table">
        <tbody>
          <tr class="parameter-row">
            <td class="parameter-meta">
              <span class="param-name">x-api-key</span>
              <span class="param-type">string</span>
              <span class="param-required">Required</span>
            </td>
            <td class="param-description">Your secret API Key. Format matches <code>facio_live_pk_...</code> or <code>facio_test_pk_...</code>. Do not share keys in client-side code.</td>
          </tr>
        </tbody>
      </table>

      <div class="glass-card" style="background: rgba(168, 85, 247, 0.05);">
        <div class="glass-card-title" style="color: var(--accent-purple);">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
          Security Enforcement
        </div>
        <p>API keys carry specific permissions scopes (e.g. <code>quotes:write</code>, <code>claims:read</code>, <code>bdx:export</code>). If an endpoint is called without matching authority scope in the key, a <code>403 Forbidden</code> response is returned.</p>
      </div>
    `
  },
  {
    id: "tenant-context",
    title: "Tenant Context",
    group: "Getting Started",
    description: `
      <h2>Tenant & Jurisdiction Context</h2>
      <p>Facio operates on a multi-tenant shared-schema architecture. To support localized coverholder binders, requests configure execution context using headers.</p>
      
      <h3>Context Headers</h3>
      <table class="parameter-table">
        <tbody>
          <tr class="parameter-row">
            <td class="parameter-meta">
              <span class="param-name">x-tenant-id</span>
              <span class="param-type">string</span>
              <span class="param-required">Optional</span>
            </td>
            <td class="param-description">Explicitly sets the operating tenant context (e.g. <code>tenant_default</code>). Defaults to your credentials' primary tenant if omitted.</td>
          </tr>
        </tbody>
      </table>
    `
  },
  {
    id: "idempotency",
    title: "Idempotency",
    group: "Getting Started",
    description: `
      <h2>Idempotency</h2>
      <p>Idempotent requests guarantee that mutating operations (such as policy binding and claim submission) can be safely retried without executing twice.</p>
      <h3>Header Specification</h3>
      <table class="parameter-table">
        <tbody>
          <tr class="parameter-row">
            <td class="parameter-meta">
              <span class="param-name">x-idempotency-key</span>
              <span class="param-type">string</span>
              <span class="param-required">Recommended</span>
            </td>
            <td class="param-description">A unique UUID or string identifier representing the request payload. Minimum length 8 characters.</td>
          </tr>
        </tbody>
      </table>
    `
  },
  {
    id: "correlation-ids",
    title: "Correlation IDs",
    group: "Getting Started",
    description: `
      <h2>Correlation & Request Tracking</h2>
      <p>For traceability across distributed microservices (Rating Engine, Document Generator, Ledger Service), partners can pass a custom correlation token.</p>
      <h3>Header Specification</h3>
      <table class="parameter-table">
        <tbody>
          <tr class="parameter-row">
            <td class="parameter-meta">
              <span class="param-name">x-correlation-id</span>
              <span class="param-type">string</span>
              <span class="param-required">Optional</span>
            </td>
            <td class="param-description">A unique tracker token. If not provided, the system will generate a standard request tracker <code>req_xxxxxxxx</code>.</td>
          </tr>
        </tbody>
      </table>
    `
  },
  {
    id: "pagination",
    title: "Pagination",
    group: "Getting Started",
    description: `
      <h2>Pagination Model</h2>
      <p>List query endpoints support cursor-based pagination. Cursor pagination avoids performance degradation and page drift common in offset-limit models during high-concurrency ledger operations.</p>
      
      <h3>Query Parameters</h3>
      <table class="parameter-table">
        <tbody>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">limit</span><span class="param-type">integer</span><span>Optional</span></td>
            <td class="param-description">The number of records to return in the page. Default is <code>20</code>, maximum is <code>100</code>.</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">after</span><span class="param-type">string</span><span>Optional</span></td>
            <td class="param-description">The cursor string representing the next page. Pass the value of <code>meta.nextCursor</code> from the previous response to load the next set of records.</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">before</span><span class="param-type">string</span><span>Optional</span></td>
            <td class="param-description">The cursor string representing the previous page. Pass the value of <code>meta.prevCursor</code> from the previous response to load the previous set of records.</td>
          </tr>
        </tbody>
      </table>

      <h3>Paginated Response Envelope</h3>
      <p>List endpoints return the following standard paginated structure:</p>
      <pre class="tour-code-block" style="background-color: #010409;"><code class="language-json">{
  "success": true,
  "data": [
    { "id": "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02", "status": "ACTIVE" }
  ],
  "meta": {
    "hasMore": true,
    "nextCursor": "eyJvZmZzZXQiOjIwLCJpZCI6ImFjYzFfMjAyNiJ9",
    "prevCursor": "eyJvZmZzZXQiOjAsImlkIjoiYWNjMV8yMDI2In0",
    "totalCount": 142
  }
}</code></pre>

      <h3>Which Endpoints Are Paginated?</h3>
      <p>All endpoints returning lists of resources are paginated. This includes:</p>
      <ul>
        <li><code>GET /v1/programs</code> (Insurance Product Catalog)</li>
        <li><code>GET /v1/quotes</code> (Generated Quote List)</li>
        <li><code>GET /v1/policies</code> (Bound Policies)</li>
        <li><code>GET /v1/claims</code> (Logged Claims)</li>
        <li><code>GET /v1/risk-transactions</code> (Immutable double-entry transactional log)</li>
        <li><code>GET /v1/webhooks/endpoints</code> (Registered sync URL listeners)</li>
        <li><code>GET /v1/webhooks/events</code> (Historical event delivery attempts log)</li>
      </ul>
    `
  },
  {
    id: "rate-limits",
    title: "Rate Limits",
    group: "Getting Started",
    description: `
      <h2>API Rate Limits</h2>
      <p>To protect system availability and ensure fair resource allocation, Facio enforces request rate limits across all endpoints. Limits are tracked per API key.</p>
      
      <h3>Limits Schedule</h3>
      <table class="parameter-table" style="margin-top: 20px;">
        <thead>
          <tr style="border-bottom: 2px solid var(--border-color); text-align: left; padding: 10px 0;">
            <th style="padding: 10px;">Limit Class</th>
            <th style="padding: 10px;">Sandbox Limit</th>
            <th style="padding: 10px;">Production Limit</th>
            <th style="padding: 10px;">Scope / Pool Details</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 12px; font-weight: 600;">General Shared Pool</td>
            <td style="padding: 12px; color: var(--accent-cyan);">20 requests/minute</td>
            <td style="padding: 12px; color: var(--accent-cyan);">100 requests/minute</td>
            <td style="padding: 12px; color: var(--text-secondary);">Applied across standard query and read endpoints.</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 12px; font-weight: 600;">High-Compute Write Pool</td>
            <td style="padding: 12px; color: var(--accent-purple);">2 requests/second</td>
            <td style="padding: 12px; color: var(--accent-purple);">10 requests/second</td>
            <td style="padding: 12px; color: var(--text-secondary);">Distinct bucket for rating engine (<code>POST /v1/quotes</code>) and policy commits (<code>POST /v1/policies</code>).</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 12px; font-weight: 600;">Document Retrieval Pool</td>
            <td style="padding: 12px; color: var(--accent-amber);">30 requests/minute</td>
            <td style="padding: 12px; color: var(--accent-amber);">30 requests/minute</td>
            <td style="padding: 12px; color: var(--text-secondary);">Separate limit bucket for document download (<code>GET /v1/policies/{policyId}/documents</code>).</td>
          </tr>
        </tbody>
      </table>

      <h3>Rate Limit Response Headers</h3>
      <p>Every response returns HTTP headers indicating your remaining quota for the active window:</p>
      <table class="parameter-table">
        <tbody>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">X-RateLimit-Limit</span><span class="param-type">integer</span></td>
            <td class="param-description">The maximum number of requests allowed in the current time window.</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">X-RateLimit-Remaining</span><span class="param-type">integer</span></td>
            <td class="param-description">The number of requests remaining in the current window.</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">X-RateLimit-Reset</span><span class="param-type">integer (Unix Epoch)</span></td>
            <td class="param-description">The Unix timestamp indicating when the current rate limit window resets.</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">Retry-After</span><span class="param-type">integer (seconds)</span></td>
            <td class="param-description">Returned only when rate limit is exceeded (HTTP 429). The number of seconds you must wait before submitting new requests.</td>
          </tr>
        </tbody>
      </table>

      <h3>Rate Limit Exceeded Error Payload</h3>
      <p>When limit pools are exhausted, endpoints respond with HTTP status <code>429 Too Many Requests</code> and a structured error envelope:</p>
      <pre class="tour-code-block" style="background-color: #010409;"><code class="language-json">{
  "success": false,
  "error": {
    "code": "rate_limit_exceeded",
    "message": "Concurrent rate limit exceeded on rating calculations. Please wait and try again.",
    "details": {
      "pool": "high_compute_write",
      "retryAfterSeconds": 5
    }
  }
}</code></pre>
    `
  },
  {
    id: "mga-onboarding",
    title: "MGA Onboarding & Roles Checklist",
    group: "Getting Started",
    description: `
      <h2>MGA Onboarding & Roles Checklist</h2>
      <p style="font-size: 15px; color: var(--text-secondary); line-height: 1.7;">
        Welcome to your Facio dedicated instance. As a Managing General Agent (MGA) operating on Facio, it is essential to understand your role, permissions structure, and technical onboarding checklist to move from sandbox tests to live production distribution.
      </p>

      <h3>Business Roles & Tenancy</h3>
      <ul>
        <li><strong>Facio</strong>: The technology provider. Facio hosts and operates your dedicated database, API gateway, rating core, and ledger.</li>
        <li><strong>MGA (You / The Tenant)</strong>: Underwrite policies as a coverholder with delegated authority bounds. You manage your carriers, risk limits, and broker connections. Your instance operates on an isolated subdomain (e.g. <code>https://your-company.facio.io/api</code>).</li>
        <li><strong>Distributors / Brokers (Your Partners)</strong>: Third-party applications or agents who calculate quotes and issue policies via your API. You issue and manage their API keys.</li>
      </ul>

      <h3>API Permission Scopes Reference</h3>
      <p>Facio uses OAuth scopes and API key privileges to govern access. Ensure that your client keys carry the minimum scopes required for their tasks:</p>
      <table class="parameter-table" style="margin-top: 20px;">
        <thead>
          <tr style="border-bottom: 2px solid var(--border-color); text-align: left; padding: 10px 0;">
            <th style="padding: 10px;">Scope Name</th>
            <th style="padding: 10px;">Granted Capability</th>
            <th style="padding: 10px;">Common Target Client</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">programs:read</td>
            <td style="padding: 10px; color: var(--text-secondary);">Query available underwriting products, dynamic questionnaire fields, and coverages.</td>
            <td style="padding: 10px; color: var(--text-muted);">Broker Quoting Frontend</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">quotes:write</td>
            <td style="padding: 10px; color: var(--text-secondary);">Generate quote proposals and trigger the Rating Engine.</td>
            <td style="padding: 10px; color: var(--text-muted);">Broker Quoting Frontend</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">quotes:read</td>
            <td style="padding: 10px; color: var(--text-secondary);">Query proposal details and check status.</td>
            <td style="padding: 10px; color: var(--text-muted);">Internal CRM / Admin portal</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">policies:bind</td>
            <td style="padding: 10px; color: var(--text-secondary);">Bind coverages, commit double-entry ledger transactions, and compile documents.</td>
            <td style="padding: 10px; color: var(--text-muted);">Broker checkout systems</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">policies:read</td>
            <td style="padding: 10px; color: var(--text-secondary);">View active coverage states, policy timelines, and download certificates.</td>
            <td style="padding: 10px; color: var(--text-muted);">Customer self-service app</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">claims:write</td>
            <td style="padding: 10px; color: var(--text-secondary);">Log First Notice of Loss (FNOL) and post reserve ledger adjustments.</td>
            <td style="padding: 10px; color: var(--text-muted);">Third-Party Claim Administrator</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">ledger:read</td>
            <td style="padding: 10px; color: var(--text-secondary);">Audit double-entry account balances and premium journals.</td>
            <td style="padding: 10px; color: var(--text-muted);">Internal Accounting / ERP</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">bdx:export</td>
            <td style="padding: 10px; color: var(--text-secondary);">Generate monthly Bordereaux files for carrier compliance.</td>
            <td style="padding: 10px; color: var(--text-muted);">Internal compliance operators</td>
          </tr>
        </tbody>
      </table>

      <h3>5-Step Technical Onboarding Sequence</h3>
      <div class="golden-path-timeline">
        <div class="golden-path-step">
          <div class="golden-path-step-badge">1</div>
          <div class="golden-path-step-content">
            <h3>Tenant Provisioning</h3>
            <p>Facio spins up your isolated dedicated instance and provides your admin credentials. Your subdomain will match <code>https://sandbox-{yourcompany}.facio.io/api</code> for testing.</p>
          </div>
        </div>
        <div class="golden-path-step">
          <div class="golden-path-step-badge">2</div>
          <div class="golden-path-step-content">
            <h3>Program and Binder Linkage</h3>
            <p>Your underwriting rules, carrier binding agreements, tax percentages, and dynamic questionnaire schemas are uploaded to your database using the Facio MCP Config Agent.</p>
          </div>
        </div>
        <div class="golden-path-step">
          <div class="golden-path-step-badge">3</div>
          <div class="golden-path-step-content">
            <h3>API Key Provisioning</h3>
            <p>Log into your Facio Tenant Dashboard to generate API keys. Assign granular permission scopes depending on if the key is for a public-facing broker frontend, internal CRM, or accounting sync.</p>
          </div>
        </div>
        <div class="golden-path-step">
          <div class="golden-path-step-badge">4</div>
          <div class="golden-path-step-content">
            <h3>Webhook Registration</h3>
            <p>Register your server endpoint URL using the <code>POST /v1/webhooks/endpoints</code> API to listen for critical events like <code>policy.bound</code> or <code>claim.fnol_received</code> to synchronize databases instantly.</p>
          </div>
        </div>
        <div class="golden-path-step">
          <div class="golden-path-step-badge">5</div>
          <div class="golden-path-step-content">
            <h3>Sandbox E2E Run</h3>
            <p>Execute the Quote-Bind-Issue workflow using test credentials and sandbox card tokens to verify ledger double-entry bookings and document generation.</p>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "dynamic-questions",
    title: "Dynamic Question Contracts Guide",
    group: "Getting Started",
    description: `
      <h2>Dynamic Question Contracts Guide</h2>
      <p style="font-size: 15px; color: var(--text-secondary); line-height: 1.7;">
        Because underwriting rules and product parameters evolve, Facio does not enforce static quote request schemas. Instead, your distributor clients query the dynamic questionnaire schema, build their forms dynamically, and post response answers back to the rating core.
      </p>

      <h3>Fetching the Schema</h3>
      <p>Before constructing a <code>POST /v1/quotes</code> payload, query <code>GET /v1/programs/{programId}/questions</code>. The response returns a standard <strong>JSON Schema Draft-07</strong> defining the exact data contract expected in the <code>quoteData</code> block.</p>

      <h3>Supported Question Types</h3>
      <p>The schema can define fields matching standard JSON formats:</p>
      <ul>
        <li><strong><code>string</code></strong>: standard text entries. Can include formats (e.g. <code>"format": "email"</code>, <code>"format": "date"</code>).</li>
        <li><strong><code>number</code> / <code>integer</code></strong>: numeric fields. Can specify restrictions such as <code>"minimum": 100</code>, <code>"maximum": 50000</code>.</li>
        <li><strong><code>boolean</code></strong>: yes/no checkboxes.</li>
        <li><strong><code>enum</code></strong>: lists of options, rendered in client UIs as select dropdowns or radio lists.</li>
        <li><strong><code>object</code></strong>: nested field groups (e.g. <code>proposer: { firstName: string, lastName: string }</code>).</li>
        <li><strong><code>array</code></strong>: list of sub-schemas (e.g. listing details of past claims).</li>
      </ul>

      <h3>UI Custom Hints (Facio Metadata Extensions)</h3>
      <p>To assist frontend rendering libraries, Facio decorates JSON Schema fields with custom UI hints:</p>
      <table class="parameter-table">
        <tbody>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">x-facio-label</span><span class="param-type">string</span></td>
            <td class="param-description">The user-friendly label to render beside the form input (e.g. <code>"License Hold Duration (Years)"</code>).</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">x-facio-display-order</span><span class="param-type">integer</span></td>
            <td class="param-description">A sorting index to arrange inputs sequentially on screen.</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">x-facio-placeholder</span><span class="param-type">string</span></td>
            <td class="param-description">The helper placeholder text to display inside the input element.</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">x-facio-width</span><span class="param-type">string ("full" | "half")</span></td>
            <td class="param-description">Column grid layout hints for rendering forms.</td>
          </tr>
        </tbody>
      </table>

      <h3>Conditional Risk Questionnaire Fields</h3>
      <p>Facio uses JSON Schema's <code>if / then / else</code> blocks to represent dynamic dependencies. For example, if a proposer states they have had claims, the frontend must dynamically render and require a secondary sub-form listing claim details.</p>
      
      <h4>Conditional Schema Example:</h4>
      <pre class="tour-code-block" style="background-color: #010409;"><code class="language-json">{
  "properties": {
    "hasPastClaims": {
      "type": "boolean",
      "x-facio-label": "Have you had any claims in the last 3 years?"
    }
  },
  "if": {
    "properties": { "hasPastClaims": { "const": true } }
  },
  "then": {
    "properties": {
      "claimsList": {
        "type": "array",
        "x-facio-label": "Please provide details of your past claims:",
        "items": {
          "type": "object",
          "properties": {
            "lossDate": { "type": "string", "format": "date", "x-facio-label": "Date of incident" },
            "settledValue": { "type": "number", "minimum": 0, "x-facio-label": "Settled claim amount (€)" }
          },
          "required": ["lossDate", "settledValue"]
        }
      }
    },
    "required": ["claimsList"]
  }
}</code></pre>
    `
  },
  {
    id: "error-model",
    title: "Error Model",
    group: "Getting Started",
    description: `
      <h2>Standard Error Envelope</h2>
      <p>Errors returned by the Facio engine follow a standardized JSON envelope. The payload provides machine-readable error codes and detailed diagnostic fields for validation failures.</p>
      
      <h3>Error JSON Envelope</h3>
      <pre class="tour-code-block" style="background-color: #010409;"><code class="language-json">{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Questionnaire validation failed. Proposer email must be a valid email format.",
    "details": {
      "quoteData.proposer.email": "Email must be a valid RFC 5322 format.",
      "quoteData.vehicleValue": "Value must be at least €100."
    }
  }
}</code></pre>

      <h3>Error Field Definitions</h3>
      <table class="parameter-table">
        <tbody>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">success</span><span class="param-type">boolean</span></td>
            <td class="param-description">Always <code>false</code> for error responses.</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">error.code</span><span class="param-type">string</span></td>
            <td class="param-description">A machine-readable constant string identifying the error category.</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">error.message</span><span class="param-type">string</span></td>
            <td class="param-description">A human-readable explanation of the error, suitable for logging.</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">error.details</span><span class="param-type">object</span></td>
            <td class="param-description">Optional. Map of field names to specific validation messages, returned during <code>VALIDATION_ERROR</code>.</td>
          </tr>
        </tbody>
      </table>

      <h3>Unified Error Code Reference Table</h3>
      <table class="parameter-table" style="margin-top: 20px;">
        <thead>
          <tr style="border-bottom: 2px solid var(--border-color); text-align: left; padding: 10px 0;">
            <th style="padding: 10px;">HTTP Status</th>
            <th style="padding: 10px;">Error Code</th>
            <th style="padding: 10px;">Description</th>
            <th style="padding: 10px;">Domain Tiers</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">400</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">VALIDATION_ERROR</td>
            <td style="padding: 10px; color: var(--text-secondary);">Request payload fields failed validation checks. Refer to <code>details</code> object.</td>
            <td style="padding: 10px; color: var(--text-muted);">Core API</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">400</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">BINDER_CAPACITY_EXHAUSTED</td>
            <td style="padding: 10px; color: var(--text-secondary);">The underwriting program's reinsurance capacity has been fully consumed.</td>
            <td style="padding: 10px; color: var(--text-muted);">Rating / Binder</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">400</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">UNDERWRITING_RULES_BLOCKED</td>
            <td style="padding: 10px; color: var(--text-secondary);">Automated decline triggered based on risk exclusions (e.g. driver too young).</td>
            <td style="padding: 10px; color: var(--text-muted);">Rating</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">400</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">QUOTE_EXPIRED</td>
            <td style="padding: 10px; color: var(--text-secondary);">The quote validity period has expired and must be re-rated.</td>
            <td style="padding: 10px; color: var(--text-muted);">Policy Bind</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">400</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">QUOTE_ALREADY_BOUND</td>
            <td style="padding: 10px; color: var(--text-secondary);">The quote has already been successfully bound into an active policy.</td>
            <td style="padding: 10px; color: var(--text-muted);">Policy Bind</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">400</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">BINDING_AUTHORITY_EXCEEDED</td>
            <td style="padding: 10px; color: var(--text-secondary);">The policy premium exceeds your coverholder binder authority limit.</td>
            <td style="padding: 10px; color: var(--text-muted);">Policy Bind</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">400</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">PAYMENT_AUTHORIZATION_FAILED</td>
            <td style="padding: 10px; color: var(--text-secondary);">The premium card transaction failed on authorization.</td>
            <td style="padding: 10px; color: var(--text-muted);">Financials</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">401</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">AUTHENTICATION_FAILED</td>
            <td style="padding: 10px; color: var(--text-secondary);">Missing, malformed, or invalid API Key in <code>x-api-key</code> header.</td>
            <td style="padding: 10px; color: var(--text-muted);">Auth Layer</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">403</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">INSUFFICIENT_PERMISSIONS</td>
            <td style="padding: 10px; color: var(--text-secondary);">The API Key is valid but lacks the scope required for this operation.</td>
            <td style="padding: 10px; color: var(--text-muted);">Auth Layer</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">404</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">RESOURCE_NOT_FOUND</td>
            <td style="padding: 10px; color: var(--text-secondary);">The requested resource ID (e.g. policyId, claimId) was not found.</td>
            <td style="padding: 10px; color: var(--text-muted);">Core API</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">409</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">IDEMPOTENCY_ERROR</td>
            <td style="padding: 10px; color: var(--text-secondary);">Re-submission of an idempotency key with different payload parameters.</td>
            <td style="padding: 10px; color: var(--text-muted);">Core API</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px;"><span class="status-badge status-4xx">429</span></td>
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">rate_limit_exceeded</td>
            <td style="padding: 10px; color: var(--text-secondary);">Your API key request frequency exceeds sandbox or production pools.</td>
            <td style="padding: 10px; color: var(--text-muted);">Auth Layer</td>
          </tr>
        </tbody>
      </table>

      <h3>Retryability & Error Categories</h3>
      <ul>
        <li><strong>Permanent Errors (Do NOT Retry)</strong>: HTTP <code>400</code>, <code>401</code>, <code>403</code>, and <code>404</code> errors indicate client configuration or parameters issues. Retrying these requests without changing input fields will yield the exact same error.</li>
        <li><strong>Transient / Retryable Errors</strong>: HTTP <code>429</code> (Rate limits) and HTTP <code>500</code>/<code>502</code>/<code>503</code> (Server hiccups) are temporary. Retrying with **exponential backoff** is recommended.</li>
      </ul>
    `
  },
  {
    id: "webhook-signatures",
    title: "Webhook Signatures",
    group: "Getting Started",
    description: `
      <h2>Webhook Signatures</h2>
      <p>Facio signs all outbound webhook payloads with a cryptographic signature. This allows your web server to verify that incoming webhook notifications originated authentically from Facio and were not tampered with in-transit.</p>
      
      <h3>Verification Headers</h3>
      <table class="parameter-table">
        <tbody>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">x-facio-signature</span><span class="param-type">string</span></td>
            <td class="param-description">The hexadecimal HMAC SHA-256 signature calculated from the payload body and the timestamp.</td>
          </tr>
          <tr class="parameter-row">
            <td class="parameter-meta"><span class="param-name">x-facio-timestamp</span><span class="param-type">string (Unix Epoch)</span></td>
            <td class="param-description">The timestamp (in seconds) representing when the event was dispatched. Prevent replay attacks by validating this value.</td>
          </tr>
        </tbody>
      </table>

      <h3>Signature Computation Formula</h3>
      <p>The signature is computed by concatenating the timestamp value from the <code>x-facio-timestamp</code> header, the character <code>.</code>, and the raw request body string. The HMAC SHA-256 hash is computed using your registered endpoint's Webhook Secret:</p>
      <pre class="tour-code-block" style="background-color: #010409;"><code style="color: #c9d1d9;">signature = HMAC_SHA256(webhook_secret, timestamp + "." + raw_request_body)</code></pre>

      <h3>Replay Attack Prevention</h3>
      <p>Always inspect the <code>x-facio-timestamp</code> header. Compare it against the current server time and reject requests where the difference exceeds <strong>300 seconds (5 minutes)</strong>.</p>

      <h3>Implementation Code Examples</h3>
      <div class="signature-verification-block">
        <div class="signature-tabs">
          <button class="signature-tab-btn active" data-lang="node">Node.js (Express)</button>
          <button class="signature-tab-btn" data-lang="python">Python (Flask)</button>
        </div>
        <div class="signature-code-block active" data-lang="node">
          <pre><code>const crypto = require('crypto');

function verifyFacioWebhook(req, res, next) {
  const signature = req.headers['x-facio-signature'];
  const timestamp = req.headers['x-facio-timestamp'];
  const rawBody = req.rawBody; // Assumes raw body buffer is preserved

  if (!signature || !timestamp) {
    return res.status(401).send('Missing signature or timestamp headers.');
  }

  // 1. Prevent Replay Attacks (5 minutes tolerance)
  const now = Math.floor(Date.now() / 1000);
  if (Math.abs(now - parseInt(timestamp, 10)) > 300) {
    return res.status(400).send('Replay attack suspected / timestamp expired.');
  }

  // 2. Re-compute HMAC signature
  const secret = process.env.FACIO_WEBHOOK_SECRET;
  const hmac = crypto.createHmac('sha256', secret);
  hmac.update(\`\${timestamp}.\${rawBody}\`);
  const expectedSignature = hmac.digest('hex');

  // 3. Constant-time comparison
  try {
    if (crypto.timingSafeEqual(Buffer.from(signature, 'hex'), Buffer.from(expectedSignature, 'hex'))) {
      return next();
    }
  } catch (err) {}
  
  return res.status(401).send('Invalid cryptographic signature.');
}</code></pre>
        </div>
        <div class="signature-code-block" data-lang="python">
          <pre><code>import time
import hmac
import hashlib
import os
from flask import request, abort

def verify_facio_webhook():
    signature = request.headers.get('x-facio-signature')
    timestamp = request.headers.get('x-facio-timestamp')
    raw_body = request.get_data() # Gets raw bytes

    if not signature or not timestamp:
        abort(401, 'Missing signature or timestamp headers.')

    # 1. Prevent Replay Attacks (5 minutes tolerance)
    now = int(time.time())
    if abs(now - int(timestamp)) > 300:
        abort(400, 'Replay attack suspected / timestamp expired.')

    # 2. Re-compute HMAC signature
    secret = os.environ.get('FACIO_WEBHOOK_SECRET').encode('utf-8')
    message = f"{timestamp}.".encode('utf-8') + raw_body
    expected_signature = hmac.new(secret, message, hashlib.sha256).hexdigest()

    # 3. Constant-time comparison
    if not hmac.compare_digest(signature, expected_signature):
        abort(401, 'Invalid cryptographic signature.')</code></pre>
        </div>
      </div>
    `
  },
  {
    id: "webhook-events",
    title: "Webhook Event Catalog",
    group: "Getting Started",
    description: `
      <h2>Webhook Event Catalog</h2>
      <p style="font-size: 15px; color: var(--text-secondary); line-height: 1.7;">
        Facio broadcasts transactional occurrences using webhooks, allowing MGAs and partner systems to synchronize database records, issue downstream notifications, or close double-entry books in real-time.
      </p>

      <h3>Outbound Event Envelope</h3>
      <p>All webhook requests are sent as <code>POST</code> JSON payloads within a standardized envelope containing event discrimination metadata.</p>
      <pre class="tour-code-block" style="max-height: 400px; overflow-y: auto; background-color: #010409;"><code style="color: #34d399;">{
  "id": "evt_281a0000-84b2-4d1e-bd15-0d6789b52000",
  "event_type": "policy.bound",
  "created_at": "2026-06-09T00:47:09Z",
  "tenant_id": "tenant_default",
  "api_version": "v1.0.5",
  "payload": {
    "policyId": "pol_private_motor_9f3a8271",
    "policyNumber": "POL-PRIVATEMOTOR-84729",
    "status": "ISSUED",
    "effectiveDate": "2026-03-01T00:00:00Z",
    "premiumBreakdown": {
      "grossPremium": 488.50,
      "netPremium": 400.00,
      "ipt": 40.00,
      "commission": 60.00,
      "stampDuty": 2.00
    }
  }
}</code></pre>

      <h3>Envelope Field Definitions</h3>
      <table class="parameter-table" style="margin-top: 20px;">
        <thead>
          <tr style="border-bottom: 2px solid var(--border-color); text-align: left; font-size: 11px; text-transform: uppercase;">
            <th style="padding: 10px;">Field</th>
            <th style="padding: 10px;">Type</th>
            <th style="padding: 10px;">Description</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">id</td>
            <td style="padding: 10px; font-family: var(--font-mono); color: var(--text-muted);">string (UUID)</td>
            <td style="padding: 10px; color: var(--text-secondary);">Globally unique identifier for this specific event notification. Use this to deduplicate events.</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">event_type</td>
            <td style="padding: 10px; font-family: var(--font-mono); color: var(--text-muted);">string (Enum)</td>
            <td style="padding: 10px; color: var(--text-secondary);">The discrimination field representing the occurrence type (e.g. <code>policy.bound</code>).</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">created_at</td>
            <td style="padding: 10px; font-family: var(--font-mono); color: var(--text-muted);">string (ISO 8601)</td>
            <td style="padding: 10px; color: var(--text-secondary);">The exact timestamp when the event was generated.</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">tenant_id</td>
            <td style="padding: 10px; font-family: var(--font-mono); color: var(--text-muted);">string</td>
            <td style="padding: 10px; color: var(--text-secondary);">The tenant workspace partition identifier (e.g. <code>tenant_default</code>).</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 10px; font-family: var(--font-mono); font-weight: 600;">payload</td>
            <td style="padding: 10px; font-family: var(--font-mono); color: var(--text-muted);">object</td>
            <td style="padding: 10px; color: var(--text-secondary);">The event-specific resource details payload.</td>
          </tr>
        </tbody>
      </table>

      <h3>Supported Webhook Events</h3>
      <p>Each event contains specialized fields inside the <code>payload</code> block:</p>
      <ul>
        <li><strong><code>quote.created</code></strong>: Emitted whenever a quote is calculated. Payload contains: <code>quoteId</code>, <code>status</code>, <code>effectiveDate</code>, <code>premiumBreakdown</code>.</li>
        <li><strong><code>policy.bound</code> / <code>policy.issued</code></strong>: Emitted when coverage binds. Payload contains: <code>policyId</code>, <code>policyNumber</code>, <code>status</code>, <code>effectiveDate</code>, <code>expiryDate</code>, <code>premiumBreakdown</code>.</li>
        <li><strong><code>policy.cancelled</code></strong>: Emitted when a policy is terminated mid-term. Payload contains: <code>policyId</code>, <code>policyNumber</code>, <code>cancellationDate</code>, <code>refundAmount</code>.</li>
        <li><strong><code>endorsement.bound</code></strong>: Emitted when an MTA is bound. Payload contains: <code>endorsementId</code>, <code>policyId</code>, <code>status</code>, <code>premiumDelta</code>, <code>changes</code>.</li>
        <li><strong><code>renewal.invited</code></strong>: Emitted when a renewal offer is dispatched. Payload contains: <code>renewalId</code>, <code>policyId</code>, <code>status</code>, <code>expiryDate</code>.</li>
        <li><strong><code>renewal.finalized</code></strong>: Emitted when a renewal proposal is bound. Payload contains: <code>renewalId</code>, <code>policyId</code>, <code>status</code>.</li>
        <li><strong><code>claim.fnol_received</code></strong>: Emitted when a claim is created. Payload contains: <code>claimId</code>, <code>claimNumber</code>, <code>policyId</code>, <code>status</code>, <code>lossDate</code>.</li>
        <li><strong><code>claim.reserve_changed</code></strong>: Emitted when claim reserves are modified. Payload contains: <code>claimId</code>, <code>claimNumber</code>, <code>reserves</code> (Outstanding Loss / Outstanding Expense).</li>
        <li><strong><code>claim.payment_created</code></strong>: Emitted when a settlement payment is recorded. Payload contains: <code>claimId</code>, <code>paymentId</code>, <code>amount</code>, <code>status</code>.</li>
        <li><strong><code>claim.closed</code></strong>: Emitted when a claim file is closed. Payload contains: <code>claimId</code>, <code>claimNumber</code>, <code>status</code>.</li>
        <li><strong><code>bdx.export_ready</code></strong>: Emitted when monthly Bordereau reports compile successfully. Payload contains: <code>exportId</code>, <code>status</code>, <code>downloadUrl</code>.</li>
      </ul>

      <div class="glass-card" style="margin-top: 24px; padding: 20px; border-left: 4px solid var(--accent-cyan); background: rgba(6, 182, 212, 0.02);">
        <div class="glass-card-title" style="color: var(--accent-cyan);">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
          Delivery Retry Policy
        </div>
        <p>If your webhook receiver responds with a status code outside the <code>2xx</code> range (e.g. 500, 502, 400) or times out (exceeds 10 seconds), Facio automatically schedules retries:</p>
        <ul>
          <li><strong>Attempts Limit</strong>: 15 delivery attempts in total.</li>
          <li><strong>Exponential Backoff</strong>: Uses an exponential delay curve. Initial retry is sent after <strong>15 seconds</strong>, scaling up to a maximum delay of <strong>1 hour</strong> between attempts.</li>
          <li><strong>Dead-Letter Queue (DLQ)</strong>: If all 15 attempts fail (spanning approximately 24 hours), the event transitions to a <code>FAILED</code> status and is sent to the Dead-Letter Queue. You can monitor and manually trigger replays using the <code>POST /v1/webhooks/events/{eventId}/replay</code> endpoint.</li>
        </ul>
      </div>
    `
  },
  {
    id: "sandbox-data",
    title: "Sandbox Test Data",
    group: "Getting Started",
    description: `
      <h2>Sandbox Test Data</h2>
      <p>Use these static variables and mock IDs in the Sandbox Environment to trigger specific logic outcomes (underwriting referral, sanction hit, payment success/fail).</p>
      <table class="parameter-table" style="margin-top: 20px;">
        <thead>
          <tr style="border-bottom: 2px solid var(--border-color); text-align: left; padding: 10px 0;">
            <th style="padding: 10px;">Resource Type</th>
            <th style="padding: 10px;">ID / Value</th>
            <th style="padding: 10px;">Behavioral Outcome</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 12px; font-weight: 600;">Program ID</td>
            <td style="padding: 12px; font-family: var(--font-mono); font-size: 11px;">e9f8a000-84b2-4d1e-bd15-0d6789b52000</td>
            <td style="padding: 12px; color: var(--text-secondary);">Default active MGA Motor program</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--border-color);">
            <td style="padding: 12px; font-weight: 600;">Driver Age</td>
            <td style="padding: 12px; font-family: var(--font-mono); font-size: 11px;">18</td>
            <td style="padding: 12px; color: var(--accent-red);">Triggers Underwriting DECLINED status</td>
          </tr>
        </tbody>
      </table>
    `
  },
  {
    id: "changelog",
    title: "API Changelog",
    group: "Getting Started",
    description: `
      <h2>API Changelog</h2>
      <p>All notable additions and changes to the Facio MGA engine are cataloged here.</p>
      <div id="changelog-v1-0-6" class="changelog-version" style="margin-top: 24px; border-bottom: 1px solid var(--border-color); padding-bottom: 16px;">
        <h3 style="color: var(--accent-cyan); display: flex; align-items: center; gap: 8px;">
          v1.0.6 <span style="font-size: 11px; font-weight: 600; color: var(--text-muted);">2026-06-09</span>
        </h3>
        <ul style="margin-top: 8px; margin-left: 20px;">
          <li><strong>Aligned:</strong> Fully synchronized data models (Policy, Quote, Claim, Endorsement, Binder, RiskTransaction, Entity, PolicyHolder, SanctionScreeningRun, ReportingPeriod) with database schema.</li>
          <li><strong>Fixed:</strong> Resolved all stubs that previously accepted <code>null</code> request bodies, providing proper JSON and multipart/form-data schemas.</li>
          <li><strong>Added:</strong> Introduced <code>GET /v1/binders</code>, <code>GET /v1/entities</code>, <code>POST /v1/entities</code>, <code>GET /v1/policyholders</code>, <code>POST /v1/policyholders</code>, <code>GET /v1/policies/{id}/sanctions</code>, <code>GET /v1/bdx/periods</code>, and <code>POST /v1/bdx/periods/{id}/lock</code> endpoints.</li>
        </ul>
      </div>
      <div id="changelog-v1-0-5" class="changelog-version" style="margin-top: 24px; border-bottom: 1px solid var(--border-color); padding-bottom: 16px;">
        <h3 style="color: var(--accent-cyan); display: flex; align-items: center; gap: 8px;">
          v1.0.5 <span style="font-size: 11px; font-weight: 600; color: var(--text-muted);">2026-03-09</span>
        </h3>
        <ul style="margin-top: 8px; margin-left: 20px;">
          <li><strong>Added:</strong> Introduced <code>POST /v1/policies/{id}/endorsements/quote</code> and bind endpoints for MTAs.</li>
        </ul>
      </div>
      <div id="changelog-v1-0-4" class="changelog-version" style="margin-top: 24px; border-bottom: 1px solid var(--border-color); padding-bottom: 16px;">
        <h3 style="color: var(--accent-cyan); display: flex; align-items: center; gap: 8px;">
          v1.0.4 <span style="font-size: 11px; font-weight: 600; color: var(--text-muted);">2026-02-15</span>
        </h3>
        <ul style="margin-top: 8px; margin-left: 20px;">
          <li><strong>Added:</strong> Introduced cursor-based pagination query parameters (<code>limit</code>, <code>after</code>, <code>before</code>) for list endpoints.</li>
          <li><strong>Added:</strong> Added standard <code>meta</code> pagination envelope in response bodies.</li>
          <li><strong>Added:</strong> Support for <code>x-correlation-id</code> and <code>x-idempotency-key</code> verification.</li>
        </ul>
      </div>
      <div id="changelog-v1-0-3" class="changelog-version" style="margin-top: 24px; border-bottom: 1px solid var(--border-color); padding-bottom: 16px;">
        <h3 style="color: var(--accent-cyan); display: flex; align-items: center; gap: 8px;">
          v1.0.3 <span style="font-size: 11px; font-weight: 600; color: var(--text-muted);">2026-01-20</span>
        </h3>
        <ul style="margin-top: 8px; margin-left: 20px;">
          <li><strong>Added:</strong> Subrogation and claim recoveries API endpoints (<code>POST /v1/claims/{id}/recoveries</code>).</li>
          <li><strong>Improved:</strong> Added double-entry validation safeguards on claim expense reservations.</li>
          <li><strong>Fixed:</strong> Corrected tax rounding precision issues for Cyprus Stamp Duty.</li>
        </ul>
      </div>
      <div id="changelog-v1-0-2" class="changelog-version" style="margin-top: 24px; border-bottom: 1px solid var(--border-color); padding-bottom: 16px;">
        <h3 style="color: var(--accent-cyan); display: flex; align-items: center; gap: 8px;">
          v1.0.2 <span style="font-size: 11px; font-weight: 600; color: var(--text-muted);">2025-12-15</span>
        </h3>
        <ul style="margin-top: 8px; margin-left: 20px;">
          <li><strong>Added:</strong> First Notice of Loss (FNOL) batch uploads and documents attachment support.</li>
          <li><strong>Improved:</strong> Watchlist screening checks cache lookup to reduce external CreditSafe API latency by 40%.</li>
          <li><strong>Fixed:</strong> Webhook replay event timeout issues when receiver is slow.</li>
        </ul>
      </div>
      <div id="changelog-v1-0-1" class="changelog-version" style="margin-top: 24px; border-bottom: 1px solid var(--border-color); padding-bottom: 16px;">
        <h3 style="color: var(--accent-cyan); display: flex; align-items: center; gap: 8px;">
          v1.0.1 <span style="font-size: 11px; font-weight: 600; color: var(--text-muted);">2025-11-20</span>
        </h3>
        <ul style="margin-top: 8px; margin-left: 20px;">
          <li><strong>Added:</strong> Multi-currency support (EUR, USD, GBP) for premium invoices.</li>
          <li><strong>Improved:</strong> Structured client-side validation errors payload context format.</li>
          <li><strong>Fixed:</strong> Corrected policy effective date calculations on leap years.</li>
        </ul>
      </div>
      <div id="changelog-v1-0-0" class="changelog-version" style="margin-top: 24px; border-bottom: 1px solid var(--border-color); padding-bottom: 16px;">
        <h3 style="color: var(--accent-cyan); display: flex; align-items: center; gap: 8px;">
          v1.0.0 <span style="font-size: 11px; font-weight: 600; color: var(--text-muted);">2025-11-01</span>
        </h3>
        <ul style="margin-top: 8px; margin-left: 20px;">
          <li><strong>Initial Release:</strong> Launch of Facio MGA Core Rating and Policy Issuance APIs.</li>
          <li><strong>Added:</strong> Reinsurance binder capacities, dynamic program question contracts, quotes, policies, and webhook synchronization.</li>
        </ul>
      </div>
    `
  },
  {
    id: "account-hierarchy",
    title: "Account Hierarchy",
    group: "Getting Started",
    description: `
      <h2>Account & Organization Hierarchy</h2>
      <p style="font-size: 15px; color: var(--text-secondary); line-height: 1.7;">
        Facio enforces a multi-tier organizational model for Managing General Agents (MGAs) and their partner networks. Understanding the relationship between Tenants, Accounts, Users, and API Keys is essential for proper authorization boundaries.
      </p>

      <div class="glass-card" style="margin-top: 24px; border-left: 4px solid var(--accent-purple); padding: 16px; margin-bottom: 24px;">
        <div class="glass-card-title" style="color: var(--accent-purple); font-weight: 600; margin-bottom: 10px;">
          Structural Hierarchy Overview
        </div>
        <ul style="margin-left: 20px; line-height: 1.6; color: var(--text-secondary);">
          <li><strong>Tenant (MGA / Operating Entity):</strong> The top-level partition. Isolated database instance mapped to a dedicated subdomain (e.g. <code>tenant_default</code>). All data resides within a specific tenant context.</li>
          <li><strong>Account (Distributor / Organization):</strong> Accounts exist within a Tenant. They represent broker organizations, corporate clients, or internal divisions. Accounts are categorized as <code>INTERNAL</code> or <code>CUSTOMER</code>.</li>
          <li><strong>User:</strong> Human operators (underwriters, brokers, customers) associated with one or more Accounts. Governed by roles (<code>ADMIN</code>, <code>UNDERWRITER</code>, <code>CUSTOMER</code>).</li>
          <li><strong>API Key:</strong> Scoped credential keys issued under an Account. The key inherits account scopes, determining which policy, quote, and claim records can be mutated.</li>
        </ul>
      </div>

      <h3>Agent Gateway OAuth 2.1 & PKCE Authentication</h3>
      <p style="font-size: 14.5px; color: var(--text-secondary); line-height: 1.6; margin-bottom: 16px;">
        For the <strong>Agent Gateway (MCP)</strong> and desktop/mobile client integrations, Facio supports standard <strong>OAuth 2.1</strong> flow with Proof Key for Code Exchange (PKCE - RFC 7636).
        Clients register dynamically to obtain credentials and requests are authorized using transient bearer tokens.
      </p>
      
      <div style="margin-top: 20px; overflow-x: auto;">
        <table class="parameter-table" style="width: 100%;">
          <thead>
            <tr style="border-bottom: 2px solid var(--border-color); text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted);">
              <th style="padding: 10px; min-width: 150px;">Flow Endpoint</th>
              <th style="padding: 10px; min-width: 100px;">HTTP Method</th>
              <th style="padding: 10px; min-width: 350px;">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">/oauth/register</td>
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">POST</td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">RFC 7591 Dynamic Client Registration to register new agent clients.</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">/oauth/authorize</td>
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">GET</td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">User authorization request endpoint (supports code challenge and PKCE verification).</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">/oauth/token</td>
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">POST</td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Exchange authorization code (with <code>code_verifier</code>) or refresh tokens for active bearer tokens.</td>
            </tr>
          </tbody>
        </table>
      </div>
    `
  },
  {
    id: "quote-bind-workflow",
    title: "Quote-Bind Workflow",
    group: "Getting Started",
    description: `
      <h2>Quote-Bind Self-Onboarding Golden Path</h2>
      <p>This business flow represents the minimum golden path for a distributor or broker wishing to construct a custom digital self-onboarding journey for insurance customers. It details the step-by-step API integration loop to move from product catalog discovery to a bound, active policy.</p>
      
      <div class="golden-path-timeline">
        <div class="golden-path-step">
          <div class="golden-path-step-badge">1</div>
          <div class="golden-path-step-content">
            <h3>Discover Available Programs</h3>
            <p>Query the MGA catalogs registry using <code>GET /v1/programs</code>. This returns the list of active underwriting programs authorized for your organization's distribution, including coverage options, active jurisdictions, and syndicates capacity metadata.</p>
            <pre class="tour-code-block" style="margin-top: 10px;"><code>GET https://api.facio.io/api/v1/programs</code></pre>
          </div>
        </div>
        
        <div class="golden-path-step">
          <div class="golden-path-step-badge">2</div>
          <div class="golden-path-step-content">
            <h3>Retrieve the Dynamic Quote Data Contract</h3>
            <p>Every underwriting program maintains its own independent schema containing risk variables, validation fields, and mandatory question items. Query <code>GET /v1/programs/{programId}/questions</code> to fetch the JSON Schema contract. Use this to construct your frontend form elements dynamically.</p>
            <pre class="tour-code-block" style="margin-top: 10px;"><code>GET https://api.facio.io/api/v1/programs/e9f8a000-84b2-4d1e-bd15-0d6789b52000/questions</code></pre>
          </div>
        </div>

        <div class="golden-path-step">
          <div class="golden-path-step-badge">3</div>
          <div class="golden-path-step-content">
            <h3>Submit Risk Payload for Quote</h3>
            <p>Collect customer responses and map them to the <code>quoteData</code> object. Submit the payload to <code>POST /v1/quotes</code> to trigger the rating engine. The response returns calculated premiums and coverage options, or routes the quote to an underwriting referral queue if rules dictate.</p>
            <pre class="tour-code-block" style="margin-top: 10px;"><code>POST https://api.facio.io/api/v1/quotes</code></pre>
          </div>
        </div>

        <div class="golden-path-step">
          <div class="golden-path-step-badge">4</div>
          <div class="golden-path-step-content">
            <h3>Issue Payment Intent</h3>
            <p>Allocate a payment transaction block linked to the quote. Submit the generated <code>quoteId</code> to <code>POST /v1/quotes/{quoteId}/payment-intent</code> to structure billing, register premium invoices, and capture card credentials.</p>
            <pre class="tour-code-block" style="margin-top: 10px;"><code>POST https://api.facio.io/api/v1/quotes/q_d4b1a201-44b0-4e0a-bf15-1d6789c52001/payment-intent</code></pre>
          </div>
        </div>

        <div class="golden-path-step">
          <div class="golden-path-step-badge">5</div>
          <div class="golden-path-step-content">
            <h3>Bind Policy & Issue Documents</h3>
            <p>Submit the quote token to <code>POST /v1/policies</code> to commit the double-entry accounting ledger transaction, bind coverage risk, and automatically dispatch PDF insurance certificate packages to the customer.</p>
            <pre class="tour-code-block" style="margin-top: 10px;"><code>POST https://api.facio.io/api/v1/policies</code></pre>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "back-office-workflows",
    title: "Back-Office Workflows",
    group: "Getting Started",
    description: `
      <h2>Back-Office Servicing & Lifecycle Operations</h2>
      <p>This flow governs post-binding operational procedures, allowing internal back-office operators or CRM connectors to manage claims reserves, process mid-term adjustments (MTAs), inspect ledgers, and manage quote revision workshops.</p>
      
      <div class="golden-path-timeline">
        <div class="golden-path-step">
          <div class="golden-path-step-badge">1</div>
          <div class="golden-path-step-content">
            <h3>Query Active Policy Context</h3>
            <p>Retrieve historical policy data, endorsement history, and premium ledger lists via <code>GET /v1/policies/{policyId}</code> to resolve current binder states.</p>
          </div>
        </div>

        <div class="golden-path-step">
          <div class="golden-path-step-badge">2</div>
          <div class="golden-path-step-content">
            <h3>Process Mid-Term Adjustments (MTA)</h3>
            <p>Create an endorsement proposal by calling <code>POST /v1/policies/{policyId}/endorsements/quote</code> with updated risk answers. The rating engine evaluates the pro-rata premium delta. To bind, invoke <code>POST /v1/policies/{policyId}/endorsements/bind</code> to commit the changes and issue modified certificates.</p>
          </div>
        </div>

        <div class="golden-path-step">
          <div class="golden-path-step-badge">3</div>
          <div class="golden-path-step-content">
            <h3>Manage Claims & Reserves Accounting</h3>
            <p>When an incident is reported, create a First Notice of Loss via <code>POST /v1/claims</code>. Track indemnity and expense accounts dynamically by posting double-entry transactions to the claims financial ledger using reserve adjustments endpoints.</p>
          </div>
        </div>

        <div class="golden-path-step">
          <div class="golden-path-step-badge">4</div>
          <div class="golden-path-step-content">
            <h3>Renewal Invitations</h3>
            <p>Check the automated renewal cron timeline, generate renewal price proposals based on updated risk profiles, and issue secure email requoting links to the policyholder.</p>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "lloyds-crs",
    title: "Lloyd's CRS V5.2 Standards",
    group: "Getting Started",
    description: `
      <h2>Lloyd's Coverholder Reporting Standards (CRS) V5.2</h2>
      <p style="font-size: 15px; color: var(--text-secondary); line-height: 1.7; margin-bottom: 24px;">
        Lloyd's V5.2 Coverholder Reporting Standards (CRS) enforce strict rules for MGAs operating as Lloyd's Coverholders. 
        Reporting coverholder business requires aligning key transaction and claim fields to standard <strong>CR Codes</strong> (Coverholder Reporting Codes) on Risk, Premium, and Claims bordereaux.
      </p>
      
      <div class="glass-card" style="margin-top: 24px; border: 1px solid rgba(99, 91, 255, 0.2); background: rgba(99, 91, 255, 0.02); padding: 16px; border-radius: var(--border-radius-md); margin-bottom: 24px;">
        <div class="glass-card-title" style="color: var(--accent-purple); display: flex; align-items: center; gap: 8px; font-weight: 600; margin-bottom: 12px;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
          Key Compliance Identifiers
        </div>
        <p style="font-size: 13.5px; line-height: 1.6; color: var(--text-secondary); margin-bottom: 8px;">
          <strong>Unique Market Reference (UMR):</strong> Designated as <code>CR0005</code>, this is a 17-character unique binder authority reference allocated by Lloyd's (e.g. <code>B1262847291039482</code>).
        </p>
        <p style="font-size: 13.5px; line-height: 1.6; color: var(--text-secondary); margin-bottom: 0;">
          <strong>Coverholder PIN (6+3 Format):</strong> Designated as <code>CR0014</code>, this is a 9-character identifier representing the coverholder branch. It consists of a 6-digit numeric portion followed by a 3-character branch suffix (e.g., <code>123456XTD</code>).
        </p>
      </div>

      <h3 style="margin-top: 32px; margin-bottom: 12px; font-family: var(--font-display); font-size: 18px;">Facio API Data Model Mapping Reference</h3>
      <p style="font-size: 13.5px; color: var(--text-secondary); margin-bottom: 16px;">
        The Facio Developer Portal integrates these CR Codes directly into the API response schemas. Below is a comprehensive cross-reference table mapping key API properties to their respective Lloyd's CRS V5.2 compliance fields:
      </p>

      <div style="margin-top: 20px; overflow-x: auto;">
        <table class="parameter-table" style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="border-bottom: 2px solid var(--border-color); text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted);">
              <th style="padding: 10px; min-width: 120px;">Facio API Object</th>
              <th style="padding: 10px; min-width: 150px;">Field Path</th>
              <th style="padding: 10px; min-width: 100px;">Lloyd's CR Code</th>
              <th style="padding: 10px; min-width: 250px;">Standard Field Description</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Policy / Quote</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">programId</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0005</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Unique Market Reference (UMR)</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Policy / Claim</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">policyNumber / policyId</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0029</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Certificate Reference</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Policy / Quote</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">effectiveDate</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0030</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Risk Inception Date</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Policy / Quote</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">expiryDate</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0031</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Risk Expiry Date</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Policy / Quote</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">grossPremium</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0021</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Total Gross Written Premium</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Policy / Quote</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">netPremium</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR1280</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Final Net Written Premium to Underwriters</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Policy / Quote</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">ipt</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0083</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Total Insurance Premium Tax</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Policy / Quote</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">commission</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0235</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Coverholder Commission Amount</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Claim</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">claimNumber</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0104</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Claim Reference / Number</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Claim</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">lossDate</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0119</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Date of Loss</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Claim</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">reserves.outstandingLoss</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0130</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Reserve - Indemnity</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Claim</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">reserves.outstandingExpense</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0131</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Reserve - Fees</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Endorsement</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">effectiveDate</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0057</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Effective Date of Transaction</td>
            </tr>
            <tr style="border-bottom: 1px solid var(--border-color);">
              <td style="padding: 12px; font-weight: 600; font-size: 13px;">Endorsement</td>
              <td style="padding: 12px; font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan);">grossPremiumDelta</td>
              <td style="padding: 12px;"><span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-family: var(--font-mono); font-size: 11px; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">CR0059</span></td>
              <td style="padding: 12px; color: var(--text-secondary); font-size: 13px;">Gross Premium Paid/Returned</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="glass-card" style="margin-top: 24px; padding: 16px; border-radius: var(--border-radius-md); border: 1px solid rgba(6, 182, 212, 0.2); background: rgba(6, 182, 212, 0.02); margin-bottom: 24px;">
        <div class="glass-card-title" style="color: var(--accent-cyan); display: flex; align-items: center; gap: 8px; font-weight: 600; margin-bottom: 12px;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
          Verification against Bordereaux Engine
        </div>
        <p style="font-size: 13.5px; line-height: 1.6; color: var(--text-secondary); margin-bottom: 0;">
          All Facio platform events containing financial transactions will automatically push validated events mapping to these CR Codes. 
          When exporting standard bordereaux (via <code>POST /v1/bdx/exports</code>), the reporting module formats these fields exactly according to the Lloyd's CRS V5.2 spreadsheet specifications.
        </p>
      </div>
    `
  }
];

const apiEndpoints = [
  ...sicClassificationEndpoints,
  // A. TENANT & HEALTH
  {
    id: "get-health",
    path: "/v1/health",
    method: "GET",
    tag: "Tenant & Health",
    summary: "Service Health Check",
    description: "Returns the current system operational status and dependent ledger subsystem health flags. This is a public endpoint and does not require bearer API key authentication.",
    parameters: [],
    requestBody: null,
    response: {
      status: 200,
      description: "Service is healthy.",
      body: {
        success: true,
        status: "OK",
        timestamp: "2026-06-09T00:00:00Z",
        services: {
          database: "CONNECTED",
          ratingEngine: "HEALTHY",
          ledgerWriter: "HEALTHY"
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "DISCOVER",
      "x-facio-domain": "INFRASTRUCTURE"
    }
  },
  {
    id: "get-me",
    path: "/v1/me",
    method: "GET",
    tag: "Tenant & Health",
    summary: "Retrieve Tenant Profile",
    description: "Returns details about the caller's tenant profile, active insurance licenses, and the specific authorization scopes configured on the bearer API key.",
    parameters: [],
    requestBody: null,
    response: {
      status: 200,
      description: "Tenant profile details returned successfully.",
      body: {
        success: true,
        data: {
          tenantId: "tenant_default",
          companyName: "Abbeygate Coverholders Ltd",
          environment: "sandbox",
          roles: ["COVERHOLDER"],
          scopes: [
            "programs:read",
            "quotes:write",
            "quotes:read",
            "policies:bind",
            "policies:read",
            "claims:write",
            "ledger:read"
          ],
          activePrograms: [
            "e9f8a000-84b2-4d1e-bd15-0d6789b52000"
          ]
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "DISCOVER",
      "x-facio-domain": "AUTH"
    }
  },
  // B. PROGRAMS & CATALOGS
  {
    id: "list-programs",
    path: "/v1/programs",
    method: "GET",
    tag: "Programs & Catalogs",
    summary: "Discover Insurance Products",
    description: "Retrieves the current catalog of insurance products (programs) your facility is contractually authorized to distribute, quote, and bind.",
    parameters: [
      { name: "limit", in: "query", type: "integer", required: false, description: "Maximum number of records to return (1-100, default 20)" },
      { name: "after", in: "query", type: "string", required: false, description: "Cursor token for fetching the next page" },
      { name: "before", in: "query", type: "string", required: false, description: "Cursor token for fetching the previous page" },
      { name: "status", in: "query", type: "string", required: false, description: "Filter by status" }
    ],
    requestBody: null,
    response: {
      status: 200,
      description: "Array of program catalogs returned successfully.",
      body: {
        success: true,
        data: [
          {
            id: "e9f8a000-84b2-4d1e-bd15-0d6789b52000",
            name: "EU Motor Scheme v2",
            status: "ACTIVE",
            productType: "MOTOR",
            currency: "EUR",
            carrier: "Lloyd's Syndicate 1991"
          }
        ],
        meta: {
          hasMore: false,
          nextCursor: null,
          prevCursor: null,
          totalCount: 1
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "DISCOVER",
      "x-facio-domain": "CATALOG",
      "x-facio-product-agnostic": true,
      "x-facio-risk-controls": ["binder_authority"]
    }
  },
  {
    id: "get-program",
    path: "/v1/programs/{programId}",
    method: "GET",
    tag: "Programs & Catalogs",
    summary: "Retrieve Program Profile",
    description: "Fetches complete program profile configurations including delegated authority limits and carrier mappings.",
    parameters: [
      { name: "programId", in: "path", type: "string (UUID)", required: true, description: "Unique program ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: {
          id: "e9f8a000-84b2-4d1e-bd15-0d6789b52000",
          name: "EU Motor Scheme v2",
          productType: "MOTOR",
          status: "ACTIVE"
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "DISCOVER",
      "x-facio-domain": "CATALOG"
    }
  },
  {
    id: "get-program-questions",
    path: "/v1/programs/{programId}/questions",
    method: "GET",
    tag: "Programs & Catalogs",
    summary: "Retrieve Program Questions Schema",
    description: "Fetches the exact dynamic pricing schema required by the Risk Engine to generate a valid quote for this specific product. Returns a standard JSON Schema draft-07.",
    parameters: [
      { name: "programId", in: "path", type: "string (UUID)", required: true, description: "Unique program ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: {
          $schema: "http://json-schema.org/draft-07/schema#",
          title: "MotorQuestionnaire",
          type: "object",
          required: ["proposer", "vehicleValue", "engineSize", "licenseYears"],
          properties: {
            vehicleValue: { type: "number", minimum: 100 },
            engineSize: { type: "integer", minimum: 500 }
          }
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "DISCOVER",
      "x-facio-domain": "CATALOG"
    }
  },
  {
    id: "get-program-coverages",
    path: "/v1/programs/{programId}/coverages",
    method: "GET",
    tag: "Programs & Catalogs",
    summary: "Retrieve Program Coverages Matrix",
    description: "Lists all standard and optional covers available under the program.",
    parameters: [
      { name: "programId", in: "path", type: "string (UUID)", required: true, description: "Unique program ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [
          { code: "BASE_LIABILITY", name: "Third Party Liability", mandatory: true }
        ]
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "DISCOVER",
      "x-facio-domain": "CATALOG"
    }
  },
  {
    id: "get-program-rules",
    path: "/v1/programs/{programId}/rules",
    method: "GET",
    tag: "Programs & Catalogs",
    summary: "Retrieve Underwriting Rule Books",
    description: "Returns metadata about the active underwriting parameters governing automatic accept, referral, or decline.",
    parameters: [
      { name: "programId", in: "path", type: "string (UUID)", required: true, description: "Unique program ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: { rulesetVersion: "r_2026_03" }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "DISCOVER",
      "x-facio-domain": "CATALOG"
    }
  },
  {
    id: "get-program-documents",
    path: "/v1/programs/{programId}/documents",
    method: "GET",
    tag: "Programs & Catalogs",
    summary: "Retrieve Document Templates Map",
    description: "Returns the template metadata schemas generated upon binding.",
    parameters: [
      { name: "programId", in: "path", type: "string (UUID)", required: true, description: "Unique program ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [{ type: "SCHEDULE", templateName: "mga_motor_schedule_v4" }]
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "DISCOVER",
      "x-facio-domain": "CATALOG"
    }
  },
  {
    id: "get-program-jurisdictions",
    path: "/v1/programs/{programId}/jurisdiction-requirements",
    method: "GET",
    tag: "Programs & Catalogs",
    summary: "Retrieve Jurisdiction Requirements",
    description: "Returns specific reporting keys, regional taxes, and local regulatory mandates required for binder compliance.",
    parameters: [
      { name: "programId", in: "path", type: "string (UUID)", required: true, description: "Unique program ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: { jurisdiction: "CY", taxRates: { insurancePremiumTaxPercent: 10.0 } }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "DISCOVER",
      "x-facio-domain": "CATALOG"
    }
  },

  // C. PRICING & QUOTES
  {
    id: "create-quote",
    path: "/v1/quotes",
    method: "POST",
    tag: "Pricing & Quotes",
    summary: "Generate Insurance Quote",
    description: `Submits risk factors against a program to execute the rating engine, returning a firm premium breakdown or underwriting referral status.
      <br/><br/>
      <strong>Dynamic Schema Integration</strong>: Fetch the dynamic questionnaire schema from <a href="#get-program-questions">GET /v1/programs/{programId}/questions</a> to construct the <code>quoteData</code> object matching the active program's constraints before calling this endpoint.
      <br/><br/>
      <strong>Rating Engine Integration</strong>: Upon receiving a payload, the rating engine executes rating algorithms tied to the selected <code>programId</code>. The underwriting algorithm assesses zip code exclusions, age restrictions, and claims history. If the risk meets automatic rules, the engine returns a <code>QUOTED</code> state alongside a cryptographically signed JWT token (<code>quoteToken</code>) which guarantees the quoted rates for binding. If the risk exceeds automated authority limits but falls within tolerable limits, the state transitions to <code>REFERRED</code> for manual underwriting review.
      <br/><br/>
      <strong>Reinsurance Checks</strong>: Prior to quote generation, the system performs validation checks against active binders, verifying binder authority caps and carrier capacity restrictions. If capacity is exhausted, a <code>400 binder_capacity_exhausted</code> error is returned.`,
    hasDynamicSchema: true,
    parameters: [],
    requestBody: {
      description: "Complete risk questionnaire data matching program schema requirements.",
      fields: {
        programId: "e9f8a000-84b2-4d1e-bd15-0d6789b52000",
        effectiveDate: "2026-03-01T00:00:00Z",
        quoteData: {
          proposer: { firstName: "Jane", lastName: "Doe", email: "jane.doe@example.com" },
          vehicleValue: 18000,
          engineSize: 1600,
          licenseYears: 10
        },
        selectedCoverages: ["BASE_LIABILITY"],
        standard_config: {
          deductible: 500,
          max_limit: 100000,
          payment_frequency: "ANNUALLY"
        },
        dynamic_config: {
          optional_covers: ["roadside_assistance", "windshield_cover"],
          discretionary_discount: 0.05
        }
      }
    },
    response: {
      status: 201,
      description: "Quote generated successfully.",
      body: {
        success: true,
        data: {
          quoteId: "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001",
          quoteToken: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
          status: "QUOTED",
          premiumBreakdown: { grossPremium: 488.50, netPremium: 400.00, ipt: 40.00, commission: 46.50, stampDuty: 2.00 },
          standard_config: {
            deductible: 500,
            max_limit: 100000,
            payment_frequency: "ANNUALLY"
          },
          dynamic_config: {
            optional_covers: ["roadside_assistance", "windshield_cover"],
            discretionary_discount: 0.05
          }
        }
      }
    },
    errorCodes: [
      { status: 400, code: "quote_capability_not_supported", description: "The requested coverages or capabilities are not available for this program." },
      { status: 400, code: "invalid_proposer_dob", description: "Proposer's birthdate indicates an age outside of allowed driving limits." },
      { status: 400, code: "binder_capacity_exhausted", description: "MGA binder capacity has been fully allocated for this risk category." },
      { status: 400, code: "underwriting_rules_blocked", description: "Immediate automated decline triggered based on high-risk vehicle or underwriting history." },
      { status: 429, code: "rate_limit_exceeded", description: "Concurrent rate limit exceeded on rating calculations." }
    ],
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "RATING",
      "x-facio-product-agnostic": true,
      "x-facio-related-webhooks": ["quote.created"],
      "x-facio-next-actions": [
        { label: "Bind Quote & Issue Policy", method: "POST", path: "/v1/policies" }
      ]
    }
  },
  {
    id: "get-quote",
    path: "/v1/quotes/{quoteId}",
    method: "GET",
    tag: "Pricing & Quotes",
    summary: "Retrieve Quote Details",
    description: "Retrieves status and premium details of an active, referred, or expired quote.",
    parameters: [
      { name: "quoteId", in: "path", type: "string (UUID)", required: true, description: "Unique quote ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: { quoteId: "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001", status: "QUOTED", premiumCalculated: 488.50 }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "RATING"
    }
  },
  {
    id: "refresh-quote",
    path: "/v1/quotes/{quoteId}/refresh",
    method: "POST",
    tag: "Pricing & Quotes",
    summary: "Recalculate / Refresh Quote",
    description: "Re-triggers rating execution for an existing quote token.",
    parameters: [
      { name: "quoteId", in: "path", type: "string (UUID)", required: true, description: "Unique quote ID" }
    ],
    requestBody: {
      description: "Optional parameters to adjust rating when refreshing the quote",
      fields: {
        extendRateLock: true,
        effectiveDate: "2026-06-15T00:00:00Z"
      }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: { quoteId: "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001", status: "QUOTED", premiumCalculated: 492.00 }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "RATING"
    }
  },
  {
    id: "refer-quote",
    path: "/v1/quotes/{quoteId}/refer",
    method: "POST",
    tag: "Pricing & Quotes",
    summary: "Escalate to Underwriter",
    description: "Manually forces a quote to REFERRED status for individual underwriter review.",
    parameters: [
      { name: "quoteId", in: "path", type: "string (UUID)", required: true, description: "Unique quote ID" }
    ],
    requestBody: {
      description: "Reason description details",
      fields: { reason: "Driver modification review required." }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: { quoteId: "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001", status: "REFERRED" }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "RATING"
    }
  },
  {
    id: "decline-quote",
    path: "/v1/quotes/{quoteId}/decline",
    method: "POST",
    tag: "Pricing & Quotes",
    summary: "Decline Quote Proposal",
    description: "Sets the status of a quote proposal to DECLINED with a decline reason code.",
    parameters: [
      { name: "quoteId", in: "path", type: "string (UUID)", required: true, description: "Unique quote ID" }
    ],
    requestBody: {
      description: "Decline reason details",
      fields: {
        declineReasonCode: "RISK_OUTSIDE_APPETITE",
        notes: "Proposer's vehicle modifications exceed the authorized program limits."
      }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: { quoteId: "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001", status: "DECLINED" }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "RATING"
    }
  },
  {
    id: "quote-payment-intent",
    path: "/v1/quotes/{quoteId}/payment-intent",
    method: "POST",
    tag: "Pricing & Quotes",
    summary: "Create Quote Payment Intent",
    description: "Generates CardCorp checkout identifiers and URLs to collect premiums before binding.",
    parameters: [
      { name: "quoteId", in: "path", type: "string (UUID)", required: true, description: "Unique quote ID" }
    ],
    requestBody: {
      description: "Payment intent initialization parameters.",
      fields: {
        paymentMethodType: "CREDIT_CARD",
        provider: "CARDCORP",
        successUrl: "https://your-mga-portal.com/success",
        cancelUrl: "https://your-mga-portal.com/cancel"
      }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: {
          checkoutId: "cc_chk_facio_test_9482",
          merchantTransactionId: "cc_tx_facio_test_0492",
          checkoutUrl: "https://checkout.cardcorp.com/pay/pay_facio_test_9482"
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "FINANCIAL"
    }
  },
  {
    id: "list-quotes",
    path: "/v1/quotes",
    method: "GET",
    tag: "Pricing & Quotes",
    summary: "List and Search Quotes",
    description: "Queries a paginated list of generated insurance quote proposals. Filter by status, programId, or proposer email.",
    parameters: [
      { name: "limit", in: "query", type: "integer", required: false, description: "Maximum number of records to return (1-100, default 20)" },
      { name: "after", in: "query", type: "string", required: false, description: "Cursor token for fetching the next page" },
      { name: "before", in: "query", type: "string", required: false, description: "Cursor token for fetching the previous page" },
      { name: "status", in: "query", type: "string", required: false, description: "Filter by status (QUOTED, REFERRED, DECLINED, EXPIRED)" },
      { name: "programId", in: "query", type: "string (UUID)", required: false, description: "Filter by program ID" },
      { name: "email", in: "query", type: "string", required: false, description: "Filter by proposer email address" }
    ],
    requestBody: null,
    response: {
      status: 200,
      description: "Paginated list of quote records.",
      body: {
        success: true,
        data: [
          {
            quoteId: "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001",
            status: "QUOTED",
            effectiveDate: "2026-03-01T00:00:00Z",
            programId: "e9f8a000-84b2-4d1e-bd15-0d6789b52000",
            premiumBreakdown: { grossPremium: 488.50, netPremium: 400.00, ipt: 40.00, commission: 60.00, stampDuty: 2.00 }
          }
        ],
        meta: {
          hasMore: false,
          nextCursor: null,
          prevCursor: null,
          totalCount: 1
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "RATING",
      "x-facio-product-agnostic": true
    }
  },
  {
    id: "clone-quote",
    path: "/v1/quotes/{quoteId}/clone",
    method: "POST",
    tag: "Pricing & Quotes",
    summary: "Clone Quote with Overrides",
    description: "Duplicates an existing quote proposal and calculates a new quote with optional override factors, avoiding redundant form data collection.",
    parameters: [
      { name: "quoteId", in: "path", type: "string (UUID)", required: true, description: "Unique quote ID to clone" }
    ],
    requestBody: {
      description: "Optional risk factor modifications and coverage selections.",
      fields: {
        overrides: {
          vehicleValue: 20000,
          selectedCoverages: ["BASE_LIABILITY", "CV_172"]
        }
      }
    },
    response: {
      status: 201,
      description: "Cloned quote generated successfully.",
      body: {
        success: true,
        data: {
          quoteId: "q_2c9d1d9f-44b0-4e0a-bf15-1d6789c52002",
          quoteToken: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
          status: "QUOTED",
          premiumBreakdown: { grossPremium: 512.40, netPremium: 420.00, ipt: 42.00, commission: 62.00, stampDuty: 2.00 }
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "RATING"
    }
  },

  // D. POLICY LIFECYCLE
  {
    id: "bind-policy",
    path: "/v1/policies",
    method: "POST",
    tag: "Policy Lifecycle",
    summary: "Bind Quote & Issue Policy",
    description: `Executes financial commitment. Converts a quoted rate into a bound policy.
      <br/><br/>
      <strong>Double-Entry Ledger Bookings</strong>: At the instant of binding, the Facio core transaction ledger records double-entry bookkeeping entries to track gross premium receivables, coverholder commissions, carrier net premiums, and state taxes (e.g. IPT and stamp duty). These balances are locked and cannot be altered retrospectively.
      <br/><br/>
      <strong>Policy Issuance & Compliance</strong>: Confirms real-time sanctions screening checks (OFAC/EU registers). Upon successful authorization, the policy status transitions to <code>ISSUED</code>, booking a unique policy number, and triggering asynchronous PDF certificate compilation for immediate broker delivery.`,
    parameters: [],
    requestBody: {
      description: "Quote ID and security token.",
      fields: {
        quoteId: "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001",
        quoteToken: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
        paymentMethod: { type: "CREDIT_CARD", provider: "CARDCORP", checkoutId: "cc_chk_facio_test_9482" }
      }
    },
    response: {
      status: 201,
      body: {
        success: true,
        data: {
          policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
          policyNumber: "POL-2026-84729",
          status: "ISSUED"
        }
      }
    },
    errorCodes: [
      { status: 400, code: "quote_already_bound", description: "The provided quote has already been bound into an active policy." },
      { status: 400, code: "quote_expired", description: "The quote validity period has expired and must be re-rated." },
      { status: 400, code: "binding_authority_exceeded", description: "The net premium exceeds your coverholder binder authority delegation limit." },
      { status: 400, code: "payment_authorization_failed", description: "Premium transaction failed. Check card credentials or funds." },
      { status: 409, code: "idempotency_error", description: "An idempotent retry occurred with different request parameters." }
    ],
    extensions: {
      "x-facio-lifecycle-stage": "BIND",
      "x-facio-domain": "POLICY_ADMIN",
      "x-facio-product-agnostic": true,
      "x-facio-related-webhooks": ["policy.bound", "policy.issued"],
      "x-facio-next-actions": [
        { label: "Retrieve Issued Documents", method: "GET", path: "/v1/policies/{policyId}/documents" }
      ],
      "x-facio-risk-controls": ["sanctions_screening", "binder_authority", "binder_capacity"]
    }
  },
  {
    id: "get-policy",
    path: "/v1/policies/{policyId}",
    method: "GET",
    tag: "Policy Lifecycle",
    summary: "Retrieve Policy Details",
    description: "Returns the current state projection of an active or expired policy.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: { policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02", policyNumber: "POL-2026-84729", status: "ACTIVE" }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },
  {
    id: "list-policies",
    path: "/v1/policies",
    method: "GET",
    tag: "Policy Lifecycle",
    summary: "List and Search Policies",
    description: "Queries a paginated list of bound insurance policies. Filter by status, programId, policyNumber, or proposer email.",
    parameters: [
      { name: "limit", in: "query", type: "integer", required: false, description: "Maximum number of records to return (1-100, default 20)" },
      { name: "after", in: "query", type: "string", required: false, description: "Cursor token for fetching the next page" },
      { name: "before", in: "query", type: "string", required: false, description: "Cursor token for fetching the previous page" },
      { name: "status", in: "query", type: "string", required: false, description: "Filter by status (ISSUED, ACTIVE, CANCELLED, EXPIRED)" },
      { name: "programId", in: "query", type: "string (UUID)", required: false, description: "Filter by program ID" },
      { name: "policyNumber", in: "query", type: "string", required: false, description: "Filter by policy number" },
      { name: "email", in: "query", type: "string", required: false, description: "Filter by proposer email address" }
    ],
    requestBody: null,
    response: {
      status: 200,
      description: "Paginated list of policy records.",
      body: {
        success: true,
        data: [
          {
            policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
            policyNumber: "POL-PRIVATEMOTOR-84729",
            status: "ACTIVE",
            programId: "e9f8a000-84b2-4d1e-bd15-0d6789b52000",
            effectiveDate: "2026-03-01T00:00:00Z",
            expiryDate: "2027-02-28T23:59:59Z"
          }
        ],
        meta: {
          hasMore: false,
          nextCursor: null,
          prevCursor: null,
          totalCount: 1
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "POLICY_ADMIN",
      "x-facio-product-agnostic": true
    }
  },
  {
    id: "get-policy-timeline",
    path: "/v1/policies/{policyId}/timeline",
    method: "GET",
    tag: "Policy Lifecycle",
    summary: "Retrieve Policy Event Timeline",
    description: "Returns the audit log of all transitions, transactions, and endorsements executed on this policy.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [{ event: "V1_BINDING_INCEPTION", status: "ISSUED", timestamp: "2026-03-08T22:10:00Z" }]
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },
  {
    id: "get-policy-documents",
    path: "/v1/policies/{policyId}/documents",
    method: "GET",
    tag: "Policy Lifecycle",
    summary: "Retrieve Issued Documents",
    description: "Fetches pre-signed download URLs for generated PDF documents (Schedules, certificates, and invoices) associated with this bound risk.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [
          { id: "f8d9e000-66d2-5f2c-dh37-3f8901eeda04", type: "SCHEDULE", filename: "Schedule_POL-2026-84729.pdf", url: "https://docs.facio.io/Schedule.pdf" }
        ]
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },
  {
    id: "get-policy-financials",
    path: "/v1/policies/{policyId}/financials",
    method: "GET",
    tag: "Policy Lifecycle",
    summary: "Retrieve Premium Financials Summary",
    description: "Returns an aggregate balance sheet of premium accounting.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: { totalPremiumPaid: 488.50, outstandingBalance: 0.00 }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "FINANCIAL"
    }
  },
  {
    id: "cancel-policy",
    path: "/v1/policies/{policyId}/cancel",
    method: "POST",
    tag: "Policy Lifecycle",
    summary: "Cancel Insurance Policy",
    description: "Terminates an active contract, recalculating pro-rata or short-rate premium returns.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: {
      description: "Cancellation parameters",
      fields: { cancellationDate: "2026-06-01T00:00:00Z", reason: "Requested by client", calculationMethod: "PRO_RATA" }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: { policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02", status: "CANCELLED", refundPremium: 364.80 }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "POLICY_ADMIN",
      "x-facio-related-webhooks": ["policy.cancelled"]
    }
  },
  {
    id: "reinstate-policy",
    path: "/v1/policies/{policyId}/reinstate",
    method: "POST",
    tag: "Policy Lifecycle",
    summary: "Reinstate Cancelled Policy",
    description: "Restores a cancelled policy back to active status, requiring a reinstatement date, reason, and payment confirmation details.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: {
      description: "Reinstatement parameters",
      fields: {
        reinstatementDate: "2026-06-09T00:00:00Z",
        reason: "Lapsed payment resolved",
        paymentDetails: {
          paymentMode: "CUSTOMER_CARD",
          provider: "CARDCORP",
          checkoutId: "chk_9482710a"
        }
      }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: { policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02", status: "ACTIVE" }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },
  {
    id: "regenerate-policy-documents",
    path: "/v1/policies/{policyId}/documents/regenerate",
    method: "POST",
    tag: "Policy Lifecycle",
    summary: "Force PDF Documents Re-generation",
    description: "Re-triggers template compilation for specified document packs or versions.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: {
      description: "Document packs to regenerate",
      fields: {
        documentTypes: ["SCHEDULE", "CERTIFICATE_OF_INSURANCE"],
        version: 2,
        reason: "Insured name corrected on MTA endorsement"
      }
    },
    response: {
      status: 200,
      body: { success: true, message: "Regeneration queued." }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },
  {
    id: "get-policy-sanctions",
    path: "/v1/policies/{policyId}/sanctions",
    method: "GET",
    tag: "Policy Lifecycle",
    summary: "Retrieve Policy Sanction Runs",
    description: "Retrieves the sanction screening outcomes and watchlist search hits checked for this policy proposer at quote/bind checkpoints.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [
          {
            id: "scr_9f3a8271-84b2-4d1e-bd15-0d6789b52000",
            provider: "CREDITSAFE",
            outcome: "CLEAR",
            hitCount: 0,
            providerRiskRating: "LOW",
            executedAt: "2026-06-09T00:47:09Z"
          }
        ]
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "UNDERWRITING"
    }
  },

  // E. ENDORSEMENTS / MTA
  {
    id: "quote-endorsement",
    path: "/v1/policies/{policyId}/endorsements/quote",
    method: "POST",
    tag: "Endorsements / MTA",
    summary: "Draft Mid-Term Adjustment (MTA)",
    description: "Initiates a coverage profile modification to a live contract, calculating pro-rata premium differences.",
    hasDynamicSchema: true,
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: {
      description: "Questionnaire edits.",
      fields: { effectiveDate: "2026-06-01T00:00:00Z", changes: { vehicleValue: 25000 } }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: { endorsementId: "mta_948201a0-44c2", status: "QUOTED", premiumDifference: 84.30 }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "MTA",
      "x-facio-domain": "POLICY_ADMIN",
      "x-facio-next-actions": [
        { label: "Bind MTA", method: "POST", path: "/v1/policies/{policyId}/endorsements/{endorsementId}/bind" }
      ]
    }
  },
  {
    id: "get-endorsement",
    path: "/v1/policies/{policyId}/endorsements/{endorsementId}",
    method: "GET",
    tag: "Endorsements / MTA",
    summary: "Retrieve Endorsement Details",
    description: "Returns details of a draft, bound, or cancelled MTA.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" },
      { name: "endorsementId", in: "path", type: "string", required: true, description: "Unique endorsement ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: { endorsementId: "mta_948201a0-44c2", status: "QUOTED" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "MTA",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },
  {
    id: "bind-endorsement",
    path: "/v1/policies/{policyId}/endorsements/{endorsementId}/bind",
    method: "POST",
    tag: "Endorsements / MTA",
    summary: "Bind Mid-Term Adjustment",
    description: "Commits the drafted MTA adjustments to the policy ledger.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" },
      { name: "endorsementId", in: "path", type: "string", required: true, description: "Unique endorsement ID" }
    ],
    requestBody: {
      description: "Payment confirmation details to bind the additional premium, or refund routing information for return premium",
      fields: {
        paymentMethod: {
          type: "CREDIT_CARD",
          provider: "CARDCORP",
          checkoutId: "cc_chk_mta_9482"
        }
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { endorsementId: "mta_948201a0-44c2", status: "BOUND" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "MTA",
      "x-facio-domain": "POLICY_ADMIN",
      "x-facio-related-webhooks": ["endorsement.bound"]
    }
  },
  {
    id: "cancel-endorsement",
    path: "/v1/policies/{policyId}/endorsements/{endorsementId}/cancel",
    method: "POST",
    tag: "Endorsements / MTA",
    summary: "Cancel Draft Endorsement",
    description: "Deletes a draft endorsement quote.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" },
      { name: "endorsementId", in: "path", type: "string", required: true, description: "Unique endorsement ID" }
    ],
    requestBody: {
      description: "Reason for cancelling the draft endorsement quote",
      fields: {
        reason: "CUSTOMER_REQUEST",
        notes: "Insured decided not to go ahead with vehicle modification."
      }
    },
    response: {
      status: 200,
      body: { success: true, message: "Draft cancelled." }
    },
    extensions: {
      "x-facio-lifecycle-stage": "MTA",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },
  {
    id: "list-endorsements",
    path: "/v1/policies/{policyId}/endorsements",
    method: "GET",
    tag: "Endorsements / MTA",
    summary: "List Policy Endorsements",
    description: "Lists all historic mid-term adjustments linked to the contract.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: [{ endorsementId: "mta_948201a0-44c2", status: "BOUND" }] }
    },
    extensions: {
      "x-facio-lifecycle-stage": "MTA",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },

  // F. RENEWALS
  {
    id: "preview-renewal",
    path: "/v1/policies/{policyId}/renewals/preview",
    method: "POST",
    tag: "Renewals",
    summary: "Preview Renewal Terms",
    description: "Runs rating calculations for the upcoming renewal period based on current risk factors. Since renewals represent terms of policies (using <code>renewalFamilyId</code> and <code>renewalSequence</code> chains) rather than separate database tables, this operates on the core policy record.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: {
      description: "Optional modified risk questionnaire answers for renewal evaluation",
      fields: {
        changes: {
          vehicleValue: 18000,
          driverLicenseYears: 11
        }
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { originalPremium: 488.50, projectedRenewalPremium: 512.00 } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "RENEWAL",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },
  {
    id: "trigger-renewal",
    path: "/v1/policies/{policyId}/renewals",
    method: "POST",
    tag: "Renewals",
    summary: "Trigger Renewal Process",
    description: "Initializes a renewal thread, issuing an INVITED status. This operates on the core policy record to generate the renewal stage terms (the renewal ID returned is the policy ID at the renewal stage, rather than a separate database table record).",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: {
      description: "Parameters to configure the triggered renewal process",
      fields: {
        daysBeforeExpiry: 30,
        sendEmailInvitation: true
      }
    },
    response: {
      status: 201,
      body: { success: true, data: { renewalId: "ren_a9f82000", status: "INVITED" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "RENEWAL",
      "x-facio-domain": "POLICY_ADMIN",
      "x-facio-related-webhooks": ["renewal.invited"],
      "x-facio-next-actions": [
        { label: "Submit Client Declaration", method: "POST", path: "/v1/renewals/{renewalId}/client-declaration" }
      ]
    }
  },
  {
    id: "get-renewal",
    path: "/v1/renewals/{renewalId}",
    method: "GET",
    tag: "Renewals",
    summary: "Retrieve Renewal Profile",
    description: "Returns the current state and declarations associated with a renewal process. The <code>renewalId</code> maps to a policy ID record at the renewal stage, as renewals are lifecycle projections on the <code>Policy</code> table rather than a separate database table.",
    parameters: [
      { name: "renewalId", in: "path", type: "string", required: true, description: "Unique renewal ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: { renewalId: "ren_a9f82000", status: "INVITED" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "RENEWAL",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },
  {
    id: "submit-renewal-declaration",
    path: "/v1/renewals/{renewalId}/client-declaration",
    method: "POST",
    tag: "Renewals",
    summary: "Submit Client Declaration",
    description: "Logs the customer's declaration answers regarding changes in risk factors. The <code>renewalId</code> maps to the policy ID record at the renewal stage.",
    hasDynamicSchema: true,
    parameters: [
      { name: "renewalId", in: "path", type: "string", required: true, description: "Unique renewal ID" }
    ],
    requestBody: {
      description: "Declaration",
      fields: { unchanged: true, notes: "No claims in past year." }
    },
    response: {
      status: 200,
      body: { success: true, data: { renewalId: "ren_a9f82000", status: "QUOTED", renewalPremium: 512.00 } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "RENEWAL",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },
  {
    id: "quote-renewal",
    path: "/v1/renewals/{renewalId}/quote",
    method: "POST",
    tag: "Renewals",
    summary: "Re-calculate Renewal Quote",
    description: "Manually re-rates a renewal following a change in risk parameters declared by the customer. The <code>renewalId</code> maps to the policy ID record at the renewal stage.",
    parameters: [
      { name: "renewalId", in: "path", type: "string", required: true, description: "Unique renewal ID" }
    ],
    requestBody: {
      description: "Amended questionnaire factors for re-rating the renewal",
      fields: {
        vehicleValue: 20000,
        driverInfo: {
          licenseYears: 6,
          claimsCount: 0
        },
        selectedCoverages: ["COMPREHENSIVE", "ROAD_ASSISTANCE"]
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { renewalId: "ren_a9f82000", status: "QUOTED", renewalPremium: 535.00 } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "RENEWAL",
      "x-facio-domain": "RATING"
    }
  },
  {
    id: "renewal-payment-intent",
    path: "/v1/renewals/{renewalId}/payment-intent",
    method: "POST",
    tag: "Renewals",
    summary: "Create Renewal Payment Intent",
    description: "Creates checkout identifiers to capture premium authorization for the renewal policy via CardCorp. The <code>renewalId</code> maps to the policy ID record at the renewal stage.",
    parameters: [
      { name: "renewalId", in: "path", type: "string", required: true, description: "Unique renewal ID" }
    ],
    requestBody: {
      description: "Payment intent initialization parameters.",
      fields: {
        paymentMethodType: "CREDIT_CARD",
        provider: "CARDCORP",
        successUrl: "https://your-mga-portal.com/success",
        cancelUrl: "https://your-mga-portal.com/cancel"
      }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: {
          checkoutId: "cc_chk_renewal_9482",
          merchantTransactionId: "cc_tx_renewal_0492",
          checkoutUrl: "https://checkout.cardcorp.com/pay/pay_facio_test_renewal_9482"
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "RENEWAL",
      "x-facio-domain": "FINANCIAL"
    }
  },
  {
    id: "finalize-renewal",
    path: "/v1/renewals/{renewalId}/finalize",
    method: "POST",
    tag: "Renewals",
    summary: "Finalize & Issue Renewal",
    description: "Binds the renewal contract, issuing a new policy term version. The <code>renewalId</code> maps to the policy ID record at the renewal stage.",
    parameters: [
      { name: "renewalId", in: "path", type: "string", required: true, description: "Unique renewal ID" }
    ],
    requestBody: {
      description: "Payment confirmation details required to bind and issue the renewal",
      fields: {
        paymentMethod: {
          type: "CREDIT_CARD",
          provider: "CARDCORP",
          checkoutId: "cc_chk_renewal_9482"
        }
      }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: { policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02", status: "ACTIVE" }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "RENEWAL",
      "x-facio-domain": "POLICY_ADMIN",
      "x-facio-related-webhooks": ["renewal.finalized"],
      "x-facio-ledger-impact": true,
      "x-facio-bdx-impact": true
    }
  },
  {
    id: "lapse-renewal",
    path: "/v1/renewals/{renewalId}/lapse",
    method: "POST",
    tag: "Renewals",
    summary: "Lapse Renewal",
    description: "Sets the renewal status to LAPSED. The <code>renewalId</code> maps to the policy ID record at the renewal stage.",
    parameters: [
      { name: "renewalId", in: "path", type: "string", required: true, description: "Unique renewal ID" }
    ],
    requestBody: {
      description: "Reason details for lapsing the renewal invitation",
      fields: {
        lapseReason: "CUSTOMER_DECLINED_RENEWAL",
        notes: "Insured found a cheaper alternative and declined to renew."
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { renewalId: "ren_a9f82000", status: "LAPSED" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "RENEWAL",
      "x-facio-domain": "POLICY_ADMIN"
    }
  },

  // G. CLAIMS & FNOL
  {
    id: "submit-fnol",
    path: "/v1/claims/fnol",
    method: "POST",
    tag: "Claims & FNOL",
    summary: "File First Notice of Loss (FNOL)",
    description: `Logs a new loss incident against an active policy, validating questions against the claims contract configuration, and inserting it into the claims review pipeline.
      <br/><br/>
      <strong>FNOL Validation</strong>: Upon submission, the engine matches incident details against active policy coverages and validity periods. If the date of loss does not align with active coverage bounds, the API returns a <code>400 invalid_incident_date</code>.
      <br/><br/>
      <strong>Accounting Reserves</strong>: Registers initial double-entry loss reserves (Outstanding Loss and Outstanding Expense accounts) on the claims ledger, adjusting active carrier exposure metrics automatically.`,
    hasDynamicSchema: true,
    parameters: [],
    requestBody: {
      description: "Structured incident details and driver statements.",
      fields: {
        policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
        intake: {
          incident: {
            type: "collision",
            date: "2026-05-15",
            description: "Rear-ended at a traffic light by a third party."
          },
          driver: { kind: "named", id: "driver-jane-doe" }
        }
      }
    },
    response: {
      status: 201,
      description: "Claim file created successfully.",
      body: {
        success: true,
        data: {
          claimId: "clm_c8c3d400-55c1-5f1b-c926-2e7890ddcb03",
          claimNumber: "CLM-2026-00102",
          status: "FNOL_RECEIVED"
        }
      }
    },
    errorCodes: [
      { status: 400, code: "claim_policy_inactive", description: "The target policy is cancelled, lapsed, or not yet active on the loss date." },
      { status: 400, code: "invalid_incident_date", description: "The loss incident date must be within the policy's effective coverage boundaries." },
      { status: 400, code: "outstanding_reserve_exceeded", description: "The initial reserve requested exceeds delegated claims handler authority cap." },
      { status: 400, code: "duplicate_claim_detected", description: "A claim with the same incident date and description has already been registered." }
    ],
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "CLAIMS",
      "x-facio-product-agnostic": true,
      "x-facio-related-webhooks": ["claim.fnol_received"],
      "x-facio-next-actions": [
        { label: "Adjust Claims Reserves", method: "POST", path: "/v1/claims/{claimId}/reserve-adjustments" }
      ]
    }
  },
  {
    id: "get-claim",
    path: "/v1/claims/{claimId}",
    method: "GET",
    tag: "Claims & FNOL",
    summary: "Poll Claim Status",
    description: "Returns the current state of a claim.",
    parameters: [
      { name: "claimId", in: "path", type: "string (UUID)", required: true, description: "Unique claim ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: { claimId: "c8c3d400-55c1-5f1b-cg26-2e7890ddcb03", status: "TRIAGE" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "CLAIMS"
    }
  },
  {
    id: "list-claims",
    path: "/v1/claims",
    method: "GET",
    tag: "Claims & FNOL",
    summary: "List and Search Claims",
    description: "Queries a paginated list of logged claims. Filter by status, policyId, or claimNumber.",
    parameters: [
      { name: "limit", in: "query", type: "integer", required: false, description: "Maximum number of records to return (1-100, default 20)" },
      { name: "after", in: "query", type: "string", required: false, description: "Cursor token for fetching the next page" },
      { name: "before", in: "query", type: "string", required: false, description: "Cursor token for fetching the previous page" },
      { name: "status", in: "query", type: "string", required: false, description: "Filter by status (OPEN, CLOSED, UNDER_INVESTIGATION, SETTLED)" },
      { name: "policyId", in: "query", type: "string (UUID)", required: false, description: "Filter by policy ID" },
      { name: "claimNumber", in: "query", type: "string", required: false, description: "Filter by claim number" }
    ],
    requestBody: null,
    response: {
      status: 200,
      description: "Paginated list of claim records.",
      body: {
        success: true,
        data: [
          {
            claimId: "clm_c8c3d400-55c1-5f1b-c926-2e7890ddcb03",
            claimNumber: "CLM-PRIVATEMOTOR-00102",
            policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
            status: "OPEN",
            lossDate: "2026-05-15T00:00:00Z",
            reportedDate: "2026-05-16T00:00:00Z"
          }
        ],
        meta: {
          hasMore: false,
          nextCursor: null,
          prevCursor: null,
          totalCount: 1
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "CLAIMS",
      "x-facio-product-agnostic": true
    }
  },
  {
    id: "get-claim-contract",
    path: "/v1/claims/{claimId}/contract",
    method: "GET",
    tag: "Claims & FNOL",
    summary: "Get claim contract",
    description: "Returns the dynamic claims contract configuration governing allowable limits.",
    parameters: [
      { name: "claimId", in: "path", type: "string (UUID)", required: true, description: "Unique claim ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: { deductibleAmount: 250.00 } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "CLAIMS"
    }
  },
  {
    id: "get-claim-timeline",
    path: "/v1/claims/{claimId}/timeline",
    method: "GET",
    tag: "Claims & FNOL",
    summary: "Retrieve Claim History",
    description: "Returns the chronological list of claim developments.",
    parameters: [
      { name: "claimId", in: "path", type: "string (UUID)", required: true, description: "Unique claim ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: [{ event: "FNOL_RECEIVED", timestamp: "2026-05-16T09:00:00Z" }] }
    },
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "CLAIMS"
    }
  },
  {
    id: "add-claim-documents",
    path: "/v1/claims/{claimId}/documents",
    method: "POST",
    tag: "Claims & FNOL",
    summary: "Attach Claim Evidence",
    description: "Uploads invoices, photos, or reports as claim evidence.",
    parameters: [
      { name: "claimId", in: "path", type: "string (UUID)", required: true, description: "Unique claim ID" }
    ],
    requestBody: {
      description: "Multipart file upload with document type metadata",
      fields: {
        file: "[binary file raw data]",
        documentType: "CLAIM_EVIDENCE_PHOTO",
        description: "Front bumper damage photo"
      }
    },
    response: {
      status: 200,
      body: { success: true, message: "Document attached." }
    },
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "CLAIMS"
    }
  },
  {
    id: "adjust-claim-reserve",
    path: "/v1/claims/{claimId}/reserve-adjustments",
    method: "POST",
    tag: "Claims & FNOL",
    summary: "Adjust Claims Reserves",
    description: "Alters the outstanding Loss or Expense reserve balances by posting a ClaimReserveTransaction.",
    parameters: [
      { name: "claimId", in: "path", type: "string (UUID)", required: true, description: "Unique claim ID" }
    ],
    requestBody: {
      description: "Adjusted reserves transaction details",
      fields: {
        type: "CASE_RESERVE",
        amount: 3500.00,
        currency: "EUR",
        notes: "Bumper replacement required",
        externalRef: "ext_adj_0029"
      }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: {
          id: "tx_res_94821a00",
          claimId: "clm_c8c3d400-55c1-5f1b-c926-2e7890ddcb03",
          type: "CASE_RESERVE",
          amount: 3500.00,
          currency: "EUR",
          occurredAt: "2026-06-09T01:36:54Z"
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "FINANCIAL",
      "x-facio-related-webhooks": ["claim.reserve_changed"],
      "x-facio-next-actions": [
        { label: "Record Claims Payout", method: "POST", path: "/v1/claims/{claimId}/payments" }
      ],
      "x-facio-ledger-impact": true,
      "x-facio-bdx-impact": true
    }
  },
  {
    id: "create-claim-payment",
    path: "/v1/claims/{claimId}/payments",
    method: "POST",
    tag: "Claims & FNOL",
    summary: "Record Claims Payout",
    description: "Executes a cash payment out of the loss reserve.",
    parameters: [
      { name: "claimId", in: "path", type: "string (UUID)", required: true, description: "Unique claim ID" }
    ],
    requestBody: {
      description: "Payment details",
      fields: { amount: 3250.00, payeeName: "Paphos Crash Repairs Ltd", isFinal: true }
    },
    response: {
      status: 201,
      body: {
        success: true,
        data: { paymentId: "clm_pmt_948271a0", amount: 3250.00, status: "PAID" }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "FINANCIAL",
      "x-facio-related-webhooks": ["claim.payment_created"],
      "x-facio-next-actions": [
        { label: "Close Claim file", method: "POST", path: "/v1/claims/{claimId}/close" }
      ],
      "x-facio-ledger-impact": true,
      "x-facio-bdx-impact": true
    }
  },
  {
    id: "create-claim-recovery",
    path: "/v1/claims/{claimId}/recoveries",
    method: "POST",
    tag: "Claims & FNOL",
    summary: "Record Claim Recovery",
    description: "Logs subrogation recoveries or salvage returned to the ledger.",
    parameters: [
      { name: "claimId", in: "path", type: "string (UUID)", required: true, description: "Unique claim ID" }
    ],
    requestBody: {
      description: "Details of recovery payment (subrogation or salvage)",
      fields: {
        amount: 1500.00,
        currency: "EUR",
        payee: "Cyprus General Insurance Ltd",
        recoveryType: "SUBROGATION",
        notes: "Third party insurer settled subrogation claim."
      }
    },
    response: {
      status: 201,
      body: { success: true, data: { recoveryId: "clm_rec_847192", amount: 1500.00 } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "FINANCIAL",
      "x-facio-ledger-impact": true,
      "x-facio-bdx-impact": true
    }
  },
  {
    id: "close-claim",
    path: "/v1/claims/{claimId}/close",
    method: "POST",
    tag: "Claims & FNOL",
    summary: "Close Claim File",
    description: "Sets status to CLOSED, reducing remaining reserves back to zero.",
    parameters: [
      { name: "claimId", in: "path", type: "string (UUID)", required: true, description: "Unique claim ID" }
    ],
    requestBody: {
      description: "Claim closure classification and finalization data",
      fields: {
        closeReason: "SETTLED_IN_FULL",
        notes: "Repairs completed and repair invoices paid in full. Remaining outstanding reserves reduced to zero."
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { claimId: "clm_c8c3d400-55c1-5f1b-c926-2e7890ddcb03", status: "CLOSED" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "CLAIMS",
      "x-facio-related-webhooks": ["claim.closed"],
      "x-facio-ledger-impact": true,
      "x-facio-bdx-impact": true
    }
  },
  {
    id: "reopen-claim",
    path: "/v1/claims/{claimId}/reopen",
    method: "POST",
    tag: "Claims & FNOL",
    summary: "Reopen Closed Claim",
    description: "Restores a claim back to active status.",
    parameters: [
      { name: "claimId", in: "path", type: "string (UUID)", required: true, description: "Unique claim ID" }
    ],
    requestBody: {
      description: "Reason and details for reopening the claim",
      fields: {
        reason: "NEW_DAMAGE_REPORTED",
        notes: "Insured reported latent electrical faults related to the collision."
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { claimId: "clm_c8c3d400-55c1-5f1b-c926-2e7890ddcb03", status: "REOPENED" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "CLAIMS"
    }
  },

  // H. BINDER & REINSURANCE COMPLIANCE
  {
    id: "validate-risk-binder",
    path: "/v1/binders/validate-risk",
    method: "POST",
    tag: "Binder & Reinsurance Compliance",
    summary: "Validate Risk Under Binder",
    description: "Evaluates risk factors (e.g. sum insured, driver age, vehicle value) against binder authority bounds, exclusions, and territorial scope rules before quoting.",
    parameters: [],
    requestBody: {
      description: "Risk evaluation metrics",
      fields: { programId: "e9f8a000-84b2-4d1e-bd15-0d6789b52000", insuredValue: 120000, countryCode: "CY" }
    },
    response: {
      status: 200,
      body: { success: true, data: { decision: "ACCEPT", binderId: "bin_b6081_abbeygate" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "REINSURANCE",
      "x-facio-risk-controls": ["binder_authority"]
    }
  },
  {
    id: "get-binder",
    path: "/v1/binders/{binderId}",
    method: "GET",
    tag: "Binder & Reinsurance Compliance",
    summary: "Retrieve Binder Profile",
    description: "Returns delegated underwriting limits, coverholder PIN, UMR, and reporting contacts details.",
    parameters: [
      { name: "binderId", in: "path", type: "string", required: true, description: "Unique binder ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: {
          binderId: "bin_b6081_abbeygate",
          coverholderName: "Abbeygate Coverholders Ltd",
          coverholderPin: "123456XTD",
          umr: "B6081ABBEYGATE0125",
          agreementNumber: "ABBEYGATE0125",
          lloydsReportingVer: "V5.2",
          defaultCurrency: "USD",
          settlementCurrency: "USD",
          startDate: "2026-01-01T00:00:00Z",
          endDate: "2026-12-31T00:00:00Z",
          status: "ACTIVE"
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "REINSURANCE"
    }
  },
  {
    id: "get-binder-capacity",
    path: "/v1/binders/{binderId}/capacity",
    method: "GET",
    tag: "Binder & Reinsurance Compliance",
    summary: "Retrieve Capacity Utilization",
    description: "Returns binder aggregate premium limits, remaining annual capacity, and exposure balances.",
    parameters: [
      { name: "binderId", in: "path", type: "string", required: true, description: "Unique binder ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: {
          binderId: "bin_b6081_abbeygate",
          grossPremiumLimit: 12000000.00,
          allocatedPremium: 4328190.50,
          remainingCapacity: 7671809.50,
          currency: "USD"
         }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "REINSURANCE"
    }
  },
  {
    id: "allocate-binder",
    path: "/v1/binders/{binderId}/allocate",
    method: "POST",
    tag: "Binder & Reinsurance Compliance",
    summary: "Allocate Binder Capacity",
    description: "Commit and lock a policy's premium exposure allocation under the binder limit.",
    parameters: [
      { name: "binderId", in: "path", type: "string", required: true, description: "Unique binder ID" }
    ],
    requestBody: {
      description: "Allocation parameters",
      fields: {
        policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
        allocationPercentage: 100.00
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { allocationId: "alloc_bin_8471201", status: "ALLOCATED" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "BIND",
      "x-facio-domain": "REINSURANCE",
      "x-facio-ledger-impact": true
    }
  },
  {
    id: "facultative-referral",
    path: "/v1/binders/{binderId}/facultative-referral",
    method: "POST",
    tag: "Binder & Reinsurance Compliance",
    summary: "Submit Binder Facultative Referral",
    description: "Escalates risks that exceed binder delegated authority limits (e.g. driver too young) to carrier underwriters for individual review.",
    parameters: [
      { name: "binderId", in: "path", type: "string", required: true, description: "Unique binder ID" }
    ],
    requestBody: {
      description: "Referral reasons and parameters",
      fields: {
        policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
        referralReason: "DRIVER_AGE_BELOW_THRESHOLD",
        notes: "Requesting exception due to spotless 5-year driving history."
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { referralId: "fac_ref_94821a", status: "PENDING_REVIEW" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "REINSURANCE"
    }
  },
  {
    id: "get-policy-binder-allocation",
    path: "/v1/policies/{policyId}/binder-allocation",
    method: "GET",
    tag: "Binder & Reinsurance Compliance",
    summary: "Retrieve Policy Binder Allocation",
    description: "Lists active binder and carrier allocations linked to the policy risk.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: [{ binderId: "bin_b6081_abbeygate", allocationPercentage: 100.00, UMR: "B6081ABBEYGATE0125" }] }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "REINSURANCE"
    }
  },
  {
    id: "get-claim-binder-recovery",
    path: "/v1/claims/{claimId}/binder-recovery",
    method: "GET",
    tag: "Binder & Reinsurance Compliance",
    summary: "Retrieve Claim Reinsurance Recovery",
    description: "Returns carrier reinsurance recovery calculation mapped against quota shares and deductibles.",
    parameters: [
      { name: "claimId", in: "path", type: "string (UUID)", required: true, description: "Unique claim ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: [{ binderId: "bin_b6081_abbeygate", recoverableAmountEur: 1950.00, deductionApplied: 500.00 }] }
    },
    extensions: {
      "x-facio-lifecycle-stage": "CLAIM",
      "x-facio-domain": "REINSURANCE"
    }
  },
  {
    id: "list-binders",
    path: "/v1/binders",
    method: "GET",
    tag: "Binder & Reinsurance Compliance",
    summary: "List Binders",
    description: "Retrieves a list of coverholder binders configured for the tenant account, showing status, UMR, agreement number, and reporting version details.",
    parameters: [
      { name: "limit", in: "query", type: "integer", required: false, description: "Maximum number of records to return (1-100, default 20)" },
      { name: "after", in: "query", type: "string", required: false, description: "Cursor token for fetching the next page" },
      { name: "before", in: "query", type: "string", required: false, description: "Cursor token for fetching the previous page" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [
          {
            id: "bin_b6081_abbeygate",
            coverholderName: "Abbeygate Coverholders Ltd",
            coverholderPin: "123456XTD",
            umr: "B6081ABBEYGATE0125",
            agreementNumber: "ABBEYGATE0125",
            lloydsReportingVer: "V5.2",
            status: "ACTIVE"
          }
        ],
        meta: {
          hasMore: false,
          nextCursor: null,
          prevCursor: null,
          totalCount: 1
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "REINSURANCE"
    }
  },
  {
    id: "get-binder-financials",
    path: "/v1/binders/{binderId}/financials",
    method: "GET",
    tag: "Binder & Reinsurance Compliance",
    summary: "Retrieve Binder Financials",
    description: "Returns the financial parameters of the binder agreement, including coverholder commission rates, gross premium limits, profit commissions structure, and settlement account details.",
    parameters: [
      { name: "binderId", in: "path", type: "string", required: true, description: "Unique binder ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: {
          binderId: "bin_b6081_abbeygate",
          grossPremiumLimit: 12000000.00,
          maxLine: 500000.00,
          notifiablePercent: 80,
          commissionRate: 15.50,
          profitCommission: {
            rate: 10.00,
            calculationMethod: "NET_WRITTEN_PREMIUM"
          }
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "REINSURANCE"
    }
  },
  {
    id: "list-entities",
    path: "/v1/entities",
    method: "GET",
    tag: "Insured & PolicyHolder Management",
    summary: "List Insured Entities",
    description: "Queries normalized insured risk subjects (persons, organizations, vehicles, or physical assets) registered in the platform.",
    parameters: [
      { name: "limit", in: "query", type: "integer", required: false, description: "Maximum number of records to return (1-100, default 20)" },
      { name: "after", in: "query", type: "string", required: false, description: "Cursor token for fetching the next page" },
      { name: "before", in: "query", type: "string", required: false, description: "Cursor token for fetching the previous page" },
      { name: "type", in: "query", type: "string", required: false, description: "Filter by entity type (PERSON, ORGANIZATION, VEHICLE, ASSET)" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [
          {
            entityId: "ent_9f3a8271-84b2-4d1e-bd15-0d6789b52000",
            type: "VEHICLE",
            name: "Audi A4 2024",
            data: {
              registrationNumber: "LL24MGA",
              vehicleValue: 35000,
              engineSizeCc: 1998
            }
          }
        ],
        meta: {
          hasMore: false,
          nextCursor: null,
          prevCursor: null,
          totalCount: 1
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "UNDERWRITING"
    }
  },
  {
    id: "create-entity",
    path: "/v1/entities",
    method: "POST",
    tag: "Insured & PolicyHolder Management",
    summary: "Create Insured Entity",
    description: "Registers a new normalized insured subject under the tenant account.",
    parameters: [],
    requestBody: {
      description: "Insured entity creation details",
      fields: {
        type: "VEHICLE",
        name: "Audi A4 2024",
        data: {
          registrationNumber: "LL24MGA",
          vehicleValue: 35000,
          engineSizeCc: 1998
        }
      }
    },
    response: {
      status: 201,
      body: {
        success: true,
        data: {
          entityId: "ent_9f3a8271-84b2-4d1e-bd15-0d6789b52000",
          type: "VEHICLE",
          name: "Audi A4 2024"
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "UNDERWRITING"
    }
  },
  {
    id: "list-policyholders",
    path: "/v1/policyholders",
    method: "GET",
    tag: "Insured & PolicyHolder Management",
    summary: "List Policyholders",
    description: "Queries all policyholder contracting party profiles (individual/corporate) responsible for premium billing.",
    parameters: [
      { name: "limit", in: "query", type: "integer", required: false, description: "Maximum number of records to return (1-100, default 20)" },
      { name: "after", in: "query", type: "string", required: false, description: "Cursor token for fetching the next page" },
      { name: "before", in: "query", type: "string", required: false, description: "Cursor token for fetching the previous page" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [
          {
            policyHolderId: "ph_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
            name: "John Doe",
            type: "INDIVIDUAL",
            email: "john.doe@example.com",
            phone: "+35799123456",
            physicalAddress: "15 Spyrou Kyprianou Ave, Limassol, Cyprus"
          }
        ],
        meta: {
          hasMore: false,
          nextCursor: null,
          prevCursor: null,
          totalCount: 1
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "CUSTOMER"
    }
  },
  {
    id: "create-policyholder",
    path: "/v1/policyholders",
    method: "POST",
    tag: "Insured & PolicyHolder Management",
    summary: "Create Policyholder Profile",
    description: "Creates a contracting/billing client profile.",
    parameters: [],
    requestBody: {
      description: "Policyholder details",
      fields: {
        name: "John Doe",
        type: "INDIVIDUAL",
        email: "john.doe@example.com",
        phone: "+35799123456",
        physicalAddress: "15 Spyrou Kyprianou Ave, Limassol, Cyprus"
      }
    },
    response: {
      status: 201,
      body: {
        success: true,
        data: {
          policyHolderId: "ph_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
          name: "John Doe"
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "CUSTOMER"
    }
  },

  // I. LEDGER, BILLING & PREMIUM ACCOUNTING
  {
    id: "get-policy-ledger",
    path: "/v1/policies/{policyId}/ledger",
    method: "GET",
    tag: "Ledger, Billing & Premium Accounting",
    summary: "Retrieve Policy Ledgers",
    description: "Returns the detailed transactional ledger entries recorded against a policy contract.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [{ transactionId: "tx_001", type: "ORIGINAL_PREMIUM", amount: 488.50 }]
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "FINANCIAL",
      "x-facio-ledger-impact": true
    }
  },
  {
    id: "get-policy-invoices",
    path: "/v1/policies/{policyId}/invoices",
    method: "GET",
    tag: "Ledger, Billing & Premium Accounting",
    summary: "Retrieve Invoices",
    description: "Lists premium payment invoices generated under this policy.",
    parameters: [
      { name: "policyId", in: "path", type: "string (UUID)", required: true, description: "Unique policy ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: [{ invoiceId: "inv_2026_948271", amount: 488.50, status: "PAID" }] }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "FINANCIAL"
    }
  },
  {
    id: "invoice-payment-intent",
    path: "/v1/invoices/{invoiceId}/payment-intent",
    method: "POST",
    tag: "Ledger, Billing & Premium Accounting",
    summary: "Create Invoice Payment Intent",
    description: "Generates CardCorp checkout workflows for a premium invoice outstanding balance.",
    parameters: [
      { name: "invoiceId", in: "path", type: "string", required: true, description: "Unique invoice ID" }
    ],
    requestBody: {
      description: "Payment intent initialization parameters.",
      fields: {
        paymentMethodType: "CREDIT_CARD",
        provider: "CARDCORP",
        successUrl: "https://your-mga-portal.com/success",
        cancelUrl: "https://your-mga-portal.com/cancel"
      }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: {
          checkoutId: "cc_chk_invoice_8472",
          merchantTransactionId: "cc_tx_invoice_0492",
          checkoutUrl: "https://checkout.cardcorp.com/pay/pay_facio_test_invoice_8472"
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "FINANCIAL"
    }
  },
  {
    id: "mark-invoice-paid",
    path: "/v1/invoices/{invoiceId}/mark-paid",
    method: "POST",
    tag: "Ledger, Billing & Premium Accounting",
    summary: "Mark Invoice Paid",
    description: "Flags an invoice as paid.",
    parameters: [
      { name: "invoiceId", in: "path", type: "string", required: true, description: "Unique invoice ID" }
    ],
    requestBody: {
      description: "Reconciliation parameters including payment reference and timestamp",
      fields: {
        paymentReference: "WIRE_TX_8492019",
        amountPaid: 488.50,
        paidAt: "2026-06-09T01:00:00Z"
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { invoiceId: "inv_2026_948271", status: "PAID" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "FINANCIAL",
      "x-facio-ledger-impact": true
    }
  },
  {
    id: "refund-invoice",
    path: "/v1/invoices/{invoiceId}/refund",
    method: "POST",
    tag: "Ledger, Billing & Premium Accounting",
    summary: "Refund Paid Premium",
    description: "Executes payment refund commands on paid invoices.",
    parameters: [
      { name: "invoiceId", in: "path", type: "string", required: true, description: "Unique invoice ID" }
    ],
    requestBody: {
      description: "Refund amount and allocation parameters",
      fields: {
        refundAmount: 488.50,
        reason: "POLICY_CANCELLED",
        refundMethod: "CREDIT_CARD"
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { invoiceId: "inv_2026_948271", status: "REFUNDED" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "FINANCIAL",
      "x-facio-ledger-impact": true
    }
  },
  {
    id: "list-risk-transactions",
    path: "/v1/risk-transactions",
    method: "GET",
    tag: "Ledger, Billing & Premium Accounting",
    summary: "Retrieve Risk Transactions",
    description: "Queries all double-entry ledger risk transactions (inception, endorsements, cancellations) posted within the organizational scope, complete with premium breakdowns and tax lines.",
    parameters: [
      { name: "limit", in: "query", type: "integer", required: false, description: "Maximum number of records to return (1-100, default 20)" },
      { name: "after", in: "query", type: "string", required: false, description: "Cursor token for fetching the next page" },
      { name: "before", in: "query", type: "string", required: false, description: "Cursor token for fetching the previous page" },
      { name: "policyId", in: "query", type: "string (UUID)", required: false, description: "Filter transactions by policy ID" },
      { name: "transactionType", in: "query", type: "string (Enum)", required: false, description: "Filter by transaction type (INCEPTION, ENDORSEMENT, CANCELLATION, REINSTATEMENT)" },
      { name: "startDate", in: "query", type: "string (ISO 8601)", required: false, description: "Filter transactions starting from this date" },
      { name: "endDate", in: "query", type: "string (ISO 8601)", required: false, description: "Filter transactions up to this date" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [
          {
            transactionId: "tx_001_inception",
            policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
            transactionNumber: 1,
            transactionType: "INCEPTION",
            status: "BOUND",
            effectiveDate: "2026-03-01T00:00:00Z",
            paymentMode: "CUSTOMER_CARD",
            premiumTransactions: [
              {
                id: "prem_tx_001",
                transactionType: "ORIGINAL",
                currency: "EUR",
                grossPremium: 488.50,
                commissionPercent: 15.00,
                commissionAmount: 60.00,
                taxesTotal: 42.00,
                feesTotal: 0.00,
                netToLondon: 386.50,
                taxLines: [
                  { lineNo: 1, jurisdiction: "CY", taxType: "IPT", taxableAmount: 400.00, taxAmount: 40.00, rate: 10.00 },
                  { lineNo: 2, jurisdiction: "CY", taxType: "STAMP_DUTY", taxableAmount: 400.00, taxAmount: 2.00, rate: 0.50 }
                ]
              }
            ]
          }
        ],
        meta: {
          hasMore: false,
          nextCursor: null,
          prevCursor: null,
          totalCount: 1
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "FINANCIAL",
      "x-facio-ledger-impact": true
    }
  },
  {
    id: "get-risk-transaction",
    path: "/v1/risk-transactions/{transactionId}",
    method: "GET",
    tag: "Ledger, Billing & Premium Accounting",
    summary: "Retrieve Risk Transaction Profile",
    description: "Returns detailed ledger entry lines and tax configurations matching a specific risk transaction key.",
    parameters: [
      { name: "transactionId", in: "path", type: "string", required: true, description: "Unique risk transaction ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: {
          transactionId: "tx_001_inception",
          policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
          transactionNumber: 1,
          transactionType: "INCEPTION",
          status: "BOUND",
          effectiveDate: "2026-03-01T00:00:00Z",
          paymentMode: "CUSTOMER_CARD",
          premiumTransactions: [
            {
              id: "prem_tx_001",
              transactionType: "ORIGINAL",
              currency: "EUR",
              grossPremium: 488.50,
              commissionPercent: 15.00,
              commissionAmount: 60.00,
              taxesTotal: 42.00,
              feesTotal: 0.00,
              netToLondon: 386.50,
              taxLines: [
                { lineNo: 1, jurisdiction: "CY", taxType: "IPT", taxableAmount: 400.00, taxAmount: 40.00, rate: 10.00 },
                { lineNo: 2, jurisdiction: "CY", taxType: "STAMP_DUTY", taxableAmount: 400.00, taxAmount: 2.00, rate: 0.50 }
              ]
            }
          ]
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "FINANCIAL"
    }
  },
  {
    id: "get-account-balance",
    path: "/v1/accounts/{accountId}/balance",
    method: "GET",
    tag: "Ledger, Billing & Premium Accounting",
    summary: "Retrieve Account Balances",
    description: "Fetches balance values (Available, blockages, unpaid invoices totals) linked to user tiers.",
    parameters: [
      { name: "accountId", in: "path", type: "string", required: true, description: "Unique account ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: { accountId: "acc_948201", availableBalance: 125400.00 } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "FINANCIAL"
    }
  },

  // J. BDX / BORDEREAUX REPORTING
  {
    id: "get-bdx-risk",
    path: "/v1/bdx/risk",
    method: "GET",
    tag: "BDX / Bordereaux Reporting",
    summary: "Retrieve Risk Bordereau",
    description: "Returns monthly policy risk declarations formatted to Lloyd's V5.2 compliance.",
    parameters: [
      { name: "month", in: "query", type: "string", required: true, description: "Target reporting month (YYYY-MM)" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: [{ policyNumber: "POL-2026-84729", umr: "B09482619A01" }] }
    },
    extensions: {
      "x-facio-lifecycle-stage": "REPORT",
      "x-facio-domain": "BORDEREAU",
      "x-facio-bdx-impact": true
    }
  },
  {
    id: "get-bdx-premium",
    path: "/v1/bdx/premium",
    method: "GET",
    tag: "BDX / Bordereaux Reporting",
    summary: "Retrieve Premium Bordereau",
    description: "Lists cash written premium, commissions, and taxes items.",
    parameters: [
      { name: "month", in: "query", type: "string", required: true, description: "Reporting month (YYYY-MM)" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: [{ policyNumber: "POL-2026-84729", grossPremium: 488.50, netToLondon: 388.50 }] }
    },
    extensions: {
      "x-facio-lifecycle-stage": "REPORT",
      "x-facio-domain": "BORDEREAU",
      "x-facio-bdx-impact": true
    }
  },
  {
    id: "get-bdx-claims",
    path: "/v1/bdx/claims",
    method: "GET",
    tag: "BDX / Bordereaux Reporting",
    summary: "Retrieve Claims Bordereau",
    description: "Compiles outstanding reserves and loss settlements calculations.",
    parameters: [
      { name: "month", in: "query", type: "string", required: true, description: "Reporting month (YYYY-MM)" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: [{ claimNumber: "CLM-2026-00102", outstandingReserve: 250.00 }] }
    },
    extensions: {
      "x-facio-lifecycle-stage": "REPORT",
      "x-facio-domain": "BORDEREAU",
      "x-facio-bdx-impact": true
    }
  },
  {
    id: "create-bdx-export",
    path: "/v1/bdx/exports",
    method: "POST",
    tag: "BDX / Bordereaux Reporting",
    summary: "Generate Bordereau Export",
    description: "Triggers worker compiles to generate downloadable XLSX files.",
    parameters: [],
    requestBody: {
      description: "Export period",
      fields: { month: "2026-03", exportType: "FULL_MGA_PACK" }
    },
    response: {
      status: 201,
      body: { success: true, data: { exportId: "bdx_exp_2026_03", status: "COMPLETED" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "REPORT",
      "x-facio-domain": "BORDEREAU",
      "x-facio-related-webhooks": ["bdx.export_ready"]
    }
  },
  {
    id: "get-bdx-export",
    path: "/v1/bdx/exports/{exportId}",
    method: "GET",
    tag: "BDX / Bordereaux Reporting",
    summary: "Retrieve Export Status",
    description: "Returns compile state and download options.",
    parameters: [
      { name: "exportId", in: "path", type: "string", required: true, description: "Unique export ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: { exportId: "bdx_exp_2026_03", status: "COMPLETED", downloadUrl: "https://vault.facio.io/exports/BDX_2026_03.xlsx" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "REPORT",
      "x-facio-domain": "BORDEREAU"
    }
  },
  {
    id: "download-bdx-export",
    path: "/v1/bdx/exports/{exportId}/download",
    method: "GET",
    tag: "BDX / Bordereaux Reporting",
    summary: "Download Export Bundle",
    description: "Directly downloads the compiled XLSX bordereau pack.",
    parameters: [
      { name: "exportId", in: "path", type: "string", required: true, description: "Unique export ID" }
    ],
    requestBody: null,
    response: {
      status: 302,
      description: "Redirect to storage vault URL."
    },
    extensions: {
      "x-facio-lifecycle-stage": "REPORT",
      "x-facio-domain": "BORDEREAU"
    }
  },
  {
    id: "validate-bdx-export",
    path: "/v1/bdx/exports/{exportId}/validate",
    method: "POST",
    tag: "BDX / Bordereaux Reporting",
    summary: "Validate BDX Data",
    description: "Runs semantic validations checks to ensure compliance.",
    parameters: [
      { name: "exportId", in: "path", type: "string", required: true, description: "Unique export ID" }
    ],
    requestBody: {
      description: "Validation rules overrides and options",
      fields: {
        strictMode: true,
        overrideWarnings: ["WARNING_ZERO_PREMIUM"]
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { valid: true } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "REPORT",
      "x-facio-domain": "BORDEREAU"
    }
  },
  {
    id: "get-bdx-submissions",
    path: "/v1/bdx/submissions",
    method: "GET",
    tag: "BDX / Bordereaux Reporting",
    summary: "Retrieve Carrier Submissions",
    description: "Returns submission logs of bordereau bundles.",
    parameters: [],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: [{ submissionId: "sub_2026_01", carrier: "Lloyd's", status: "ACKNOWLEDGED" }] }
    },
    extensions: {
      "x-facio-lifecycle-stage": "REPORT",
      "x-facio-domain": "BORDEREAU"
    }
  },
  {
    id: "create-bdx-submission",
    path: "/v1/bdx/submissions",
    method: "POST",
    tag: "BDX / Bordereaux Reporting",
    summary: "Transmit Bordereau to Carrier",
    description: "Submits verified monthly files to the capacity provider API gateway.",
    parameters: [],
    requestBody: {
      description: "Submission details for carrier",
      fields: {
        exportId: "bdx_exp_2026_03",
        carrierEndpoint: "https://api.lloyds.com/v1/coverholder-submissions",
        reportingPeriodId: "rp_2026_03"
      }
    },
    response: {
      status: 201,
      body: { success: true, data: { submissionId: "sub_2026_02", status: "TRANSMITTED" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "REPORT",
      "x-facio-domain": "BORDEREAU"
    }
  },
  {
    id: "list-bdx-periods",
    path: "/v1/bdx/periods",
    method: "GET",
    tag: "BDX / Bordereaux Reporting",
    summary: "List Reporting Periods",
    description: "Queries historical and current BDX compliance reporting periods, showing status (OPEN, LOCKED, SUBMITTED) and date boundaries.",
    parameters: [
      { name: "binderId", in: "query", type: "string", required: false, description: "Filter by binder ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [
          {
            id: "rp_2026_05",
            binderId: "bin_b6081_abbeygate",
            year: 2026,
            month: 5,
            startDate: "2026-05-01T00:00:00Z",
            endDate: "2026-05-31T23:59:59Z",
            status: "LOCKED"
          },
          {
            id: "rp_2026_06",
            binderId: "bin_b6081_abbeygate",
            year: 2026,
            month: 6,
            startDate: "2026-06-01T00:00:00Z",
            endDate: "2026-06-30T23:59:59Z",
            status: "OPEN"
          }
        ]
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "REPORT",
      "x-facio-domain": "BORDEREAU"
    }
  },
  {
    id: "lock-bdx-period",
    path: "/v1/bdx/periods/{periodId}/lock",
    method: "POST",
    tag: "BDX / Bordereaux Reporting",
    summary: "Lock Reporting Period",
    description: "Locks a reporting period for BDX generation, freezing all transactions. Prevents mid-term adjustment edits or retrospective policy additions in the locked month.",
    parameters: [
      { name: "periodId", in: "path", type: "string", required: true, description: "Unique reporting period ID (e.g. rp_2026_05)" }
    ],
    requestBody: {
      description: "Period locking parameters",
      fields: {
        lockNotes: "Monthly reconciliation completed and verified.",
        authorizedByUser: "usr_9f3a8271-84b2-4d1e-bd15"
      }
    },
    response: {
      status: 200,
      body: {
        success: true,
        data: {
          id: "rp_2026_05",
          status: "LOCKED",
          lockedAt: "2026-06-09T01:36:54Z"
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "REPORT",
      "x-facio-domain": "BORDEREAU"
    }
  },

  // K. WEBHOOKS
  {
    id: "register-webhook",
    path: "/v1/webhooks/endpoints",
    method: "POST",
    tag: "Webhooks",
    summary: "Register Sync Endpoint",
    description: "Registers a secure HTTPS listener to receive asynchronous push notifications from our engine.",
    parameters: [],
    requestBody: {
      description: "Webhook details",
      fields: { url: "https://api.partner.com/v1/facio-webhooks", description: "Primary Sync" }
    },
    response: {
      status: 201,
      body: {
        success: true,
        data: { id: "wh_920184c2-9f37-4b15-bd15-0d6789b52000", url: "https://api.partner.com/v1/facio-webhooks", secret: "whsec_2f8d2lSkd92kSlSms91mSkd82kSk..." }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "INTEGRATION"
    }
  },
  {
    id: "list-webhooks",
    path: "/v1/webhooks/endpoints",
    method: "GET",
    tag: "Webhooks",
    summary: "List Registered Listeners",
    description: "Retrieves all active operations synchronization endpoints.",
    parameters: [
      { name: "limit", in: "query", type: "integer", required: false, description: "Maximum number of records to return (1-100, default 20)" },
      { name: "after", in: "query", type: "string", required: false, description: "Cursor token for fetching the next page" },
      { name: "before", in: "query", type: "string", required: false, description: "Cursor token for fetching the previous page" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [{ id: "wh_920184c2-9f37-4b15-bd15-0d6789b52000", url: "https://api.partner.com/v1/facio-webhooks" }],
        meta: {
          hasMore: false,
          nextCursor: null,
          prevCursor: null,
          totalCount: 1
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "INTEGRATION"
    }
  },
  {
    id: "get-webhook",
    path: "/v1/webhooks/endpoints/{endpointId}",
    method: "GET",
    tag: "Webhooks",
    summary: "Retrieve Webhook Details",
    description: "Returns configuration values of a registered webhook endpoint.",
    parameters: [
      { name: "endpointId", in: "path", type: "string", required: true, description: "Unique webhook ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: { id: "wh_920184c2-9f37-4b15-bd15-0d6789b52000", url: "https://api.partner.com/v1/facio-webhooks" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "INTEGRATION"
    }
  },
  {
    id: "patch-webhook",
    path: "/v1/webhooks/endpoints/{endpointId}",
    method: "PATCH",
    tag: "Webhooks",
    summary: "Update Webhook Endpoint",
    description: "Amends the listener destination URL or toggles activity state.",
    parameters: [
      { name: "endpointId", in: "path", type: "string", required: true, description: "Unique webhook ID" }
    ],
    requestBody: {
      description: "Webhook update parameters",
      fields: {
        url: "https://api.partner.com/v1/facio-webhooks-updated",
        isActive: true,
        description: "Primary backup synchronization receiver"
      }
    },
    response: {
      status: 200,
      body: { success: true, data: { id: "wh_920184c2-9f37-4b15-bd15-0d6789b52000", isActive: false } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "INTEGRATION"
    }
  },
  {
    id: "delete-webhook",
    path: "/v1/webhooks/endpoints/{endpointId}",
    method: "DELETE",
    tag: "Webhooks",
    summary: "Deactivate Sync Endpoint",
    description: "Permanently removes a listener.",
    parameters: [
      { name: "endpointId", in: "path", type: "string", required: true, description: "Unique webhook ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, message: "Deleted successfully." }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "INTEGRATION"
    }
  },
  {
    id: "test-webhook",
    path: "/v1/webhooks/endpoints/{endpointId}/test",
    method: "POST",
    tag: "Webhooks",
    summary: "Send Test Event",
    description: "Triggers a simulated webhook event delivery.",
    parameters: [
      { name: "endpointId", in: "path", type: "string", required: true, description: "Unique webhook ID" }
    ],
    requestBody: {
      description: "Optional event details to test the webhook configuration",
      fields: {
        eventType: "policy.issued"
      }
    },
    response: {
      status: 200,
      body: { success: true, deliverStatus: "SUCCESS" }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "INTEGRATION"
    }
  },
  {
    id: "list-webhook-events",
    path: "/v1/webhooks/events",
    method: "GET",
    tag: "Webhooks",
    summary: "List Dispatched Events",
    description: "Lists logs of event deliveries sent to partner sync systems.",
    parameters: [
      { name: "limit", in: "query", type: "integer", required: false, description: "Maximum number of records to return (1-100, default 20)" },
      { name: "after", in: "query", type: "string", required: false, description: "Cursor token for fetching the next page" },
      { name: "before", in: "query", type: "string", required: false, description: "Cursor token for fetching the previous page" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: {
        success: true,
        data: [{ eventId: "evt_948201a0-44c2-4e0a-bf15-1d6789ccba02", eventType: "policy.issued" }],
        meta: {
          hasMore: false,
          nextCursor: null,
          prevCursor: null,
          totalCount: 1
        }
      }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "INTEGRATION"
    }
  },
  {
    id: "get-webhook-event",
    path: "/v1/webhooks/events/{eventId}",
    method: "GET",
    tag: "Webhooks",
    summary: "Retrieve Event Details",
    description: "Returns delivery records and payload data for a specific webhook event.",
    parameters: [
      { name: "eventId", in: "path", type: "string", required: true, description: "Unique event ID" }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { success: true, data: { eventId: "evt_948201a0-44c2-4e0a-bf15-1d6789ccba02", eventType: "policy.issued" } }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "INTEGRATION"
    }
  },
  {
    id: "replay-webhook-event",
    path: "/v1/webhooks/events/{eventId}/replay",
    method: "POST",
    tag: "Webhooks",
    summary: "Replay Sync Event",
    description: "Manually re-triggers a webhook delivery.",
    parameters: [
      { name: "eventId", in: "path", type: "string", required: true, description: "Unique event ID" }
    ],
    requestBody: {
      description: "Optional parameters to override delivery destination",
      fields: {
        customEndpointUrl: "https://testing-alternative-webhook-receiver.com/events"
      }
    },
    response: {
      status: 200,
      body: { success: true, message: "Replay initiated." }
    },
    extensions: {
      "x-facio-lifecycle-stage": "SERVICING",
      "x-facio-domain": "INTEGRATION"
    }
  },
  {
    id: "calculate-general-taxes",
    path: "/v1/tax-calculator/calculate-general-taxes",
    method: "POST",
    tag: "Tax Calculations",
    summary: "Calculate Surplus Lines Taxes",
    description: `FacioMGA’s Tax Calculator API simplifies compliance by automatically calculating complex, state-specific insurance taxes and fees for single or multi-line transactions. By providing basic details like location, dates, and coverage types, you instantly receive a complete line-by-line tax breakdown, the exact state stamp wording, transparent calculation rules, and a list of necessary filing documents to ensure accurate quoting and compliance.`,
    parameters: [],
    requestBody: {
      description: "Transaction parameters to compute tax breakdown, filing documents, and state stamp disclosures.",
      fields: {
        physical_state: "TX",
        transaction_type: "PN",
        transaction_line_of_business_list: "GEN-1002",
        transaction_line_of_business_coverage: "400.00",
        agency_fee: 400.00,
        inspection_fee: 150.42,
        rpg: 0,
        policy_effective_date: "2026-06-09",
        transaction_effective_date: "2026-06-09",
        mailing_entity_type: "commercial",
        commission_received: 0,
        admitted: 0,
        account_written_as: "B",
        tax_exempt: 0,
        ecp: 0
      }
    },
    response: {
      status: 200,
      description: "Tax calculations and document requirements calculated successfully.",
      body: {
        line_of_business_list: [
          {
            generic_code: "GEN-1002",
            fm_tax_percentage: null,
            id: 3644,
            code: "1276",
            fm_tax_title: null,
            empa_tax_title: null
          }
        ],
        state_properties: {
          state_stamp_wording_text: "<p>Guaranty Fund Nonparticipation Notice</p><p>This insurance contract is with an insurer not licensed to transact insurance in this state and is issued and delivered as surplus line coverage under the Texas insurance statutes. The Texas Department of TDI does not audit the finances or review the solvency of the surplus lines insurer providing this coverage, and the insurer is not a member of the property and casualty insurance guaranty association created under Chapter 462, Insurance Code. Chapter 225, Insurance Code, requires payment of a 4.85 percent tax on gross premium.</p><p><strong>State Stamp Wording 2</strong></p><p>DSLI Guaranty Fund Nonparticipation Notice</p><p>This insurance contract is issued and delivered as surplus lines coverage under the Texas Insurance Code. The insurer is not a member of the property and casualty insurance guaranty association created under Insurance Code Chapter 462. Insurance Code Chapter 225 requires payment of a 4.85 percent tax on gross premium.</p>",
          state_stamp_wording_instructions: "Each new or renewal insurance contract, certificate, cover note or other confirmation of insurance must state- Please select the appropriate notice per your policy specifications.",
          state_stamp_wording_font_size: 11,
          state_stamp_wording_font_bold: 0,
          state_stamp_wording_font_italics: 0,
          state_stamp_wording_font_color: "black",
          state_stamp_wording_type: "wording",
          state_stamp_wording_updated_at: "2026-04-08 00:00:00"
        },
        mailing_state_properties: null,
        account_written_as: "B",
        line_of_business: null,
        transaction_type: "PN",
        premium: 400,
        agency_fee: 400,
        inspection_fee: 150.42,
        sl_tax: 46.10,
        stamping_fee: 0.38,
        sl_service_charge: 0,
        municipal_fee: null,
        county_fee: null,
        fm_tax: 0,
        empa_tax: 0,
        rpg: 0,
        tax_rule: {
          is_sl_tax_active: true,
          sl_tax_amount_type: 1,
          sl_tax_amount: 0.0485,
          is_sl_tax_input_manual: false,
          is_stamping_fee_active: true,
          stamping_fee_amount_type: 1,
          stamping_fee_amount: 0.0004,
          is_stamping_fee_input_manual: false,
          exclude_stamping_fee_when_negative_premium: true,
          is_sl_service_charge_active: false,
          sl_service_charge_amount_type: 1,
          sl_service_charge_amount: 0,
          is_sl_service_charge_input_manual: false,
          is_municipal_fee_active: false,
          municipal_fee_amount_type: 1,
          municipal_fee_amount: 0,
          is_municipal_fee_input_manual: false,
          fee_restrictions: false,
          fee_restriction_amount_fixed_active: false,
          fee_restriction_amount_fixed: null,
          fee_restriction_amount_percentage_active: false,
          fee_restriction_amount_percentage: null,
          max_fee: false,
          max_fee_amount_type: 1,
          max_fee_amount: null,
          fee_restriction_apply_to: [],
          fee_restriction_apply_from: [],
          both_fees_allowed: true,
          ask_if_commission_received: false,
          zero_agency_fee_if_commission_received: false,
          round_up_taxes_to_the_nearest_dollar: false,
          round_up_premium_to_the_nearest_dollar: false,
          sl_tax_title: "Surplus Lines Tax",
          sl_service_charge_title: "Service Charge",
          stamping_fee_title: "Stamping Fee",
          municipal_fee_title: "Municipal Tax",
          state_notes: [],
          state_documents: [
            {
              name: "Texas Surplus Lines Policy Diligent Effort Form",
              code: "TX-DE-01",
              document_note: "Required to be completed by the producing broker.",
              document_download_link: "https://sandbox.facio.io/api/v1/documents/TX-DE-01.pdf",
              upload_required: true,
              transaction_types: [
                { name: "New Policy", short_code: "PN" }
              ],
              required_for_filing: true,
              required_for_binding: true
            }
          ]
        }
      }
    },
    errorCodes: [
      { status: 400, code: "invalid_physical_state", description: "The provided physical state code is invalid or not supported." },
      { status: 400, code: "missing_required_fields", description: "One or more required fields are missing or improperly formatted." },
      { status: 401, code: "unauthorized", description: "API Key is missing or invalid." }
    ],
    extensions: {
      "x-facio-lifecycle-stage": "QUOTE",
      "x-facio-domain": "COMPLIANCE"
    }
  }
];

// ===== 2. AGENT GATEWAY (MCP) DATABASE & GUIDES =====

const mcpGuides = [
  {
    id: "mcp-overview",
    title: "Overview",
    group: "Agent Gateway",
    description: `
      <h2>Welcome to Facio Agent Gateway</h2>
      <p style="font-size: 15px; color: var(--text-secondary); line-height: 1.6; margin-bottom: 24px;">
        Facio Agent Gateway exposes governed Model Context Protocol (MCP) tools that allow approved AI clients to safely operate parts of the Facio MGA platform.
      </p>
      <p>
        Unlike a raw API wrapper, every Facio MCP tool is permission-scoped, tenant-bound, schema-validated, audited, and classified by operational risk. Agents can inspect data, draft changes, simulate outcomes, prepare communications, and execute controlled workflow actions without bypassing the Facio system of record.
      </p>

      <div class="mcp-cards-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin: 32px 0;">
        <div class="glass-card mcp-card-item" style="border-left: 4px solid var(--accent-purple); padding: 18px; background: rgba(168, 85, 247, 0.02);">
          <h3 style="margin-top: 0; font-size: 15px; color: var(--text-primary); font-weight: 700;">Config Agent</h3>
          <p style="font-size: 13px; color: var(--text-muted); margin-bottom: 0;">Configure products, rules, documents, billing, coverages, and validation scenarios.</p>
        </div>
        <div class="glass-card mcp-card-item" style="border-left: 4px solid var(--accent-cyan); padding: 18px; background: rgba(6, 182, 212, 0.02);">
          <h3 style="margin-top: 0; font-size: 15px; color: var(--text-primary); font-weight: 700;">Operator Agent</h3>
          <p style="font-size: 13px; color: var(--text-muted); margin-bottom: 0;">Assist insurance operations: search customers, revise quotes, send links, draft endorsements, inspect claims.</p>
        </div>
        <div class="glass-card mcp-card-item" style="border-left: 4px solid var(--accent-green); padding: 18px; background: rgba(16, 185, 129, 0.02);">
          <h3 style="margin-top: 0; font-size: 15px; color: var(--text-primary); font-weight: 700;">Governance Layer</h3>
          <p style="font-size: 13px; color: var(--text-muted); margin-bottom: 0;">Every tool call is permissioned, schema-validated, tenant-bound, and audit logged.</p>
        </div>
        <div class="glass-card mcp-card-item" style="border-left: 4px solid var(--accent-magenta); padding: 18px; background: rgba(236, 72, 153, 0.02);">
          <h3 style="margin-top: 0; font-size: 15px; color: var(--text-primary); font-weight: 700;">Preview / Confirm</h3>
          <p style="font-size: 13px; color: var(--text-muted); margin-bottom: 0;">Customer-visible or downstream mutations require preview-before-commit workflows.</p>
        </div>
      </div>

      <h3 style="font-size: 16px; font-weight: 700; color: var(--text-primary); margin-top: 32px; margin-bottom: 12px;">Facio currently exposes two MCP mounts:</h3>
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px; margin-bottom: 32px;">
        <div class="glass-card" style="padding: 18px; background: rgba(168, 85, 247, 0.02);">
          <h4 style="margin-top: 0; color: var(--accent-purple); font-weight: 700;">Config Agent</h4>
          <p style="font-size: 13.5px; color: var(--text-secondary); margin-bottom: 0;">Used to configure product templates, underwriting rules, required documents, billing terms, coverage selection, validation scenarios, and sandbox product publishing.</p>
        </div>
        <div class="glass-card" style="padding: 18px; background: rgba(6, 182, 212, 0.02);">
          <h4 style="margin-top: 0; color: var(--accent-cyan); font-weight: 700;">Operator Agent</h4>
          <p style="font-size: 13.5px; color: var(--text-secondary); margin-bottom: 0;">Used to support live insurance operations: customer search, quote and policy inspection, safe customer communications, quote revisions, endorsement drafts, underwriting queue analytics, and claim memory analysis.</p>
        </div>
      </div>

      <h3 style="font-size: 16px; font-weight: 700; color: var(--text-primary); margin-top: 32px; margin-bottom: 12px;">Agent Gateway Architecture Flow</h3>
      <div class="philosophy-flow" style="display: flex; flex-direction: column; gap: 8px; font-family: var(--font-mono); font-size: 13px; color: var(--text-secondary); background: rgba(0, 0, 0, 0.2); padding: 20px; border-radius: var(--border-radius-lg); border: 1px solid rgba(255, 255, 255, 0.05); margin-bottom: 32px;">
        <div style="display: flex; align-items: center; color: var(--accent-purple); font-weight: 700;">
          AI Client <span style="font-size:11px; color: var(--text-muted); font-weight:400; font-family: var(--font-body); margin-left:8px;">(ChatGPT / Claude / Cursor / Partner Agent)</span>
        </div>
        <div style="color: var(--text-muted); padding-left: 20px;">↓</div>
        <div style="display: flex; align-items: center; color: var(--accent-cyan); font-weight: 700;">
          Facio MCP Mount <span style="font-size:11px; color: var(--text-muted); font-weight:400; font-family: var(--font-body); margin-left:8px;">(/api/v1/mcp/config or /api/v1/mcp/operator)</span>
        </div>
        <div style="color: var(--text-muted); padding-left: 20px;">↓</div>
        <div style="display: flex; align-items: center; color: var(--text-primary); font-weight: 700;">
          Authentication <span style="font-size:11px; color: var(--text-muted); font-weight:400; font-family: var(--font-body); margin-left:8px;">(Bearer facio_... or OAuth at_...)</span>
        </div>
        <div style="color: var(--text-muted); padding-left: 20px;">↓</div>
        <div style="display: flex; align-items: center; color: var(--accent-amber); font-weight: 700;">
          Permission Filter <span style="font-size:11px; color: var(--text-muted); font-weight:400; font-family: var(--font-body); margin-left:8px;">(tools/list only returns allowed tools)</span>
        </div>
        <div style="color: var(--text-muted); padding-left: 20px;">↓</div>
        <div style="display: flex; align-items: center; color: var(--accent-green); font-weight: 700;">
          Tool Execution Funnel <span style="font-size:11px; color: var(--text-muted); font-weight:400; font-family: var(--font-body); margin-left:8px;">(authorize → validate schema → run tool → audit)</span>
        </div>
        <div style="color: var(--text-muted); padding-left: 20px;">↓</div>
        <div style="display: flex; align-items: center; color: var(--accent-magenta); font-weight: 700;">
          Facio MGA Platform <span style="font-size:11px; color: var(--text-muted); font-weight:400; font-family: var(--font-body); margin-left:8px;">(Config, policy, quotes, claims, ledger, communications)</span>
        </div>
      </div>
    `
  },
  {
    id: "what-is-mcp",
    title: "What is Facio MCP?",
    group: "Agent Gateway",
    description: `
      <h2>What is Facio MCP?</h2>
      <p>The Model Context Protocol (MCP) is an open-source standard enabling LLM applications (Hosts like Claude Desktop, Cursor, or remote agents) to securely communicate with third-party databases and services via standardized JSON-RPC protocols.</p>
      <p>Unlike API keys used in custom integrations, Facio MCP structures operational boundaries around the agent, defining what schemas the agent can read, simulate, or edit.</p>
    `
  },
  {
    id: "connect-ai",
    title: "Connect an AI Client",
    group: "Agent Gateway",
    description: `
      <h2>Connect an AI Client</h2>
      <p>Agents connect to Facio by configuring local settings files or using direct Server-Sent Events (SSE) interfaces. Handshakes are initiated dynamically by loading either the Config Agent or Operator Agent mounts.</p>
      <h3>Stateless Handshake</h3>
      <p>Your client makes a standard <code>POST</code> request containing the authorization header to retrieve the available tool catalog:</p>
      <pre class="tour-code-block"><code>POST https://sandbox.facio.io/api/v1/mcp/operator</code></pre>
    `
  },
  {
    id: "auth-overview",
    title: "Authentication",
    group: "Agent Gateway",
    description: `
      <h2>Authentication</h2>
      <p>Facio Agent Gateway secures tool execution using modern token-based auth mechanisms. Every request validates tenant scopes and carrier keys before proceeding.</p>
      <ul>
        <li><strong>API Keys</strong>: Included in Bearer headers for daemon-to-server runs.</li>
        <li><strong>OAuth 2.0 Access Tokens</strong>: Used for interactive third-party client authentication.</li>
      </ul>
    `
  },
  {
    id: "oauth-discovery",
    title: "OAuth Discovery",
    group: "Agent Gateway",
    description: `
      <h2>OAuth Discovery</h2>
      <p>Facio hosts standard RFC-compliant metadata endpoints allowing chat clients (e.g. ChatGPT builder) to dynamically discover authentication servers, token lifespans, and required operational scopes.</p>
      <pre class="tour-code-block"><code>GET https://sandbox.facio.io/api/v1/mcp/.well-known/mcp-configuration</code></pre>
    `
  },
  {
    id: "api-key-setup",
    title: "API Key Setup",
    group: "Agent Gateway",
    description: `
      <h2>API Key Setup</h2>
      <p>To mount tools inside local IDE wrappers (like Cursor or VSCode Copilot), generate an API Key in the Facio Developer Dashboard, and supply it via headers in your local JSON configuration.</p>
    `
  },
  {
    id: "tool-naming",
    title: "Tool Naming: Canonical vs Wire Names",
    group: "Agent Gateway",
    description: `
      <h2>Tool Naming Conventions</h2>
      <p>To support AI hosts with different schema constraints, Facio publishes tool names in two identical versions:</p>
      <ul>
        <li><strong>Canonical Names (Dotted)</strong>: Standard dotted notation, e.g. <code>operator.quotes.search</code>. Used in TypeScript SDK and Claude setups.</li>
        <li><strong>Wire Names (Underscored)</strong>: Underscored notation, e.g. <code>operator_quotes_search</code>. Required by ChatGPT and other LLMs that reject dots in tool identifiers.</li>
      </ul>
    `
  },
  {
    id: "permissions-scopes",
    title: "Permissions & Scopes",
    group: "Agent Gateway",
    description: `
      <h2>Permissions & Scopes</h2>
      <p>Granular scopes protect your balance sheet. Agents are bound to security permissions limiting them to safe scopes (e.g., <code>operator.read</code>, <code>operator.quote.mutate</code>) corresponding to their operational role.</p>
    `
  },
  {
    id: "audit-classes",
    title: "Audit Classes",
    group: "Agent Gateway",
    description: `
      <h2>Audit Classes</h2>
      <p>Safety is built into our runtime. Every tool is assigned a risk-level safety tier, designating how mutations are logged and reviewed:</p>
      <ul>
        <li><code>read</code>: Passive queries, always auto-approved.</li>
        <li><code>draft</code> / <code>mutate-staging</code>: Temporary edits in client spaces.</li>
        <li><code>simulate</code> / <code>validate</code>: Safe rating engine simulations.</li>
        <li><code>comm</code>: Customer-visible actions (requires confirmation tokens).</li>
        <li><code>mutate</code>: Core ledger commits (requires confirmation tokens).</li>
      </ul>
    `
  },
  {
    id: "preview-confirm-pattern",
    title: "Preview / Confirm Pattern",
    group: "Agent Gateway",
    description: `
      <h2>Preview / Confirm Pattern</h2>
      <p>State-mutating tools utilize a double-pass pattern. The agent first executes a "Preview" call, validating underwriting conditions. The Gateway returns the draft plus a temporary <code>confirmation_token</code>. Only when the token is committed does the transaction write to the ledger.</p>
    `
  },
  {
    id: "error-model",
    title: "Error Model",
    group: "Agent Gateway",
    description: `
      <h2>JSON-RPC Error Model</h2>
      <p>Facio implements standard JSON-RPC 2.0 error payloads to guide AI agents when handshakes, authentication, or input schema validations fail.</p>
    `
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    group: "Agent Gateway",
    description: `
      <h2>Troubleshooting & Health Diagnostics</h2>
      <p>Guides on resolving standard execution faults, scope mismatches, network pings, and invalid arguments.</p>
    `
  },
  {
    id: "mcp-mount-config",
    title: "Config Agent Mount",
    group: "MCP Mounts",
    description: `
      <h2>Config Agent Mount</h2>
      <div style="font-family: var(--font-mono); color: var(--accent-purple); font-weight:600; margin-bottom:12px;">POST /api/v1/mcp/config</div>
      <p>Exposes tools for program templates setup, question rules modification, billing terms adjustments, and underwriting parameters configuration.</p>
      <h3>Supported Methods</h3>
      <ul>
        <li><code>tools/list</code>: Returns Config Agent tools catalog.</li>
        <li><code>tools/call</code>: Triggers configuration modifications in the sandbox draft.</li>
      </ul>
    `
  },
  {
    id: "mcp-mount-operator",
    title: "Operator Agent Mount",
    group: "MCP Mounts",
    description: `
      <h2>Operator Agent Mount</h2>
      <div style="font-family: var(--font-mono); color: var(--accent-cyan); font-weight:600; margin-bottom:12px;">POST /api/v1/mcp/operator</div>
      <p>Exposes operations servicing tools: Customer searches, quote workspace forks, document resends, and claim memory analysis.</p>
      <h3>Supported Methods</h3>
      <ul>
        <li><code>tools/list</code>: Returns Operator Agent tools catalog.</li>
        <li><code>tools/call</code>: Triggers business servcing processes in sandbox.</li>
      </ul>
    `
  }
];

const mcpTools = [
  // --- CONFIG AGENT TOOLS ---
  {
    id: "config-ping",
    wireName: "config_ping",
    canonicalName: "config.ping",
    family: "Config Agent Tools",
    phase: "Connectivity",
    auditClass: "read",
    requiredPermission: "config.read",
    riskLevel: "Safe Read",
    description: "Evaluates handshake and connection latency with the config agent server.",
    inputSchema: "{}",
    outputSchema: "{\n  \"status\": \"healthy\",\n  \"latency\": \"number\"\n}",
    promptExample: "Check if the config agent is reachable.",
    governance: "None",
    typicalFlow: "config.ping",
    auditEventProduced: "config.connectivity.pinged",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_ping", arguments: {} }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { status: "healthy", latency: 24 }, id: 1 }
  },
  {
    id: "config-products-listTemplates",
    wireName: "config_products_listTemplates",
    canonicalName: "config.products.listTemplates",
    family: "Config Agent Tools",
    phase: "Product Templates",
    auditClass: "read",
    requiredPermission: "config.read",
    riskLevel: "Safe Read",
    description: "Lists master product carrier templates available to clone.",
    inputSchema: "{\n  \"type\": \"object\",\n  \"properties\": {\n    \"lineOfBusiness\": { \"type\": \"string\" }\n  }\n}",
    outputSchema: "{\n  \"templates\": \"array\"\n}",
    promptExample: "Find available templates for property insurance.",
    governance: "None",
    typicalFlow: "config.products.listTemplates -> config.products.cloneTemplate",
    auditEventProduced: "config.templates.listed",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_products_listTemplates", arguments: { lineOfBusiness: "PROPERTY" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { templates: [{ id: "tpl_prop_01", name: "Commercial Property Standard", lob: "PROPERTY" }] }, id: 1 }
  },
  {
    id: "config-products-cloneTemplate",
    wireName: "config_products_cloneTemplate",
    canonicalName: "config.products.cloneTemplate",
    family: "Config Agent Tools",
    phase: "Product Templates",
    auditClass: "mutate-staging",
    requiredPermission: "config.write",
    riskLevel: "Staging-only change",
    description: "Clones a master carrier template to create a new draft configuration program.",
    inputSchema: "{\n  \"type\": \"object\",\n  \"required\": [\"templateId\", \"programName\"],\n  \"properties\": {\n    \"templateId\": { \"type\": \"string\" },\n    \"programName\": { \"type\": \"string\" }\n  }\n}",
    outputSchema: "{\n  \"draftId\": \"string\",\n  \"status\": \"string\"\n}",
    promptExample: "Clone template tpl_prop_01 as 'Alpha Office Package v1'.",
    governance: "Requires subsequent draft configuration steps.",
    typicalFlow: "config.products.cloneTemplate -> config.drafts.getSummary",
    auditEventProduced: "config.template.cloned",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_products_cloneTemplate", arguments: { templateId: "tpl_prop_01", programName: "Alpha Office Package v1" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { draftId: "drf_office_102", status: "DRAFT" }, id: 1 }
  },
  {
    id: "config-drafts-getSummary",
    wireName: "config_drafts_getSummary",
    canonicalName: "config.drafts.getSummary",
    family: "Config Agent Tools",
    phase: "Draft Configuration",
    auditClass: "read",
    requiredPermission: "config.read",
    riskLevel: "Safe Read",
    description: "Inspects status and summary parameters of a draft workspace configuration.",
    inputSchema: "{\n  \"draftId\": \"string\"\n}",
    outputSchema: "{\n  \"summary\": \"object\"\n}",
    promptExample: "Get configuration status of draft drf_office_102.",
    governance: "None",
    typicalFlow: "config.drafts.getSummary",
    auditEventProduced: "config.drafts.inspected",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_drafts_getSummary", arguments: { draftId: "drf_office_102" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { summary: { draftId: "drf_office_102", status: "IN_PROGRESS", rulesCount: 14 } }, id: 1 }
  },
  {
    id: "config-questions-setRequiredness",
    wireName: "config_questions_setRequiredness",
    canonicalName: "config.questions.setRequiredness",
    family: "Config Agent Tools",
    phase: "Draft Configuration",
    auditClass: "draft",
    requiredPermission: "config.write",
    riskLevel: "Staging-only change",
    description: "Adjusts underwriting field required status in the questionnaire schema.",
    inputSchema: "{\n  \"draftId\": \"string\",\n  \"fieldPath\": \"string\",\n  \"required\": \"boolean\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Make proposer email required in draft drf_office_102.",
    governance: "None",
    typicalFlow: "config.questions.setRequiredness",
    auditEventProduced: "config.question.updated",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_questions_setRequiredness", arguments: { draftId: "drf_office_102", fieldPath: "proposer.email", required: true } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "config-documents-setRequiredDocument",
    wireName: "config_documents_setRequiredDocument",
    canonicalName: "config.documents.setRequiredDocument",
    family: "Config Agent Tools",
    phase: "Draft Configuration",
    auditClass: "draft",
    requiredPermission: "config.write",
    riskLevel: "Staging-only change",
    description: "Configures mandatory compliance documents for policy binding.",
    inputSchema: "{\n  \"draftId\": \"string\",\n  \"documentType\": \"string\",\n  \"required\": \"boolean\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Require business lease documents in office package config.",
    governance: "None",
    typicalFlow: "config.documents.setRequiredDocument",
    auditEventProduced: "config.document.required_set",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_documents_setRequiredDocument", arguments: { draftId: "drf_office_102", documentType: "LEASE_AGREEMENT", required: true } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "config-billing-setBillingTerms",
    wireName: "config_billing_setBillingTerms",
    canonicalName: "config.billing.setBillingTerms",
    family: "Config Agent Tools",
    phase: "Draft Configuration",
    auditClass: "draft",
    requiredPermission: "config.write",
    riskLevel: "Staging-only change",
    description: "Configures credit periods, payment schedules, and tax rules.",
    inputSchema: "{\n  \"draftId\": \"string\",\n  \"billingMode\": \"string\",\n  \"taxPercent\": \"number\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Set monthly billing and 9% tax in draft drf_office_102.",
    governance: "None",
    typicalFlow: "config.billing.setBillingTerms",
    auditEventProduced: "config.billing.terms_set",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_billing_setBillingTerms", arguments: { draftId: "drf_office_102", billingMode: "MONTHLY", taxPercent: 9.0 } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "config-coverage-setCoverageSelection",
    wireName: "config_coverage_setCoverageSelection",
    canonicalName: "config.coverage.setCoverageSelection",
    family: "Config Agent Tools",
    phase: "Draft Configuration",
    auditClass: "draft",
    requiredPermission: "config.write",
    riskLevel: "Staging-only change",
    description: "Configures supported coverages and limits of liability.",
    inputSchema: "{\n  \"draftId\": \"string\",\n  \"coverageCode\": \"string\",\n  \"maxLimit\": \"number\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Set base liability coverage limit to 2,000,000.",
    governance: "None",
    typicalFlow: "config.coverage.setCoverageSelection",
    auditEventProduced: "config.coverage.selection_set",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_coverage_setCoverageSelection", arguments: { draftId: "drf_office_102", coverageCode: "BASE_LIABILITY", maxLimit: 2000000 } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "config-rules-listReferralRules",
    wireName: "config_rules_listReferralRules",
    canonicalName: "config.rules.listReferralRules",
    family: "Config Agent Tools",
    phase: "Underwriting Rules",
    auditClass: "read",
    requiredPermission: "config.read",
    riskLevel: "Safe Read",
    description: "Lists active underwriting referral constraints on a draft program.",
    inputSchema: "{\n  \"draftId\": \"string\"\n}",
    outputSchema: "{\n  \"rules\": \"array\"\n}",
    promptExample: "View referral rules in draft drf_office_102.",
    governance: "None",
    typicalFlow: "config.rules.listReferralRules",
    auditEventProduced: "config.rules.referrals_listed",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_rules_listReferralRules", arguments: { draftId: "drf_office_102" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { rules: [{ code: "REF_01", field: "sumInsured", limit: 5000000 }] }, id: 1 }
  },
  {
    id: "config-rules-addReferralRule",
    wireName: "config_rules_addReferralRule",
    canonicalName: "config.rules.addReferralRule",
    family: "Config Agent Tools",
    phase: "Underwriting Rules",
    auditClass: "draft",
    requiredPermission: "config.write",
    riskLevel: "Staging-only change",
    description: "Creates an underwriter referral constraint for risk checks.",
    inputSchema: "{\n  \"draftId\": \"string\",\n  \"ruleCode\": \"string\",\n  \"condition\": \"string\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Add referral rule for sumInsured exceeding 5,000,000.",
    governance: "None",
    typicalFlow: "config.rules.addReferralRule",
    auditEventProduced: "config.rule.referral_added",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_rules_addReferralRule", arguments: { draftId: "drf_office_102", ruleCode: "REF_01", condition: "sumInsured > 5000000" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "config-rules-addApprovalRule",
    wireName: "config_rules_addApprovalRule",
    canonicalName: "config.rules.addApprovalRule",
    family: "Config Agent Tools",
    phase: "Underwriting Rules",
    auditClass: "draft",
    requiredPermission: "config.write",
    riskLevel: "Staging-only change",
    description: "Creates auto-approval rule parameters for underwriting.",
    inputSchema: "{\n  \"draftId\": \"string\",\n  \"condition\": \"string\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Configure auto-approval if claimCount is 0.",
    governance: "None",
    typicalFlow: "config.rules.addApprovalRule",
    auditEventProduced: "config.rule.approval_added",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_rules_addApprovalRule", arguments: { draftId: "drf_office_102", condition: "claimsHistory.count == 0" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "config-drafts-validate",
    wireName: "config_drafts_validate",
    canonicalName: "config.drafts.validate",
    family: "Config Agent Tools",
    phase: "Validation & Simulation",
    auditClass: "validate",
    requiredPermission: "config.read",
    riskLevel: "Safe Check",
    description: "Runs configuration diagnostics and validation checks on a draft.",
    inputSchema: "{\n  \"draftId\": \"string\"\n}",
    outputSchema: "{\n  \"valid\": \"boolean\",\n  \"warnings\": \"array\"\n}",
    promptExample: "Validate config rules in draft drf_office_102.",
    governance: "None",
    typicalFlow: "config.drafts.validate -> config.drafts.publishToSandbox",
    auditEventProduced: "config.draft.validated",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_drafts_validate", arguments: { draftId: "drf_office_102" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { valid: true, warnings: [] }, id: 1 }
  },
  {
    id: "config-scenarios-runQuoteScenario",
    wireName: "config_scenarios_runQuoteScenario",
    canonicalName: "config.scenarios.runQuoteScenario",
    family: "Config Agent Tools",
    phase: "Validation & Simulation",
    auditClass: "simulate",
    requiredPermission: "config.read",
    riskLevel: "Safe Simulation",
    description: "Simulates quote calculations on draft underwriting structures.",
    inputSchema: "{\n  \"draftId\": \"string\",\n  \"mockQuoteData\": \"object\"\n}",
    outputSchema: "{\n  \"simulatedPremium\": \"number\"\n}",
    promptExample: "Simulate office quote with propertyValue of 100,000.",
    governance: "None",
    typicalFlow: "config.scenarios.runQuoteScenario",
    auditEventProduced: "config.scenario.quote_simulated",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_scenarios_runQuoteScenario", arguments: { draftId: "drf_office_102", mockQuoteData: { propertyValue: 100000 } } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { simulatedPremium: 250.0 }, id: 1 }
  },
  {
    id: "config-scenarios-runDemoScenarioPack",
    wireName: "config_scenarios_runDemoScenarioPack",
    canonicalName: "config.scenarios.runDemoScenarioPack",
    family: "Config Agent Tools",
    phase: "Validation & Simulation",
    auditClass: "simulate",
    requiredPermission: "config.read",
    riskLevel: "Safe Simulation",
    description: "Runs rating algorithms on standard carrier compliance packs.",
    inputSchema: "{\n  \"draftId\": \"string\"\n}",
    outputSchema: "{\n  \"passed\": \"boolean\",\n  \"failedCount\": \"number\"\n}",
    promptExample: "Run standard rating compliance scenarios pack.",
    governance: "None",
    typicalFlow: "config.scenarios.runDemoScenarioPack",
    auditEventProduced: "config.scenarios.demo_run",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_scenarios_runDemoScenarioPack", arguments: { draftId: "drf_office_102" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { passed: true, failedCount: 0 }, id: 1 }
  },
  {
    id: "config-drafts-publishToSandbox",
    wireName: "config_drafts_publishToSandbox",
    canonicalName: "config.drafts.publishToSandbox",
    family: "Config Agent Tools",
    phase: "Publish",
    auditClass: "mutate",
    requiredPermission: "config.publish",
    riskLevel: "Downstream financial mutation",
    description: "Deploys draft config to active sandbox environment. REQUIRES CONFIRMATION.",
    inputSchema: "{\n  \"draftId\": \"string\"\n}",
    outputSchema: "{\n  \"publishedProgramId\": \"string\",\n  \"status\": \"string\"\n}",
    promptExample: "Publish office draft config to sandbox program.",
    governance: "Requires a confirmation token generated by config.drafts.validate.",
    typicalFlow: "config.drafts.validate -> config.drafts.publishToSandbox",
    auditEventProduced: "config.program.published",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "config_drafts_publishToSandbox", arguments: { draftId: "drf_office_102" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { publishedProgramId: "e9f8a000-84b2-4d1e-bd15-0d6789b52000", status: "ACTIVE" }, id: 1 }
  },

  // --- OPERATOR AGENT TOOLS ---
  {
    id: "operator-ping",
    wireName: "operator_ping",
    canonicalName: "operator.ping",
    family: "Operator Agent Tools",
    phase: "Connectivity",
    auditClass: "read",
    requiredPermission: "operator.read",
    riskLevel: "Safe Read",
    description: "Evaluates handshake and latency with operator agent.",
    inputSchema: "{}",
    outputSchema: "{\n  \"status\": \"healthy\",\n  \"latency\": \"number\"\n}",
    promptExample: "Check if the operator agent is online.",
    governance: "None",
    typicalFlow: "operator.ping",
    auditEventProduced: "operator.connectivity.pinged",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_ping", arguments: {} }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { status: "healthy", latency: 18 }, id: 1 }
  },
  {
    id: "operator-customers-search",
    wireName: "operator_customers_search",
    canonicalName: "operator.customers.search",
    family: "Operator Agent Tools",
    phase: "Entity Resolution",
    auditClass: "read",
    requiredPermission: "operator.read",
    riskLevel: "Safe Read",
    description: "Searches customer profiles by name, email, or tenant ID.",
    inputSchema: "{\n  \"type\": \"object\",\n  \"required\": [\"query\"],\n  \"properties\": {\n    \"query\": { \"type\": \"string\" }\n  }\n}",
    outputSchema: "{\n  \"customers\": \"array\"\n}",
    promptExample: "Search for a customer named 'Emily Watson'.",
    governance: "None",
    typicalFlow: "operator.customers.search -> operator.customers.getContext",
    auditEventProduced: "operator.customers.searched",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_customers_search", arguments: { query: "Emily Watson" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { customers: [{ id: "cust_emily_03", name: "Emily Watson", email: "emily.watson@example.com" }] }, id: 1 }
  },
  {
    id: "operator-customers-getContext",
    wireName: "operator_customers_getContext",
    canonicalName: "operator.customers.getContext",
    family: "Operator Agent Tools",
    phase: "Entity Resolution",
    auditClass: "read",
    requiredPermission: "operator.read",
    riskLevel: "Safe Read",
    description: "Retrieves complete CRM timeline and policies list for a customer.",
    inputSchema: "{\n  \"customerId\": \"string\"\n}",
    outputSchema: "{\n  \"profile\": \"object\",\n  \"activePolicies\": \"array\"\n}",
    promptExample: "Get profile context details for customer cust_emily_03.",
    governance: "None",
    typicalFlow: "operator.customers.getContext",
    auditEventProduced: "operator.customer.context_inspected",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_customers_getContext", arguments: { customerId: "cust_emily_03" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { profile: { id: "cust_emily_03", name: "Emily Watson" }, activePolicies: [{ id: "pol_health_777", policyNumber: "POL-HEALTH-38290" }] }, id: 1 }
  },
  {
    id: "operator-quotes-search",
    wireName: "operator_quotes_search",
    canonicalName: "operator.quotes.search",
    family: "Operator Agent Tools",
    phase: "Entity Resolution",
    auditClass: "read",
    requiredPermission: "operator.read",
    riskLevel: "Safe Read",
    description: "Queries active or referred quote logs matching search constraints.",
    inputSchema: "{\n  \"type\": \"object\",\n  \"properties\": {\n    \"status\": { \"type\": \"string\" }\n  }\n}",
    outputSchema: "{\n  \"quotes\": \"array\"\n}",
    promptExample: "Find all quotes currently marked as referred.",
    governance: "None",
    typicalFlow: "operator.quotes.search -> operator.quotes.get",
    auditEventProduced: "operator.quotes.searched",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_quotes_search", arguments: { status: "UNDERWRITER_REFERRAL" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { quotes: [{ id: "q_motor_8471", proposer: "Jane Doe", status: "UNDERWRITER_REFERRAL" }] }, id: 1 }
  },
  {
    id: "operator-policies-search",
    wireName: "operator_policies_search",
    canonicalName: "operator.policies.search",
    family: "Operator Agent Tools",
    phase: "Entity Resolution",
    auditClass: "read",
    requiredPermission: "operator.read",
    riskLevel: "Safe Read",
    description: "Queries bound policy logs matching search parameters.",
    inputSchema: "{\n  \"type\": \"object\",\n  \"properties\": {\n    \"policyNumber\": { \"type\": \"string\" }\n  }\n}",
    outputSchema: "{\n  \"policies\": \"array\"\n}",
    promptExample: "Find policy with number POL-2026-84729.",
    governance: "None",
    typicalFlow: "operator.policies.search -> operator.policies.get",
    auditEventProduced: "operator.policies.searched",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_policies_search", arguments: { policyNumber: "POL-2026-84729" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { policies: [{ id: "pol_motor_002", policyNumber: "POL-2026-84729", status: "ISSUED" }] }, id: 1 }
  },
  {
    id: "operator-quotes-get",
    wireName: "operator_quotes_get",
    canonicalName: "operator.quotes.get",
    family: "Operator Agent Tools",
    phase: "Entity Resolution",
    auditClass: "read",
    requiredPermission: "operator.read",
    riskLevel: "Safe Read",
    description: "Retrieves complete transaction log and breakdown for a quote ID.",
    inputSchema: "{\n  \"quoteId\": \"string\"\n}",
    outputSchema: "{\n  \"quote\": \"object\"\n}",
    promptExample: "Get quote details for quote ID q_motor_8471.",
    governance: "None",
    typicalFlow: "operator.quotes.get",
    auditEventProduced: "operator.quote.inspected",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_quotes_get", arguments: { quoteId: "q_motor_8471" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { quote: { id: "q_motor_8471", proposer: "Jane Doe", grossPremium: 450.0 } }, id: 1 }
  },
  {
    id: "operator-policies-get",
    wireName: "operator_policies_get",
    canonicalName: "operator.policies.get",
    family: "Operator Agent Tools",
    phase: "Entity Resolution",
    auditClass: "read",
    requiredPermission: "operator.read",
    riskLevel: "Safe Read",
    description: "Retrieves complete schedule metadata and transaction timeline for a policy ID.",
    inputSchema: "{\n  \"policyId\": \"string\"\n}",
    outputSchema: "{\n  \"policy\": \"object\"\n}",
    promptExample: "Retrieve policy file details for policy ID pol_motor_002.",
    governance: "None",
    typicalFlow: "operator.policies.get",
    auditEventProduced: "operator.policy.inspected",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_policies_get", arguments: { policyId: "pol_motor_002" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { policy: { id: "pol_motor_002", policyNumber: "POL-2026-84729", status: "ISSUED" } }, id: 1 }
  },
  {
    id: "operator-links-sendWizardLink",
    wireName: "operator_links_sendWizardLink",
    canonicalName: "operator.links.sendWizardLink",
    family: "Operator Agent Tools",
    phase: "Communications",
    auditClass: "comm",
    requiredPermission: "operator.write",
    riskLevel: "Customer-visible action",
    description: "Generates and dispatches a customer data capture wizard link. REQUIRES CONFIRMATION.",
    inputSchema: "{\n  \"type\": \"object\",\n  \"required\": [\"customerId\", \"type\"],\n  \"properties\": {\n    \"customerId\": { \"type\": \"string\" },\n    \"type\": { \"type\": \"string\" }\n  }\n}",
    outputSchema: "{\n  \"success\": \"boolean\",\n  \"dispatchId\": \"string\"\n}",
    promptExample: "Send quote data capture link to customer cust_emily_03.",
    governance: "Dispatches SMS/email with interactive link to policyholder.",
    typicalFlow: "operator.links.sendWizardLink",
    auditEventProduced: "operator.comms.wizard_dispatched",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_links_sendWizardLink", arguments: { customerId: "cust_emily_03", type: "QUOTE_CAPTURE" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true, dispatchId: "dsp_wiz_84721" }, id: 1 }
  },
  {
    id: "operator-quotes-sendReminder",
    wireName: "operator_quotes_sendReminder",
    canonicalName: "operator.quotes.sendReminder",
    family: "Operator Agent Tools",
    phase: "Communications",
    auditClass: "comm",
    requiredPermission: "operator.write",
    riskLevel: "Customer-visible action",
    description: "Sends automated payment or binding email reminders. REQUIRES CONFIRMATION.",
    inputSchema: "{\n  \"quoteId\": \"string\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Resend binding reminder email for quote q_motor_8471.",
    governance: "Dispatches email reminder.",
    typicalFlow: "operator.quotes.sendReminder",
    auditEventProduced: "operator.comms.reminder_sent",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_quotes_sendReminder", arguments: { quoteId: "q_motor_8471" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "operator-documents-resendPolicyDocuments",
    wireName: "operator_documents_resendPolicyDocuments",
    canonicalName: "operator.documents.resendPolicyDocuments",
    family: "Operator Agent Tools",
    phase: "Communications",
    auditClass: "comm",
    requiredPermission: "operator.write",
    riskLevel: "Customer-visible action",
    description: "Re-dispatches bound schedule PDF files to policyholder email. REQUIRES CONFIRMATION.",
    inputSchema: "{\n  \"policyId\": \"string\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Resend bound policy schedule files to policyholder.",
    governance: "Email PDF dispatch.",
    typicalFlow: "operator.documents.resendPolicyDocuments",
    auditEventProduced: "operator.comms.docs_resent",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_documents_resendPolicyDocuments", arguments: { policyId: "pol_motor_002" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "operator-claims-sendFnolLink",
    wireName: "operator_claims_sendFnolLink",
    canonicalName: "operator.claims.sendFnolLink",
    family: "Operator Agent Tools",
    phase: "Communications",
    auditClass: "comm",
    requiredPermission: "operator.write",
    riskLevel: "Customer-visible action",
    description: "Generates FNOL claims onboarding link for the customer. REQUIRES CONFIRMATION.",
    inputSchema: "{\n  \"policyId\": \"string\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\",\n  \"link\": \"string\"\n}",
    promptExample: "Send claims incident FNOL report link to customer.",
    governance: "SMS claims link generation and dispatch.",
    typicalFlow: "operator.claims.sendFnolLink",
    auditEventProduced: "operator.comms.fnol_link_sent",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_claims_sendFnolLink", arguments: { policyId: "pol_motor_002" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true, link: "https://claims.facio.io/onboard/fnol_2026_84" }, id: 1 }
  },
  {
    id: "operator-underwriting-listQueue",
    wireName: "operator_underwriting_listQueue",
    canonicalName: "operator.underwriting.listQueue",
    family: "Operator Agent Tools",
    phase: "Analytics",
    auditClass: "read",
    requiredPermission: "operator.read",
    riskLevel: "Safe Read",
    description: "Lists pending underwriter referral tasks in the queue.",
    inputSchema: "{}",
    outputSchema: "{\n  \"referrals\": \"array\"\n}",
    promptExample: "View all referred policies in the underwriting queue.",
    governance: "None",
    typicalFlow: "operator.underwriting.listQueue",
    auditEventProduced: "operator.underwriting.queue_listed",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_underwriting_listQueue", arguments: {} }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { referrals: [{ quoteId: "q_motor_8471", reason: "HIGH_VALUE_VEHICLE", date: "2026-06-08T12:00:00Z" }] }, id: 1 }
  },
  {
    id: "operator-analytics-getSalesStats",
    wireName: "operator_analytics_getSalesStats",
    canonicalName: "operator.analytics.getSalesStats",
    family: "Operator Agent Tools",
    phase: "Analytics",
    auditClass: "read",
    requiredPermission: "operator.read",
    riskLevel: "Safe Read",
    description: "Retrieves month-to-date sales figures, gross written premiums, and bind rates.",
    inputSchema: "{}",
    outputSchema: "{\n  \"gwp\": \"number\",\n  \"bindRate\": \"number\"\n}",
    promptExample: "Get sales bind statistics MTD.",
    governance: "None",
    typicalFlow: "operator.analytics.getSalesStats",
    auditEventProduced: "operator.analytics.sales_pulled",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_analytics_getSalesStats", arguments: {} }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { gwp: 148200.0, bindRate: 72.4 }, id: 1 }
  },
  {
    id: "operator-analytics-getQuotePipelineStats",
    wireName: "operator_analytics_getQuotePipelineStats",
    canonicalName: "operator.analytics.getQuotePipelineStats",
    family: "Operator Agent Tools",
    phase: "Analytics",
    auditClass: "read",
    requiredPermission: "operator.read",
    riskLevel: "Safe Read",
    description: "Retrieves conversion funnel analytics across quote pipelines.",
    inputSchema: "{}",
    outputSchema: "{\n  \"quoted\": \"number\",\n  \"referred\": \"number\"\n}",
    promptExample: "Get quote conversion stats.",
    governance: "None",
    typicalFlow: "operator.analytics.getQuotePipelineStats",
    auditEventProduced: "operator.analytics.pipeline_pulled",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_analytics_getQuotePipelineStats", arguments: {} }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { quoted: 580, referred: 32 }, id: 1 }
  },
  {
    id: "operator-quotes-forkWorkspace",
    wireName: "operator_quotes_forkWorkspace",
    canonicalName: "operator.quotes.forkWorkspace",
    family: "Operator Agent Tools",
    phase: "Quote Workspace",
    auditClass: "mutate-staging",
    requiredPermission: "operator.write",
    riskLevel: "Staging-only change",
    description: "Creates an isolated sandbox workspace to modify quote risk criteria.",
    inputSchema: "{\n  \"quoteId\": \"string\"\n}",
    outputSchema: "{\n  \"workspaceId\": \"string\",\n  \"status\": \"string\"\n}",
    promptExample: "Fork quote q_motor_8471 into a revisions workspace.",
    governance: "Creates revision draft workspace.",
    typicalFlow: "operator.quotes.forkWorkspace -> operator.quotes.updateTerms -> operator.quotes.rate",
    auditEventProduced: "operator.workspace.forked",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_quotes_forkWorkspace", arguments: { quoteId: "q_motor_8471" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { workspaceId: "wksp_quote_jane_v2", status: "OPEN" }, id: 1 }
  },
  {
    id: "operator-quotes-updateTerms",
    wireName: "operator_quotes_updateTerms",
    canonicalName: "operator.quotes.updateTerms",
    family: "Operator Agent Tools",
    phase: "Quote Workspace",
    auditClass: "draft",
    requiredPermission: "operator.write",
    riskLevel: "Staging-only change",
    description: "Updates custom questionnaire answers in the workspace draft.",
    inputSchema: "{\n  \"workspaceId\": \"string\",\n  \"updatedFields\": \"object\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Update engineSize to 1800 cc in quote workspace.",
    governance: "None",
    typicalFlow: "operator.quotes.updateTerms -> operator.quotes.rate",
    auditEventProduced: "operator.quote.fields_updated",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_quotes_updateTerms", arguments: { workspaceId: "wksp_quote_jane_v2", updatedFields: { "vehicle.engineSize": 1800 } } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "operator-quotes-rate",
    wireName: "operator_quotes_rate",
    canonicalName: "operator.quotes.rate",
    family: "Operator Agent Tools",
    phase: "Quote Workspace",
    auditClass: "simulate",
    requiredPermission: "operator.write",
    riskLevel: "Safe Simulation",
    description: "Simulates rating checks and underwriting validations on modified terms.",
    inputSchema: "{\n  \"workspaceId\": \"string\"\n}",
    outputSchema: "{\n  \"premiumBreakdown\": \"object\",\n  \"referralRequired\": \"boolean\"\n}",
    promptExample: "Re-rate the quote in workspace wksp_quote_jane_v2.",
    governance: "None",
    typicalFlow: "operator.quotes.rate -> operator.quotes.previewSend",
    auditEventProduced: "operator.quote.rated",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_quotes_rate", arguments: { workspaceId: "wksp_quote_jane_v2" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { premiumBreakdown: { grossPremium: 492.00 }, referralRequired: false }, id: 1 },
    errorCodes: [
      { status: 400, code: "workspace_not_found", description: "The specified workspaceId is invalid or has expired." },
      { status: 400, code: "incomplete_workspace_terms", description: "Mandatory question fields are missing from the quote terms draft." },
      { status: 403, code: "insufficient_permissions", description: "Your API key credentials do not authorize simulation writes on this client ledger." }
    ]
  },
  {
    id: "operator-quotes-previewSend",
    wireName: "operator_quotes_previewSend",
    canonicalName: "operator.quotes.previewSend",
    family: "Operator Agent Tools",
    phase: "Quote Workspace",
    auditClass: "validate",
    requiredPermission: "operator.write",
    riskLevel: "Safe Preview",
    description: "Generates draft email payload and premium schedule for preview. Generates confirmation token.",
    inputSchema: "{\n  \"workspaceId\": \"string\"\n}",
    outputSchema: "{\n  \"emailPreview\": \"string\",\n  \"confirmationToken\": \"string\"\n}",
    promptExample: "Generate quote preview and confirmation token for revised terms.",
    governance: "Generates temporary confirmation token.",
    typicalFlow: "operator.quotes.previewSend -> operator.quotes.sendRevisedQuote",
    auditEventProduced: "operator.quote.preview_generated",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_quotes_previewSend", arguments: { workspaceId: "wksp_quote_jane_v2" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { emailPreview: "Draft Email to Jane Doe...", confirmationToken: "cf_tok_operator_118" }, id: 1 }
  },
  {
    id: "operator-quotes-saveRevision",
    wireName: "operator_quotes_saveRevision",
    canonicalName: "operator.quotes.saveRevision",
    family: "Operator Agent Tools",
    phase: "Quote Workspace",
    auditClass: "mutate-staging",
    requiredPermission: "operator.write",
    riskLevel: "Staging-only change",
    description: "Commits revisions as a draft version on the core quote schema.",
    inputSchema: "{\n  \"workspaceId\": \"string\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Save workspace revision changes.",
    governance: "Staging commit.",
    typicalFlow: "operator.quotes.saveRevision",
    auditEventProduced: "operator.quote.revision_saved",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_quotes_saveRevision", arguments: { workspaceId: "wksp_quote_jane_v2" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "operator-quotes-sendRevisedQuote",
    wireName: "operator_quotes_sendRevisedQuote",
    canonicalName: "operator.quotes.sendRevisedQuote",
    family: "Operator Agent Tools",
    phase: "Quote Workspace",
    auditClass: "mutate",
    requiredPermission: "operator.write",
    riskLevel: "Downstream financial mutation",
    description: "Locks the revised workspace and dispatches email update to proposer. REQUIRES CONFIRMATION.",
    inputSchema: "{\n  \"workspaceId\": \"string\",\n  \"confirmationToken\": \"string\"\n}",
    outputSchema: "{\n  \"revisedQuoteId\": \"string\",\n  \"status\": \"QUOTED\"\n}",
    promptExample: "Send revised quote to customer Jane Doe. REQUIRES CONFIRMATION.",
    governance: "Requires a confirmation token generated by previewQuoteSend.",
    typicalFlow: "operator.quotes.previewSend -> operator.quotes.sendRevisedQuote",
    auditEventProduced: "operator.quote.revised",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_quotes_sendRevisedQuote", arguments: { workspaceId: "wksp_quote_jane_v2", confirmationToken: "cf_tok_operator_118" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { revisedQuoteId: "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001", status: "QUOTED" }, id: 1 }
  },
  {
    id: "operator-endorsements-createDraft",
    wireName: "operator_endorsements_createDraft",
    canonicalName: "operator.endorsements.createDraft",
    family: "Operator Agent Tools",
    phase: "Endorsements",
    auditClass: "mutate-staging",
    requiredPermission: "operator.write",
    riskLevel: "Staging-only change",
    description: "Creates draft mid-term adjustment (MTA) shell to endorse parameters on an issued policy.",
    inputSchema: "{\n  \"policyId\": \"string\",\n  \"reason\": \"string\"\n}",
    outputSchema: "{\n  \"endorsementId\": \"string\",\n  \"status\": \"string\"\n}",
    promptExample: "Create draft endorsement to change vehicle value.",
    governance: "None",
    typicalFlow: "operator.endorsements.createDraft -> operator.endorsements.sendDataCaptureLink",
    auditEventProduced: "operator.endorsement.drafted",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_endorsements_createDraft", arguments: { policyId: "pol_motor_002", reason: "Value upgrade" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { endorsementId: "mta_draft_8471a", status: "DRAFT" }, id: 1 }
  },
  {
    id: "operator-endorsements-sendDataCaptureLink",
    wireName: "operator_endorsements_sendDataCaptureLink",
    canonicalName: "operator.endorsements.sendDataCaptureLink",
    family: "Operator Agent Tools",
    phase: "Endorsements",
    auditClass: "comm",
    requiredPermission: "operator.write",
    riskLevel: "Customer-visible action",
    description: "Sends questionnaire link to customer to input updated risk answers. REQUIRES CONFIRMATION.",
    inputSchema: "{\n  \"endorsementId\": \"string\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Send MTA questionnaire link to proposer.",
    governance: "Customer email communication.",
    typicalFlow: "operator.endorsements.sendDataCaptureLink",
    auditEventProduced: "operator.endorsement.link_sent",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_endorsements_sendDataCaptureLink", arguments: { endorsementId: "mta_draft_8471a" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "operator-endorsements-submitForReview",
    wireName: "operator_endorsements_submitForReview",
    canonicalName: "operator.endorsements.submitForReview",
    family: "Operator Agent Tools",
    phase: "Endorsements",
    auditClass: "mutate",
    requiredPermission: "operator.write",
    riskLevel: "Downstream financial mutation",
    description: "Submits MTA risk parameters for underwriter review and logs to ledger. REQUIRES CONFIRMATION.",
    inputSchema: "{\n  \"endorsementId\": \"string\",\n  \"updatedFields\": \"object\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\",\n  \"status\": \"string\"\n}",
    promptExample: "Submit vehicle value changes for underwriter review.",
    governance: "Requires underwriter confirmation token.",
    typicalFlow: "operator.endorsements.submitForReview",
    auditEventProduced: "operator.endorsement.submitted",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_endorsements_submitForReview", arguments: { endorsementId: "mta_draft_8471a", updatedFields: { "vehicle.vehicleValue": 25000 } } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true, status: "PENDING_REVIEW" }, id: 1 }
  },
  {
    id: "operator-claims-getClaimMemory",
    wireName: "operator_claims_getClaimMemory",
    canonicalName: "operator.claims.getClaimMemory",
    family: "Operator Agent Tools",
    phase: "Claim Memory",
    auditClass: "read",
    requiredPermission: "claims.read",
    riskLevel: "Safe Read",
    description: "Retrieves unstructured comments timeline and notes regarding claim events.",
    inputSchema: "{\n  \"claimId\": \"string\"\n}",
    outputSchema: "{\n  \"memoryTimeline\": \"array\"\n}",
    promptExample: "Get claims memory updates for claim ID clm_health_101.",
    governance: "None",
    typicalFlow: "operator.claims.getClaimMemory",
    auditEventProduced: "operator.claims.timeline_retrieved",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_claims_getClaimMemory", arguments: { claimId: "clm_health_101" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { memoryTimeline: [{ timestamp: "2026-05-16T10:00:00Z", comment: "Laparoscopic appendectomy executed." }] }, id: 1 }
  },
  {
    id: "operator-claims-refreshClaimMemory",
    wireName: "operator_claims_refreshClaimMemory",
    canonicalName: "operator.claims.refreshClaimMemory",
    family: "Operator Agent Tools",
    phase: "Claim Memory",
    auditClass: "mutate-staging",
    requiredPermission: "claims.write",
    riskLevel: "Staging-only change",
    description: "Regenerates and indexes claims memory with recent adjuster documents.",
    inputSchema: "{\n  \"claimId\": \"string\"\n}",
    outputSchema: "{\n  \"success\": \"boolean\"\n}",
    promptExample: "Re-index claim memory with new hospital bills.",
    governance: "None",
    typicalFlow: "operator.claims.refreshClaimMemory",
    auditEventProduced: "operator.claims.memory_reindexed",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_claims_refreshClaimMemory", arguments: { claimId: "clm_health_101" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { success: true }, id: 1 }
  },
  {
    id: "operator-claims-findSimilarClaims",
    wireName: "operator_claims_findSimilarClaims",
    canonicalName: "operator.claims.findSimilarClaims",
    family: "Operator Agent Tools",
    phase: "Claim Memory",
    auditClass: "read",
    requiredPermission: "claims.read",
    riskLevel: "Safe Read",
    description: "Finds similar historical claims based on incident context and billing.",
    inputSchema: "{\n  \"claimId\": \"string\"\n}",
    outputSchema: "{\n  \"similarClaims\": \"array\"\n}",
    promptExample: "Find similar historical claims to clm_health_101.",
    governance: "None",
    typicalFlow: "operator.claims.findSimilarClaims",
    auditEventProduced: "operator.claims.similar_searched",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_claims_findSimilarClaims", arguments: { claimId: "clm_health_101" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { similarClaims: [{ claimId: "clm_health_092", similarityScore: 0.94 }] }, id: 1 }
  },
  {
    id: "operator-claims-analyzeClaimMemory",
    wireName: "operator_claims_analyzeClaimMemory",
    canonicalName: "operator.claims.analyzeClaimMemory",
    family: "Operator Agent Tools",
    phase: "Claim Memory",
    auditClass: "simulate",
    requiredPermission: "claims.read",
    riskLevel: "Safe Simulation",
    description: "Runs LLM semantic checks on claims memory to identify inconsistencies or fraud.",
    inputSchema: "{\n  \"claimId\": \"string\"\n}",
    outputSchema: "{\n  \"fraudProbability\": \"number\",\n  \"analysisReport\": \"string\"\n}",
    promptExample: "Run fraud analysis checks on claim clm_health_101.",
    governance: "None",
    typicalFlow: "operator.claims.analyzeClaimMemory",
    auditEventProduced: "operator.claims.fraud_analyzed",
    jsonRpcRequest: { jsonrpc: "2.0", method: "tools/call", params: { name: "operator_claims_analyzeClaimMemory", arguments: { claimId: "clm_health_101" } }, id: 1 },
    jsonRpcResponse: { jsonrpc: "2.0", result: { fraudProbability: 0.05, analysisReport: "Low fraud risk. Billing patterns match similar inpatient procedures." }, id: 1 }
  }
];

// ===== 3. PRODUCT CONTRACTS (DYNAMIC SCHEMAS FOR /v1/quotes) =====

const productContracts = {
  "private-motor": {
    name: "Alpha Private Motor - Private Car",
    programId: "e9f8a000-84b2-4d1e-bd15-0d6789b52000",
    quotes: {
      fields: [
        { path: "proposer.firstName", type: "string", required: "yes", validation: "min 1", desc: "Policyholder first name" },
        { path: "proposer.lastName", type: "string", required: "yes", validation: "min 1", desc: "Policyholder last name" },
        { path: "proposer.dateOfBirth", type: "string", required: "yes", validation: "ISO Date", desc: "Policyholder birthdate" },
        { path: "proposer.email", type: "string", required: "yes", validation: "Email", desc: "Policyholder email address" },
        { path: "vehicle.make", type: "string", required: "yes", validation: "min 1", desc: "Vehicle manufacturer make" },
        { path: "vehicle.model", type: "string", required: "yes", validation: "min 1", desc: "Vehicle model name" },
        { path: "vehicle.year", type: "number", required: "yes", validation: "1900–2027", desc: "Year of manufacture" },
        { path: "vehicle.vehicleValue", type: "number", required: "yes", validation: "min 0", desc: "Declared vehicle market value" },
        { path: "vehicle.engineSize", type: "number", required: "yes", validation: "min 0", desc: "Engine displacement in cc" },
        { path: "vehicle.countryOfRegistration", type: "string", required: "yes", validation: "\"CY\"", desc: "Country registration code" },
        { path: "risk.coverRequired", type: "string", required: "yes", validation: "Comprehensive/ThirdPartyOnly", desc: "Level of cover selection" },
        { path: "risk.licenseYears", type: "number", required: "yes", validation: "0–80", desc: "Number of years holding license" },
        { path: "risk.hasClaims", type: "boolean", required: "yes", validation: "true/false", desc: "History of prior claims indicator" },
        { path: "standard_config.deductible", type: "number", required: "yes", validation: "min 0", desc: "Standard deductible" },
        { path: "standard_config.max_limit", type: "number", required: "yes", validation: "min 0", desc: "Standard coverage limit" },
        { path: "standard_config.payment_frequency", type: "string", required: "yes", validation: "MONTHLY/ANNUALLY", desc: "Payment frequency" },
        { path: "dynamic_config.optional_covers", type: "array", required: "no", validation: "string array", desc: "Selected optional covers" },
        { path: "dynamic_config.discretionary_discount", type: "number", required: "no", validation: "0.0-1.0", desc: "Discretionary discount" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Proposer Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>First Name</label><div class="visual-field-value">Jane</div></div>
            <div class="visual-field"><label>Last Name</label><div class="visual-field-value">Doe</div></div>
            <div class="visual-field"><label>Date of Birth</label><div class="visual-field-value">1985-06-15</div></div>
            <div class="visual-field"><label>Email Address</label><div class="visual-field-value">jane.doe@example.com</div></div>
          </div>
        </div>
        <div class="visual-form-group">
          <div class="visual-group-title">Vehicle Specifications</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Registration Country</label><div class="visual-field-value">CY</div></div>
            <div class="visual-field"><label>Make</label><div class="visual-field-value">Toyota</div></div>
            <div class="visual-field"><label>Model</label><div class="visual-field-value">Corolla</div></div>
            <div class="visual-field"><label>Year</label><div class="visual-field-value">2018</div></div>
            <div class="visual-field"><label>Engine Size (cc)</label><div class="visual-field-value">1500</div></div>
            <div class="visual-field"><label>Vehicle Value (€)</label><div class="visual-field-value">15000</div></div>
          </div>
        </div>
        <div class="visual-form-group">
          <div class="visual-group-title">Underwriting Risk Factors</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Cover Required</label><div class="visual-field-value">Comprehensive</div></div>
            <div class="visual-field"><label>Years Licensed</label><div class="visual-field-value">10</div></div>
            <div class="visual-field"><label>Prior Claims</label><div class="visual-field-value">false</div></div>
          </div>
        </div>
      `,
      schema: {
        "$schema": "http://json-schema.org/draft-07/schema#",
        "title": "PrivateMotorQuoteSchema",
        "type": "object",
        "required": ["proposer", "vehicle", "risk"],
        "properties": {
          "proposer": {
            "type": "object",
            "required": ["firstName", "lastName", "dateOfBirth", "email"],
            "properties": {
              "firstName": { "type": "string", "minLength": 1 },
              "lastName": { "type": "string", "minLength": 1 },
              "dateOfBirth": { "type": "string", "format": "date" },
              "email": { "type": "string", "format": "email" }
            }
          },
          "vehicle": {
            "type": "object",
            "required": ["make", "model", "year", "vehicleValue", "engineSize", "countryOfRegistration"],
            "properties": {
              "make": { "type": "string" },
              "model": { "type": "string" },
              "year": { "type": "integer", "minimum": 1900 },
              "vehicleValue": { "type": "number", "minimum": 0 },
              "engineSize": { "type": "integer", "minimum": 0 },
              "countryOfRegistration": { "type": "string", "enum": ["CY"] }
            }
          },
          "risk": {
            "type": "object",
            "required": ["coverRequired", "licenseYears", "hasClaims"],
            "properties": {
              "coverRequired": { "type": "string", "enum": ["Comprehensive", "ThirdPartyOnly"] },
              "licenseYears": { "type": "integer", "minimum": 0 },
              "hasClaims": { "type": "boolean" }
            }
          },
          "standard_config": {
            "type": "object",
            "required": ["deductible", "max_limit", "payment_frequency"],
            "properties": {
              "deductible": { "type": "integer", "minimum": 0 },
              "max_limit": { "type": "integer", "minimum": 0 },
              "payment_frequency": { "type": "string", "enum": ["MONTHLY", "ANNUALLY"] }
            }
          },
          "dynamic_config": {
            "type": "object",
            "required": ["optional_covers", "discretionary_discount"],
            "properties": {
              "optional_covers": {
                "type": "array",
                "items": { "type": "string" }
              },
              "discretionary_discount": { "type": "number", "minimum": 0, "maximum": 1 }
            }
          }
        }
      },
      payload: {
        programId: "e9f8a000-84b2-4d1e-bd15-0d6789b52000",
        effectiveDate: "2026-03-01T00:00:00.000Z",
        quoteData: {
          proposer: {
            firstName: "Jane",
            lastName: "Doe",
            email: "jane.doe@example.com",
            dateOfBirth: "1985-06-15"
          },
          vehicle: {
            make: "Toyota",
            model: "Corolla",
            year: 2018,
            vehicleValue: 15000,
            engineSize: 1500,
            countryOfRegistration: "CY"
          },
          risk: {
            coverRequired: "Comprehensive",
            licenseYears: 10,
            hasClaims: false
          }
        },
        standard_config: {
          deductible: 500,
          max_limit: 100000,
          payment_frequency: "ANNUALLY"
        },
        dynamic_config: {
          optional_covers: ["roadside_assistance", "windshield_cover"],
          discretionary_discount: 0.05
        }
      }
    },
    mta: {
      fields: [
        { path: "effectiveDate", type: "string", required: "yes", validation: "ISO Date", desc: "The adjustment inception date" },
        { path: "changes.vehicle.vehicleValue", type: "number", required: "yes", validation: "min 0", desc: "Updated vehicle market value" },
        { path: "reason", type: "string", required: "yes", validation: "min 1", desc: "Statement of reasons for endorsement" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Endorsement Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Effective Date</label><div class="visual-field-value">2026-06-01T00:00:00Z</div></div>
            <div class="visual-field"><label>Vehicle Value (€)</label><div class="visual-field-value">25000</div></div>
            <div class="visual-field"><label>Reason</label><div class="visual-field-value">Upgraded vehicle Trim with alloy wheels.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["effectiveDate", "changes", "reason"],
        "properties": {
          "effectiveDate": { "type": "string", "format": "date-time" },
          "changes": {
            "type": "object",
            "properties": {
              "vehicle": {
                "type": "object",
                "properties": {
                  "vehicleValue": { "type": "number", "minimum": 0 }
                }
              }
            }
          },
          "reason": { "type": "string" }
        }
      },
      payload: {
        effectiveDate: "2026-06-01T00:00:00Z",
        changes: { vehicle: { vehicleValue: 25000 } },
        reason: "Upgraded vehicle Trim with alloy wheels."
      }
    },
    renewals: {
      fields: [
        { path: "unchanged", type: "boolean", required: "yes", validation: "true/false", desc: "Confirm no changes to risk profile" },
        { path: "notes", type: "string", required: "no", validation: "string", desc: "Renewal notes" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Renewal Declarations</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Profile Unchanged</label><div class="visual-field-value">true</div></div>
            <div class="visual-field"><label>Operator Notes</label><div class="visual-field-value">No claims or traffic offenses in past year.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["unchanged"],
        "properties": {
          "unchanged": { "type": "boolean" },
          "notes": { "type": "string" }
        }
      },
      payload: {
        unchanged: true,
        notes: "No claims or traffic offenses in past year."
      }
    },
    claims: {
      fields: [
        { path: "policyId", type: "string", required: "yes", validation: "UUID", desc: "Identifier of the target policy" },
        { path: "intake.incident.type", type: "string", required: "yes", validation: "enum", desc: "Category of loss event" },
        { path: "intake.incident.date", type: "string", required: "yes", validation: "ISO Date", desc: "Date of loss event occurrence" },
        { path: "intake.incident.description", type: "string", required: "yes", validation: "min 10", desc: "Descriptive loss narrative" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Incident Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Policy ID</label><div class="visual-field-value">pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02</div></div>
            <div class="visual-field"><label>Incident Type</label><div class="visual-field-value">collision</div></div>
            <div class="visual-field"><label>Incident Date</label><div class="visual-field-value">2026-05-15</div></div>
            <div class="visual-field"><label>Incident Description</label><div class="visual-field-value">Rear-ended at a traffic light by a third party.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["policyId", "intake"],
        "properties": {
          "policyId": { "type": "string" },
          "intake": {
            "type": "object",
            "required": ["incident"],
            "properties": {
              "incident": {
                "type": "object",
                "required": ["type", "date", "description"],
                "properties": {
                  "type": { "type": "string", "enum": ["collision", "theft", "fire", "windstorm"] },
                  "date": { "type": "string" },
                  "description": { "type": "string" }
                }
              }
            }
          }
        }
      },
      payload: {
        policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
        intake: {
          incident: {
            type: "collision",
            date: "2026-05-15",
            description: "Rear-ended at a traffic light by a third party."
          }
        }
      }
    }
  },
  "commercial-property": {
    name: "Alpha Commercial Property - Multi-Risk",
    programId: "p6b9a111-84b2-4d1e-bd15-0d6789b52111",
    quotes: {
      fields: [
        { path: "proposer.companyName", type: "string", required: "yes", validation: "min 1", desc: "Corporate entity name" },
        { path: "proposer.taxId", type: "string", required: "yes", validation: "9 digits", desc: "Corporate tax identification number" },
        { path: "premises.address", type: "string", required: "yes", validation: "min 5", desc: "Physical address of insured property" },
        { path: "premises.constructionClass", type: "string", required: "yes", validation: "Class A/B/C", desc: "Fire safety building classification" },
        { path: "premises.yearBuilt", type: "number", required: "yes", validation: "1600-2027", desc: "Year the premises was built" },
        { path: "coverage.buildingSumInsured", type: "number", required: "yes", validation: "min 10000", desc: "Rebuild cost coverage limit" },
        { path: "coverage.contentsSumInsured", type: "number", required: "yes", validation: "min 0", desc: "Value limit of business contents" },
        { path: "risk.occupancyType", type: "string", required: "yes", validation: "enum", desc: "Type of business operation on site" },
        { path: "risk.hasSprinklers", type: "boolean", required: "yes", validation: "true/false", desc: "Automatic sprinkler protection flag" },
        { path: "risk.burglarAlarmGrade", type: "number", required: "yes", validation: "1-4", desc: "Security alarm standard grade" },
        { path: "standard_config.deductible", type: "number", required: "yes", validation: "min 0", desc: "Standard deductible" },
        { path: "standard_config.max_limit", type: "number", required: "yes", validation: "min 0", desc: "Standard coverage limit" },
        { path: "standard_config.payment_frequency", type: "string", required: "yes", validation: "MONTHLY/ANNUALLY", desc: "Payment frequency" },
        { path: "dynamic_config.optional_covers", type: "array", required: "no", validation: "string array", desc: "Selected optional covers" },
        { path: "dynamic_config.discretionary_discount", type: "number", required: "no", validation: "0.0-1.0", desc: "Discretionary discount" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Corporate Proposer</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Company Name</label><div class="visual-field-value">Global Logistics Corp</div></div>
            <div class="visual-field"><label>Tax ID</label><div class="visual-field-value">948201942</div></div>
          </div>
        </div>
        <div class="visual-form-group">
          <div class="visual-group-title">Premises Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Address</label><div class="visual-field-value">Unit 14, Industrial Park, Limassol</div></div>
            <div class="visual-field"><label>Construction Class</label><div class="visual-field-value">Class A (Steel & Concrete)</div></div>
            <div class="visual-field"><label>Year Built</label><div class="visual-field-value">2014</div></div>
          </div>
        </div>
        <div class="visual-form-group">
          <div class="visual-group-title">Limits & Protective Devices</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Building Limit (€)</label><div class="visual-field-value">2500000</div></div>
            <div class="visual-field"><label>Contents Limit (€)</label><div class="visual-field-value">850000</div></div>
            <div class="visual-field"><label>Occupancy</label><div class="visual-field-value">Warehouse Logistics</div></div>
            <div class="visual-field"><label>Sprinkler System</label><div class="visual-field-value">true</div></div>
            <div class="visual-field"><label>Alarm Grade</label><div class="visual-field-value">Grade 3</div></div>
          </div>
        </div>
      `,
      schema: {
        "$schema": "http://json-schema.org/draft-07/schema#",
        "title": "CommercialPropertyQuoteSchema",
        "type": "object",
        "required": ["proposer", "premises", "coverage", "risk"],
        "properties": {
          "proposer": {
            "type": "object",
            "required": ["companyName", "taxId"],
            "properties": {
              "companyName": { "type": "string" },
              "taxId": { "type": "string", "pattern": "^[0-9]{9}$" }
            }
          },
          "premises": {
            "type": "object",
            "required": ["address", "constructionClass", "yearBuilt"],
            "properties": {
              "address": { "type": "string" },
              "constructionClass": { "type": "string", "enum": ["Class A", "Class B", "Class C"] },
              "yearBuilt": { "type": "integer", "minimum": 1600 }
            }
          },
          "coverage": {
            "type": "object",
            "required": ["buildingSumInsured", "contentsSumInsured"],
            "properties": {
              "buildingSumInsured": { "type": "number", "minimum": 10000 },
              "contentsSumInsured": { "type": "number", "minimum": 0 }
            }
          },
          "risk": {
            "type": "object",
            "required": ["occupancyType", "hasSprinklers", "burglarAlarmGrade"],
            "properties": {
              "occupancyType": { "type": "string", "enum": ["Warehouse Logistics", "Retail Office", "Heavy Industrial"] },
              "hasSprinklers": { "type": "boolean" },
              "burglarAlarmGrade": { "type": "integer", "minimum": 1, "maximum": 4 }
            }
          },
          "standard_config": {
            "type": "object",
            "required": ["deductible", "max_limit", "payment_frequency"],
            "properties": {
              "deductible": { "type": "integer", "minimum": 0 },
              "max_limit": { "type": "integer", "minimum": 0 },
              "payment_frequency": { "type": "string", "enum": ["MONTHLY", "ANNUALLY"] }
            }
          },
          "dynamic_config": {
            "type": "object",
            "required": ["optional_covers", "discretionary_discount"],
            "properties": {
              "optional_covers": {
                "type": "array",
                "items": { "type": "string" }
              },
              "discretionary_discount": { "type": "number", "minimum": 0, "maximum": 1 }
            }
          }
        }
      },
      payload: {
        programId: "p6b9a111-84b2-4d1e-bd15-0d6789b52111",
        effectiveDate: "2026-03-01T00:00:00.000Z",
        quoteData: {
          proposer: {
            companyName: "Global Logistics Corp",
            taxId: "948201942"
          },
          premises: {
            address: "Unit 14, Industrial Park, Limassol",
            constructionClass: "Class A",
            yearBuilt: 2014
          },
          coverage: {
            buildingSumInsured: 2500000,
            contentsSumInsured: 850000
          },
          risk: {
            occupancyType: "Warehouse Logistics",
            hasSprinklers: true,
            burglarAlarmGrade: 3
          }
        },
        standard_config: {
          deductible: 2500,
          max_limit: 2000000,
          payment_frequency: "ANNUALLY"
        },
        dynamic_config: {
          optional_covers: ["flood_cover", "earthquake_cover"],
          discretionary_discount: 0.08
        }
      }
    },
    mta: {
      fields: [
        { path: "effectiveDate", type: "string", required: "yes", desc: "Endorsement date" },
        { path: "changes.coverage.buildingSumInsured", type: "number", required: "yes", desc: "New building limit" },
        { path: "reason", type: "string", required: "yes", desc: "MTA statement reason" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">MTA Property Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Effective Date</label><div class="visual-field-value">2026-07-01T00:00:00Z</div></div>
            <div class="visual-field"><label>Building Limit (€)</label><div class="visual-field-value">3000000</div></div>
            <div class="visual-field"><label>Reason</label><div class="visual-field-value">Expansion of mezzanine storage rack layout.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["effectiveDate", "changes", "reason"],
        "properties": {
          "effectiveDate": { "type": "string" },
          "changes": {
            "type": "object",
            "properties": {
              "coverage": {
                "type": "object",
                "properties": {
                  "buildingSumInsured": { "type": "number" }
                }
              }
            }
          },
          "reason": { "type": "string" }
        }
      },
      payload: {
        effectiveDate: "2026-07-01T00:00:00Z",
        changes: { coverage: { buildingSumInsured: 3000000 } },
        reason: "Expansion of mezzanine storage rack layout."
      }
    },
    renewals: {
      fields: [
        { path: "unchanged", type: "boolean", required: "yes", desc: "Unchanged structural risk status" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Commercial Property Renewal</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Property Structure Unchanged</label><div class="visual-field-value">true</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["unchanged"],
        "properties": { "unchanged": { "type": "boolean" } }
      },
      payload: { unchanged: true }
    },
    claims: {
      fields: [
        { path: "policyId", type: "string", required: "yes", desc: "Bound policy UUID" },
        { path: "intake.incident.type", type: "string", required: "yes", desc: "fire/flood/break-in" },
        { path: "intake.incident.description", type: "string", required: "yes", desc: "Detailed loss description" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Commercial Property Loss</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Policy ID</label><div class="visual-field-value">p6b9a111-84b2-4d1e-bd15-0d6789b52111</div></div>
            <div class="visual-field"><label>Loss Type</label><div class="visual-field-value">fire</div></div>
            <div class="visual-field"><label>Description</label><div class="visual-field-value">Electrical short circuit in server room triggers fire sprinkler system.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["policyId", "intake"],
        "properties": {
          "policyId": { "type": "string" },
          "intake": {
            "type": "object",
            "required": ["incident"],
            "properties": {
              "incident": {
                "type": "object",
                "required": ["type", "description"],
                "properties": {
                  "type": { "type": "string", "enum": ["fire", "flood", "break-in"] },
                  "description": { "type": "string" }
                }
              }
            }
          }
        }
      },
      payload: {
        policyId: "p6b9a111-84b2-4d1e-bd15-0d6789b52111",
        intake: {
          incident: {
            type: "fire",
            description: "Electrical short circuit in server room triggers fire sprinkler system."
          }
        }
      }
    }
  },
  "homeowners": {
    name: "Alpha Homeowners / Household",
    programId: "h7b2a222-84b2-4d1e-bd15-0d6789b52222",
    quotes: {
      fields: [
        { path: "proposer.firstName", type: "string", required: "yes", desc: "Policyholder first name" },
        { path: "proposer.lastName", type: "string", required: "yes", desc: "Policyholder last name" },
        { path: "property.address", type: "string", required: "yes", desc: "Risk address location" },
        { path: "property.yearBuilt", type: "number", required: "yes", desc: "Year built of residence" },
        { path: "property.wallType", type: "string", required: "yes", desc: "Brick/Stone/Timber framing" },
        { path: "property.roofType", type: "string", required: "yes", desc: "Tile/Slate/Thatch" },
        { path: "coverage.buildingRebuildValue", type: "number", required: "yes", desc: "Rebuild cost estimation limit" },
        { path: "coverage.contentsValue", type: "number", required: "yes", desc: "Value of home contents" },
        { path: "risk.hasSmokeAlarms", type: "boolean", required: "yes", desc: "Smoke detector check" },
        { path: "standard_config.deductible", type: "number", required: "yes", validation: "min 0", desc: "Standard deductible" },
        { path: "standard_config.max_limit", type: "number", required: "yes", validation: "min 0", desc: "Standard coverage limit" },
        { path: "standard_config.payment_frequency", type: "string", required: "yes", validation: "MONTHLY/ANNUALLY", desc: "Payment frequency" },
        { path: "dynamic_config.optional_covers", type: "array", required: "no", validation: "string array", desc: "Selected optional covers" },
        { path: "dynamic_config.discretionary_discount", type: "number", required: "no", validation: "0.0-1.0", desc: "Discretionary discount" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-form-group-title">Homeowner Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Owner Name</label><div class="visual-field-value">John Watson</div></div>
            <div class="visual-field"><label>Address</label><div class="visual-field-value">221B Baker Street, London</div></div>
          </div>
        </div>
        <div class="visual-form-group">
          <div class="visual-group-title">Property Construction</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Year Built</label><div class="visual-field-value">1885</div></div>
            <div class="visual-field"><label>Wall Construction</label><div class="visual-field-value">Brick</div></div>
            <div class="visual-field"><label>Roof Type</label><div class="visual-field-value">Slate</div></div>
          </div>
        </div>
        <div class="visual-form-group">
          <div class="visual-group-title">Coverages & Protections</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Rebuild Cost (€)</label><div class="visual-field-value">450000</div></div>
            <div class="visual-field"><label>Contents Limit (€)</label><div class="visual-field-value">120000</div></div>
            <div class="visual-field"><label>Smoke Alarms</label><div class="visual-field-value">true</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["proposer", "property", "coverage", "risk"],
        "properties": {
          "proposer": {
            "type": "object",
            "required": ["firstName", "lastName"],
            "properties": { "firstName": { "type": "string" }, "lastName": { "type": "string" } }
          },
          "property": {
            "type": "object",
            "required": ["address", "yearBuilt", "wallType", "roofType"],
            "properties": {
              "address": { "type": "string" },
              "yearBuilt": { "type": "integer" },
              "wallType": { "type": "string", "enum": ["Brick", "Stone", "Timber"] },
              "roofType": { "type": "string", "enum": ["Tile", "Slate", "Thatch"] }
            }
          },
          "coverage": {
            "type": "object",
            "required": ["buildingRebuildValue", "contentsValue"],
            "properties": {
              "buildingRebuildValue": { "type": "number" },
              "contentsValue": { "type": "number" }
            }
          },
          "risk": {
            "type": "object",
            "required": ["hasSmokeAlarms"],
            "properties": { "hasSmokeAlarms": { "type": "boolean" } }
          },
          "standard_config": {
            "type": "object",
            "required": ["deductible", "max_limit", "payment_frequency"],
            "properties": {
              "deductible": { "type": "integer", "minimum": 0 },
              "max_limit": { "type": "integer", "minimum": 0 },
              "payment_frequency": { "type": "string", "enum": ["MONTHLY", "ANNUALLY"] }
            }
          },
          "dynamic_config": {
            "type": "object",
            "required": ["optional_covers", "discretionary_discount"],
            "properties": {
              "optional_covers": {
                "type": "array",
                "items": { "type": "string" }
              },
              "discretionary_discount": { "type": "number", "minimum": 0, "maximum": 1 }
            }
          }
        }
      },
      payload: {
        programId: "h7b2a222-84b2-4d1e-bd15-0d6789b52222",
        effectiveDate: "2026-03-01T00:00:00.000Z",
        quoteData: {
          proposer: { firstName: "John", lastName: "Watson" },
          property: { address: "221B Baker Street, London", yearBuilt: 1885, wallType: "Brick", roofType: "Slate" },
          coverage: { buildingRebuildValue: 450000, contentsValue: 120000 },
          risk: { hasSmokeAlarms: true }
        },
        standard_config: {
          deductible: 250,
          max_limit: 500000,
          payment_frequency: "ANNUALLY"
        },
        dynamic_config: {
          optional_covers: ["valuable_possessions_cover", "accidental_damage_cover"],
          discretionary_discount: 0.05
        }
      }
    },
    mta: {
      fields: [
        { path: "effectiveDate", type: "string", required: "yes", desc: "MTA effective date" },
        { path: "changes.coverage.contentsValue", type: "number", required: "yes", desc: "New contents limit" },
        { path: "reason", type: "string", required: "yes", desc: "Adjustment reason statement" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Homeowners MTA Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Effective Date</label><div class="visual-field-value">2026-08-01T00:00:00Z</div></div>
            <div class="visual-field"><label>Contents Limit (€)</label><div class="visual-field-value">150000</div></div>
            <div class="visual-field"><label>Reason</label><div class="visual-field-value">Acquired antique artwork and valuables.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["effectiveDate", "changes", "reason"],
        "properties": {
          "effectiveDate": { "type": "string" },
          "changes": {
            "type": "object",
            "properties": {
              "coverage": {
                "type": "object",
                "properties": { "contentsValue": { "type": "number" } }
              }
            }
          },
          "reason": { "type": "string" }
        }
      },
      payload: {
        effectiveDate: "2026-08-01T00:00:00Z",
        changes: { coverage: { contentsValue: 150000 } },
        reason: "Acquired antique artwork and valuables."
      }
    },
    renewals: {
      fields: [
        { path: "unchanged", type: "boolean", required: "yes", desc: "Unchanged household declarations" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Homeowners Renewal</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Household Profile Unchanged</label><div class="visual-field-value">true</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["unchanged"],
        "properties": { "unchanged": { "type": "boolean" } }
      },
      payload: { unchanged: true }
    },
    claims: {
      fields: [
        { path: "policyId", type: "string", required: "yes", desc: "Bound policy ID" },
        { path: "intake.incident.type", type: "string", required: "yes", desc: "storm/flood/theft" },
        { path: "intake.incident.description", type: "string", required: "yes", desc: "loss details" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Homeowners Loss Report</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Policy ID</label><div class="visual-field-value">h7b2a222-84b2-4d1e-bd15-0d6789b52222</div></div>
            <div class="visual-field"><label>Incident Type</label><div class="visual-field-value">storm</div></div>
            <div class="visual-field"><label>Description</label><div class="visual-field-value">Windstorm damages slate roofing, causing minor ceiling leak.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["policyId", "intake"],
        "properties": {
          "policyId": { "type": "string" },
          "intake": {
            "type": "object",
            "required": ["incident"],
            "properties": {
              "incident": {
                "type": "object",
                "required": ["type", "description"],
                "properties": {
                  "type": { "type": "string", "enum": ["storm", "flood", "theft"] },
                  "description": { "type": "string" }
                }
              }
            }
          }
        }
      },
      payload: {
        policyId: "h7b2a222-84b2-4d1e-bd15-0d6789b52222",
        intake: {
          incident: {
            type: "storm",
            description: "Windstorm damages slate roofing, causing minor ceiling leak."
          }
        }
      }
    }
  },
  "travel": {
    name: "Alpha Travel Insurance",
    programId: "t5c9a333-84b2-4d1e-bd15-0d6789b52333",
    quotes: {
      fields: [
        { path: "proposer.firstName", type: "string", required: "yes", desc: "Traveler first name" },
        { path: "proposer.lastName", type: "string", required: "yes", desc: "Traveler last name" },
        { path: "travel.region", type: "string", required: "yes", desc: "Europe/Worldwide geographical scope" },
        { path: "travel.startDate", type: "string", required: "yes", desc: "Trip departure date" },
        { path: "travel.endDate", type: "string", required: "yes", desc: "Trip return date" },
        { path: "travel.hasPreExistingMedical", type: "boolean", required: "yes", desc: "Medical conditions indicator" },
        { path: "standard_config.deductible", type: "number", required: "yes", validation: "min 0", desc: "Standard deductible" },
        { path: "standard_config.max_limit", type: "number", required: "yes", validation: "min 0", desc: "Standard coverage limit" },
        { path: "standard_config.payment_frequency", type: "string", required: "yes", validation: "MONTHLY/ANNUALLY", desc: "Payment frequency" },
        { path: "dynamic_config.optional_covers", type: "array", required: "no", validation: "string array", desc: "Selected optional covers" },
        { path: "dynamic_config.discretionary_discount", type: "number", required: "no", validation: "0.0-1.0", desc: "Discretionary discount" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Traveler Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>First Name</label><div class="visual-field-value">David</div></div>
            <div class="visual-field"><label>Last Name</label><div class="visual-field-value">Miller</div></div>
          </div>
        </div>
        <div class="visual-form-group">
          <div class="visual-group-title">Trip Schedule</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Region</label><div class="visual-field-value">Europe</div></div>
            <div class="visual-field"><label>Start Date</label><div class="visual-field-value">2026-07-01</div></div>
            <div class="visual-field"><label>End Date</label><div class="visual-field-value">2026-07-14</div></div>
            <div class="visual-field"><label>Pre-Existing Medical</label><div class="visual-field-value">false</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["proposer", "travel", "standard_config", "dynamic_config"],
        "properties": {
          "proposer": {
            "type": "object",
            "required": ["firstName", "lastName", "email"],
            "properties": {
              "firstName": { "type": "string" },
              "lastName": { "type": "string" },
              "email": { "type": "string", "format": "email" }
            }
          },
          "travel": {
            "type": "object",
            "required": ["age", "region", "hasPreExistingMedical"],
            "properties": {
              "age": { "type": "integer" },
              "region": { "type": "string", "enum": ["Europe", "Worldwide", "WorldwideExcludingUS"] },
              "hasPreExistingMedical": { "type": "boolean" }
            }
          },
          "standard_config": {
            "type": "object",
            "required": ["deductible", "max_limit", "payment_frequency"],
            "properties": {
              "deductible": { "type": "integer", "minimum": 0 },
              "max_limit": { "type": "integer", "minimum": 0 },
              "payment_frequency": { "type": "string", "enum": ["MONTHLY", "ANNUALLY"] }
            }
          },
          "dynamic_config": {
            "type": "object",
            "required": ["optional_covers", "discretionary_discount"],
            "properties": {
              "optional_covers": {
                "type": "array",
                "items": { "type": "string" }
              },
              "discretionary_discount": { "type": "number", "minimum": 0, "maximum": 1 }
            }
          }
        }
      },
      payload: {
        programId: "t5c9a333-84b2-4d1e-bd15-0d6789b52444",
        effectiveDate: "2026-03-01T00:00:00.000Z",
        quoteData: {
          proposer: {
            firstName: "John",
            lastName: "Smith",
            email: "john.smith@travelers.co.uk"
          },
          travel: {
            age: 34,
            region: "Worldwide",
            hasPreExistingMedical: false
          }
        },
        standard_config: {
          deductible: 50,
          max_limit: 100000,
          payment_frequency: "ANNUALLY"
        },
        dynamic_config: {
          optional_covers: ["baggage_cover", "winter_sports_cover"],
          discretionary_discount: 0.05
        }
      }
    },
    mta: {
      fields: [
        { path: "effectiveDate", type: "string", required: "yes", validation: "ISO Date", desc: "MTA active start date" },
        { path: "changes.region", type: "string", required: "yes", validation: "enum", desc: "Updated travel region" },
        { path: "reason", type: "string", required: "yes", validation: "min 1", desc: "servicing explanation" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Endorsement Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Effective Date</label><div class="visual-field-value">2026-09-01T00:00:00Z</div></div>
            <div class="visual-field"><label>Travel Region</label><div class="visual-field-value">WorldwideIncludingUS</div></div>
            <div class="visual-field"><label>Reason</label><div class="visual-field-value">Adding business trip to New York.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "properties": {
          "effectiveDate": { "type": "string" },
          "changes": {
            "type": "object",
            "properties": {
              "region": { "type": "string" }
            }
          },
          "reason": { "type": "string" }
        }
      },
      payload: {
        effectiveDate: "2026-09-01T00:00:00Z",
        changes: { region: "WorldwideIncludingUS" },
        reason: "Adding business trip to New York."
      }
    },
    renewals: {
      fields: [
        { path: "unchanged", type: "boolean", required: "yes", validation: "true/false", desc: "Medical and travel habits unchanged" },
        { path: "notes", type: "string", required: "no", validation: "string", desc: "servicing notes" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Travel Renewal Declarations</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Medical Conditions Unchanged</label><div class="visual-field-value">true</div></div>
            <div class="visual-field"><label>Operator Notes</label><div class="visual-field-value">No changes to medical history.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "properties": {
          "unchanged": { "type": "boolean" },
          "notes": { "type": "string" }
        }
      },
      payload: {
        unchanged: true,
        notes: "No changes to medical history."
      }
    },
    claims: {
      fields: [
        { path: "policyId", type: "string", required: "yes", validation: "UUID", desc: "Active travel policy ID" },
        { path: "intake.incident.type", type: "string", required: "yes", validation: "enum", desc: "Type of loss incident" },
        { path: "intake.incident.date", type: "string", required: "yes", validation: "ISO Date", desc: "Incident date" },
        { path: "intake.incident.description", type: "string", required: "yes", validation: "min 10", desc: "narrative" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Incident Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Policy ID</label><div class="visual-field-value">t5c9a333-84b2-4d1e-bd15-0d6789b52444</div></div>
            <div class="visual-field"><label>Incident Type</label><div class="visual-field-value">baggage_delay</div></div>
            <div class="visual-field"><label>Incident Date</label><div class="visual-field-value">2026-05-30</div></div>
            <div class="visual-field"><label>Incident Description</label><div class="visual-field-value">Flight transfer delayed bag delivery in Lisbon by 36 hours.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "properties": {
          "policyId": { "type": "string" },
          "intake": {
            "type": "object",
            "properties": {
              "incident": {
                "type": "object",
                "properties": {
                  "type": { "type": "string" },
                  "date": { "type": "string" },
                  "description": { "type": "string" }
                }
              }
            }
          }
        }
      },
      payload: {
        policyId: "t5c9a333-84b2-4d1e-bd15-0d6789b52444",
        intake: {
          incident: {
            type: "baggage_delay",
            date: "2026-05-30",
            description: "Flight transfer delayed bag delivery in Lisbon by 36 hours."
          }
        }
      }
    }
  },
  "pet": {
    name: "Alpha Pet Insurance",
    programId: "p5c9a333-84b2-4d1e-bd15-0d6789b52444",
    quotes: {
      fields: [
        { path: "proposer.firstName", type: "string", required: "yes", desc: "Owner first name" },
        { path: "proposer.lastName", type: "string", required: "yes", desc: "Owner last name" },
        { path: "pet.type", type: "string", required: "yes", desc: "Dog/Cat classification" },
        { path: "pet.breed", type: "string", required: "yes", desc: "Specific breed type" },
        { path: "pet.ageYears", type: "number", required: "yes", desc: "Age of pet in years" },
        { path: "coverage.veterinaryLimit", type: "number", required: "yes", desc: "Annual maximum vet limits" },
        { path: "risk.hasPreExistingMedical", type: "boolean", required: "yes", desc: "Prior pet illness history" },
        { path: "standard_config.deductible", type: "number", required: "yes", validation: "min 0", desc: "Standard deductible" },
        { path: "standard_config.max_limit", type: "number", required: "yes", validation: "min 0", desc: "Standard coverage limit" },
        { path: "standard_config.payment_frequency", type: "string", required: "yes", validation: "MONTHLY/ANNUALLY", desc: "Payment frequency" },
        { path: "dynamic_config.optional_covers", type: "array", required: "no", validation: "string array", desc: "Selected optional covers" },
        { path: "dynamic_config.discretionary_discount", type: "number", required: "no", validation: "0.0-1.0", desc: "Discretionary discount" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Pet & Owner Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Owner</label><div class="visual-field-value">Sarah Jenkins</div></div>
            <div class="visual-field"><label>Pet Name</label><div class="visual-field-value">Bella</div></div>
          </div>
        </div>
        <div class="visual-form-group">
          <div class="visual-group-title">Pet Specs & Coverage</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Type</label><div class="visual-field-value">Dog</div></div>
            <div class="visual-field"><label>Breed</label><div class="visual-field-value">Golden Retriever</div></div>
            <div class="visual-field"><label>Age (Years)</label><div class="visual-field-value">3</div></div>
            <div class="visual-field"><label>Annual Vet Limit (£)</label><div class="visual-field-value">7500</div></div>
            <div class="visual-field"><label>Pre-Existing Medical</label><div class="visual-field-value">false</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["proposer", "pet", "coverage", "risk"],
        "properties": {
          "proposer": {
            "type": "object",
            "required": ["firstName", "lastName"],
            "properties": { "firstName": { "type": "string" }, "lastName": { "type": "string" } }
          },
          "pet": {
            "type": "object",
            "required": ["type", "breed", "ageYears"],
            "properties": {
              "type": { "type": "string", "enum": ["Dog", "Cat"] },
              "breed": { "type": "string" },
              "ageYears": { "type": "integer" }
            }
          },
          "coverage": {
            "type": "object",
            "required": ["veterinaryLimit"],
            "properties": { "veterinaryLimit": { "type": "number", "enum": [3000, 5000, 7500] } }
          },
          "risk": {
            "type": "object",
            "required": ["hasPreExistingMedical"],
            "properties": { "hasPreExistingMedical": { "type": "boolean" } }
          },
          "standard_config": {
            "type": "object",
            "required": ["deductible", "max_limit", "payment_frequency"],
            "properties": {
              "deductible": { "type": "integer", "minimum": 0 },
              "max_limit": { "type": "integer", "minimum": 0 },
              "payment_frequency": { "type": "string", "enum": ["MONTHLY", "ANNUALLY"] }
            }
          },
          "dynamic_config": {
            "type": "object",
            "required": ["optional_covers", "discretionary_discount"],
            "properties": {
              "optional_covers": {
                "type": "array",
                "items": { "type": "string" }
              },
              "discretionary_discount": { "type": "number", "minimum": 0, "maximum": 1 }
            }
          }
        }
      },
      payload: {
        programId: "p5c9a333-84b2-4d1e-bd15-0d6789b52444",
        effectiveDate: "2026-03-01T00:00:00.000Z",
        quoteData: {
          proposer: { firstName: "Sarah", lastName: "Jenkins" },
          pet: { type: "Dog", breed: "Golden Retriever", ageYears: 3 },
          coverage: { veterinaryLimit: 7500 },
          risk: { hasPreExistingMedical: false }
        },
        standard_config: {
          deductible: 100,
          max_limit: 7500,
          payment_frequency: "MONTHLY"
        },
        dynamic_config: {
          optional_covers: ["dental_illness_cover", "accident_only_cover"],
          discretionary_discount: 0.05
        }
      }
    },
    mta: {
      fields: [
        { path: "effectiveDate", type: "string", required: "yes", desc: "MTA effective date" },
        { path: "changes.coverage.veterinaryLimit", type: "number", required: "yes", desc: "New veterinary limit" },
        { path: "reason", type: "string", required: "yes", desc: "MTA reason" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Pet Cover MTA Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Effective Date</label><div class="visual-field-value">2026-09-01T00:00:00Z</div></div>
            <div class="visual-field"><label>Annual Vet Limit (£)</label><div class="visual-field-value">5000</div></div>
            <div class="visual-field"><label>Reason</label><div class="visual-field-value">Downgraded vet cover tier to save premium costs.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["effectiveDate", "changes", "reason"],
        "properties": {
          "effectiveDate": { "type": "string" },
          "changes": {
            "type": "object",
            "properties": {
              "coverage": {
                "type": "object",
                "properties": { "veterinaryLimit": { "type": "number" } }
              }
            }
          },
          "reason": { "type": "string" }
        }
      },
      payload: {
        effectiveDate: "2026-09-01T00:00:00Z",
        changes: { coverage: { veterinaryLimit: 5000 } },
        reason: "Downgraded vet cover tier to save premium costs."
      }
    },
    renewals: {
      fields: [
        { path: "unchanged", type: "boolean", required: "yes", desc: "Unchanged pet declarations" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Pet Cover Renewal</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Health & Treatment Profile Unchanged</label><div class="visual-field-value">true</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["unchanged"],
        "properties": { "unchanged": { "type": "boolean" } }
      },
      payload: { unchanged: true }
    },
    claims: {
      fields: [
        { path: "policyId", type: "string", required: "yes", desc: "Pet policy UUID" },
        { path: "intake.incident.type", type: "string", required: "yes", desc: "injury/illness" },
        { path: "intake.incident.description", type: "string", required: "yes", desc: "clinical notes" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Pet Veterinary Claim</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Policy ID</label><div class="visual-field-value">p5c9a333-84b2-4d1e-bd15-0d6789b52444</div></div>
            <div class="visual-field"><label>Incident Type</label><div class="visual-field-value">injury</div></div>
            <div class="visual-field"><label>Description</label><div class="visual-field-value">Ligament rupture in hind leg requiring orthopedic surgery.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["policyId", "intake"],
        "properties": {
          "policyId": { "type": "string" },
          "intake": {
            "type": "object",
            "required": ["incident"],
            "properties": {
              "incident": {
                "type": "object",
                "required": ["type", "description"],
                "properties": {
                  "type": { "type": "string", "enum": ["injury", "illness"] },
                  "description": { "type": "string" }
                }
              }
            }
          }
        }
      },
      payload: {
        policyId: "p5c9a333-84b2-4d1e-bd15-0d6789b52444",
        intake: {
          incident: {
            type: "injury",
            description: "Ligament rupture in hind leg requiring orthopedic surgery."
          }
        }
      }
    }
  },
  "builders-risk": {
    name: "Alpha Builder’s Risk / Construction",
    programId: "b6c9a333-84b2-4d1e-bd15-0d6789b52555",
    quotes: {
      fields: [
        { path: "proposer.companyName", type: "string", required: "yes", desc: "Contractor name" },
        { path: "project.projectName", type: "string", required: "yes", desc: "Construction project title" },
        { path: "project.contractValue", type: "number", required: "yes", desc: "Total works budget value" },
        { path: "project.durationMonths", type: "number", required: "yes", desc: "Contract works duration" },
        { path: "site.address", type: "string", required: "yes", desc: "Construction site address" },
        { path: "site.excavationDepth", type: "number", required: "yes", desc: "Maximum excavation depth" },
        { path: "risk.constructionType", type: "string", required: "yes", desc: "Structural frame materials type" },
        { path: "standard_config.deductible", type: "number", required: "yes", validation: "min 0", desc: "Standard deductible" },
        { path: "standard_config.max_limit", type: "number", required: "yes", validation: "min 0", desc: "Standard coverage limit" },
        { path: "standard_config.payment_frequency", type: "string", required: "yes", validation: "MONTHLY/ANNUALLY", desc: "Payment frequency" },
        { path: "dynamic_config.optional_covers", type: "array", required: "no", validation: "string array", desc: "Selected optional covers" },
        { path: "dynamic_config.discretionary_discount", type: "number", required: "no", validation: "0.0-1.0", desc: "Discretionary discount" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Contractor & Project Specs</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Contractor Name</label><div class="visual-field-value">BuildCorp Developers Ltd</div></div>
            <div class="visual-field"><label>Project Name</label><div class="visual-field-value">Limassol Marina Residential Block C</div></div>
            <div class="visual-field"><label>Contract Value (€)</label><div class="visual-field-value">12000000</div></div>
            <div class="visual-field"><label>Duration (Months)</label><div class="visual-field-value">18</div></div>
          </div>
        </div>
        <div class="visual-form-group">
          <div class="visual-group-title">Site Specifications & Risk</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Site Address</label><div class="visual-field-value">Marina Sector 4, Limassol</div></div>
            <div class="visual-field"><label>Excavation Depth (m)</label><div class="visual-field-value">5</div></div>
            <div class="visual-field"><label>Structure Frame Type</label><div class="visual-field-value">Reinforced Concrete</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["proposer", "project", "site", "risk"],
        "properties": {
          "proposer": {
            "type": "object",
            "required": ["companyName"],
            "properties": { "companyName": { "type": "string" } }
          },
          "project": {
            "type": "object",
            "required": ["projectName", "contractValue", "durationMonths"],
            "properties": {
              "projectName": { "type": "string" },
              "contractValue": { "type": "number", "minimum": 10000 },
              "durationMonths": { "type": "integer" }
            }
          },
          "site": {
            "type": "object",
            "required": ["address", "excavationDepth"],
            "properties": {
              "address": { "type": "string" },
              "excavationDepth": { "type": "number", "minimum": 0 }
            }
          },
          "risk": {
            "type": "object",
            "required": ["constructionType"],
            "properties": { "constructionType": { "type": "string", "enum": ["Reinforced Concrete", "Steel Frame", "Timber Frame"] } }
          },
          "standard_config": {
            "type": "object",
            "required": ["deductible", "max_limit", "payment_frequency"],
            "properties": {
              "deductible": { "type": "integer", "minimum": 0 },
              "max_limit": { "type": "integer", "minimum": 0 },
              "payment_frequency": { "type": "string", "enum": ["MONTHLY", "ANNUALLY"] }
            }
          },
          "dynamic_config": {
            "type": "object",
            "required": ["optional_covers", "discretionary_discount"],
            "properties": {
              "optional_covers": {
                "type": "array",
                "items": { "type": "string" }
              },
              "discretionary_discount": { "type": "number", "minimum": 0, "maximum": 1 }
            }
          }
        }
      },
      payload: {
        programId: "b6c9a333-84b2-4d1e-bd15-0d6789b52555",
        effectiveDate: "2026-03-01T00:00:00.000Z",
        quoteData: {
          proposer: { companyName: "BuildCorp Developers Ltd" },
          project: { projectName: "Limassol Marina Residential Block C", contractValue: 12000000, durationMonths: 18 },
          site: { address: "Marina Sector 4, Limassol", excavationDepth: 5 },
          risk: { constructionType: "Reinforced Concrete" }
        },
        standard_config: {
          deductible: 10000,
          max_limit: 15000000,
          payment_frequency: "ANNUALLY"
        },
        dynamic_config: {
          optional_covers: ["delay_in_start_up", "third_party_liability"],
          discretionary_discount: 0.10
        }
      }
    },
    mta: {
      fields: [
        { path: "effectiveDate", type: "string", required: "yes", desc: "MTA effective date" },
        { path: "changes.project.contractValue", type: "number", required: "yes", desc: "Updated works value limit" },
        { path: "reason", type: "string", required: "yes", desc: "MTA reason details" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Builder's Risk MTA Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Effective Date</label><div class="visual-field-value">2026-10-01T00:00:00Z</div></div>
            <div class="visual-field"><label>Contract Value (€)</label><div class="visual-field-value">13500000</div></div>
            <div class="visual-field"><label>Reason</label><div class="visual-field-value">Scope addition of structural basement floor layout.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["effectiveDate", "changes", "reason"],
        "properties": {
          "effectiveDate": { "type": "string" },
          "changes": {
            "type": "object",
            "properties": {
              "project": {
                "type": "object",
                "properties": { "contractValue": { "type": "number" } }
              }
            }
          },
          "reason": { "type": "string" }
        }
      },
      payload: {
        effectiveDate: "2026-10-01T00:00:00Z",
        changes: { project: { contractValue: 13500000 } },
        reason: "Scope addition of structural basement floor layout."
      }
    },
    renewals: {
      fields: [
        { path: "unchanged", type: "boolean", required: "yes", desc: "Unchanged project risk status" },
        { path: "notes", type: "string", required: "no", desc: "Project delay explanations" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Builder's Risk Renewal</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Project Risk Unchanged</label><div class="visual-field-value">false</div></div>
            <div class="visual-field"><label>Project Delay Note</label><div class="visual-field-value">Project extended by 3 months due to excavation delays.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["unchanged"],
        "properties": { "unchanged": { "type": "boolean" }, "notes": { "type": "string" } }
      },
      payload: { unchanged: false, notes: "Project extended by 3 months due to excavation delays." }
    },
    claims: {
      fields: [
        { path: "policyId", type: "string", required: "yes", desc: "Construction policy UUID" },
        { path: "intake.incident.type", type: "string", required: "yes", desc: "collapse/theft/windstorm" },
        { path: "intake.incident.description", type: "string", required: "yes", desc: "loss details" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Builder's Risk Claim Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Policy ID</label><div class="visual-field-value">b6c9a333-84b2-4d1e-bd15-0d6789b52555</div></div>
            <div class="visual-field"><label>Incident Type</label><div class="visual-field-value">collapse</div></div>
            <div class="visual-field"><label>Description</label><div class="visual-field-value">Scaffolding collapse during strong winds damaging primary block masonry.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["policyId", "intake"],
        "properties": {
          "policyId": { "type": "string" },
          "intake": {
            "type": "object",
            "required": ["incident"],
            "properties": {
              "incident": {
                "type": "object",
                "required": ["type", "description"],
                "properties": {
                  "type": { "type": "string", "enum": ["collapse", "theft", "windstorm"] },
                  "description": { "type": "string" }
                }
              }
            }
          }
        }
      },
      payload: {
        policyId: "b6c9a333-84b2-4d1e-bd15-0d6789b52555",
        intake: {
          incident: {
            type: "collapse",
            description: "Scaffolding collapse during strong winds damaging primary block masonry."
          }
        }
      }
    }
  },
  "professional-indemnity": {
    name: "Alpha Professional Indemnity / E&O",
    programId: "p7d9a111-84b2-4d1e-bd15-0d6789b52666",
    quotes: {
      fields: [
        { path: "proposer.firmName", type: "string", required: "yes", desc: "Incorporated firm name" },
        { path: "proposer.profession", type: "string", required: "yes", desc: "IT Consultant/Lawyer/Engineer" },
        { path: "proposer.annualRevenue", type: "number", required: "yes", desc: "Annual gross revenue (€)" },
        { path: "coverage.limitOfIndemnity", type: "number", required: "yes", desc: "Liability cover limit" },
        { path: "coverage.deductible", type: "number", required: "yes", desc: "Self-insured deductible limit" },
        { path: "risk.retroactiveDate", type: "string", required: "yes", desc: "Retroactive coverage date" },
        { path: "standard_config.deductible", type: "number", required: "yes", validation: "min 0", desc: "Standard deductible" },
        { path: "standard_config.max_limit", type: "number", required: "yes", validation: "min 0", desc: "Standard coverage limit" },
        { path: "standard_config.payment_frequency", type: "string", required: "yes", validation: "MONTHLY/ANNUALLY", desc: "Payment frequency" },
        { path: "dynamic_config.optional_covers", type: "array", required: "no", validation: "string array", desc: "Selected optional covers" },
        { path: "dynamic_config.discretionary_discount", type: "number", required: "no", validation: "0.0-1.0", desc: "Discretionary discount" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Firm & Profession Profile</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Firm Name</label><div class="visual-field-value">Cyberdyne Consulting Ltd</div></div>
            <div class="visual-field"><label>Profession</label><div class="visual-field-value">IT Consultant</div></div>
            <div class="visual-field"><label>Annual Revenue (€)</label><div class="visual-field-value">1500000</div></div>
          </div>
        </div>
        <div class="visual-form-group">
          <div class="visual-group-title">Coverage Limits & Retroactive Date</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Limit of Indemnity (€)</label><div class="visual-field-value">2000000</div></div>
            <div class="visual-field"><label>Deductible (€)</label><div class="visual-field-value">10000</div></div>
            <div class="visual-field"><label>Retroactive Date</label><div class="visual-field-value">2020-01-01</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["proposer", "coverage", "risk"],
        "properties": {
          "proposer": {
            "type": "object",
            "required": ["firmName", "profession", "annualRevenue"],
            "properties": {
              "firmName": { "type": "string" },
              "profession": { "type": "string", "enum": ["IT Consultant", "Lawyer", "Engineer"] },
              "annualRevenue": { "type": "number" }
            }
          },
          "coverage": {
            "type": "object",
            "required": ["limitOfIndemnity", "deductible"],
            "properties": {
              "limitOfIndemnity": { "type": "number", "enum": [1000000, 2000000, 5000000] },
              "deductible": { "type": "number" }
            }
          },
          "risk": {
            "type": "object",
            "required": ["retroactiveDate"],
            "properties": { "retroactiveDate": { "type": "string", "format": "date" } }
          },
          "standard_config": {
            "type": "object",
            "required": ["deductible", "max_limit", "payment_frequency"],
            "properties": {
              "deductible": { "type": "integer", "minimum": 0 },
              "max_limit": { "type": "integer", "minimum": 0 },
              "payment_frequency": { "type": "string", "enum": ["MONTHLY", "ANNUALLY"] }
            }
          },
          "dynamic_config": {
            "type": "object",
            "required": ["optional_covers", "discretionary_discount"],
            "properties": {
              "optional_covers": {
                "type": "array",
                "items": { "type": "string" }
              },
              "discretionary_discount": { "type": "number", "minimum": 0, "maximum": 1 }
            }
          }
        }
      },
      payload: {
        programId: "p7d9a111-84b2-4d1e-bd15-0d6789b52666",
        effectiveDate: "2026-03-01T00:00:00.000Z",
        quoteData: {
          proposer: { firmName: "Cyberdyne Consulting Ltd", profession: "IT Consultant", annualRevenue: 1500000 },
          coverage: { limitOfIndemnity: 2000000, deductible: 10000 },
          risk: { retroactiveDate: "2020-01-01" }
        },
        standard_config: {
          deductible: 10000,
          max_limit: 5000000,
          payment_frequency: "ANNUALLY"
        },
        dynamic_config: {
          optional_covers: ["cyber_liability_add_on", "retroactive_cover"],
          discretionary_discount: 0.15
        }
      }
    },
    mta: {
      fields: [
        { path: "effectiveDate", type: "string", required: "yes", desc: "MTA date" },
        { path: "changes.proposer.annualRevenue", type: "number", required: "yes", desc: "Updated revenue limit" },
        { path: "reason", type: "string", required: "yes", desc: "Endorsement statement" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">PI MTA details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Effective Date</label><div class="visual-field-value">2026-11-01T00:00:00Z</div></div>
            <div class="visual-field"><label>Annual Revenue (€)</label><div class="visual-field-value">2000000</div></div>
            <div class="visual-field"><label>Reason</label><div class="visual-field-value">Acquired new consulting division contract.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["effectiveDate", "changes", "reason"],
        "properties": {
          "effectiveDate": { "type": "string" },
          "changes": {
            "type": "object",
            "properties": {
              "proposer": {
                "type": "object",
                "properties": { "annualRevenue": { "type": "number" } }
              }
            }
          },
          "reason": { "type": "string" }
        }
      },
      payload: {
        effectiveDate: "2026-11-01T00:00:00Z",
        changes: { proposer: { annualRevenue: 2000000 } },
        reason: "Acquired new consulting division contract."
      }
    },
    renewals: {
      fields: [
        { path: "unchanged", type: "boolean", required: "yes", desc: "Unchanged professional indemnity declarations" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Professional Indemnity Renewal</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Professional Risk Profile Unchanged</label><div class="visual-field-value">true</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["unchanged"],
        "properties": { "unchanged": { "type": "boolean" } }
      },
      payload: { unchanged: true }
    },
    claims: {
      fields: [
        { path: "policyId", type: "string", required: "yes", desc: "Liability policy UUID" },
        { path: "intake.incident.type", type: "string", required: "yes", desc: "negligence/breach/data-breach" },
        { path: "intake.incident.description", type: "string", required: "yes", desc: "liability action narrative" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Professional Indemnity Liability Claim</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Policy ID</label><div class="visual-field-value">p7d9a111-84b2-4d1e-bd15-0d6789b52666</div></div>
            <div class="visual-field"><label>Incident Type</label><div class="visual-field-value">negligence</div></div>
            <div class="visual-field"><label>Description</label><div class="visual-field-value">Client alleges system downtime caused by software updates led to loss of business revenue.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["policyId", "intake"],
        "properties": {
          "policyId": { "type": "string" },
          "intake": {
            "type": "object",
            "required": ["incident"],
            "properties": {
              "incident": {
                "type": "object",
                "required": ["type", "description"],
                "properties": {
                  "type": { "type": "string", "enum": ["negligence", "breach", "data-breach"] },
                  "description": { "type": "string" }
                }
              }
            }
          }
        }
      },
      payload: {
        policyId: "p7d9a111-84b2-4d1e-bd15-0d6789b52666",
        intake: {
          incident: {
            type: "negligence",
            description: "Client alleges system downtime caused by software updates led to loss of business revenue."
          }
        }
      }
    }
  },
  "health-accident": {
    name: "Alpha Health / Accident",
    programId: "h8d9a111-84b2-4d1e-bd15-0d6789b52777",
    quotes: {
      fields: [
        { path: "proposer.firstName", type: "string", required: "yes", desc: "Insured first name" },
        { path: "proposer.lastName", type: "string", required: "yes", desc: "Insured last name" },
        { path: "medical.smokerStatus", type: "string", required: "yes", desc: "Smoker/Non-smoker status" },
        { path: "medical.priorHospitalization", type: "boolean", required: "yes", desc: "Prior medical hospital history" },
        { path: "risk.hasChronicConditions", type: "boolean", required: "yes", desc: "Chronic illness declaratives" },
        { path: "standard_config.deductible", type: "number", required: "yes", validation: "min 0", desc: "Standard deductible" },
        { path: "standard_config.max_limit", type: "number", required: "yes", validation: "min 0", desc: "Standard coverage limit" },
        { path: "standard_config.payment_frequency", type: "string", required: "yes", validation: "MONTHLY/ANNUALLY", desc: "Payment frequency" },
        { path: "dynamic_config.optional_covers", type: "array", required: "no", validation: "string array", desc: "Selected optional covers" },
        { path: "dynamic_config.discretionary_discount", type: "number", required: "no", validation: "0.0-1.0", desc: "Discretionary discount" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Insured Personal Health Details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>First Name</label><div class="visual-field-value">Emily</div></div>
            <div class="visual-field"><label>Last Name</label><div class="visual-field-value">Watson</div></div>
            <div class="visual-field"><label>Smoker Status</label><div class="visual-field-value">Non-Smoker</div></div>
            <div class="visual-field"><label>Prior Hospitalization</label><div class="visual-field-value">false</div></div>
            <div class="visual-field"><label>Chronic Conditions</label><div class="visual-field-value">false</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["proposer", "medical", "risk"],
        "properties": {
          "proposer": {
            "type": "object",
            "required": ["firstName", "lastName"],
            "properties": { "firstName": { "type": "string" }, "lastName": { "type": "string" } }
          },
          "medical": {
            "type": "object",
            "required": ["smokerStatus", "priorHospitalization"],
            "properties": {
              "smokerStatus": { "type": "string", "enum": ["Smoker", "Non-Smoker"] },
              "priorHospitalization": { "type": "boolean" }
            }
          },
          "risk": {
            "type": "object",
            "required": ["hasChronicConditions"],
            "properties": { "hasChronicConditions": { "type": "boolean" } }
          },
          "standard_config": {
            "type": "object",
            "required": ["deductible", "max_limit", "payment_frequency"],
            "properties": {
              "deductible": { "type": "integer", "minimum": 0 },
              "max_limit": { "type": "integer", "minimum": 0 },
              "payment_frequency": { "type": "string", "enum": ["MONTHLY", "ANNUALLY"] }
            }
          },
          "dynamic_config": {
            "type": "object",
            "required": ["optional_covers", "discretionary_discount"],
            "properties": {
              "optional_covers": {
                "type": "array",
                "items": { "type": "string" }
              },
              "discretionary_discount": { "type": "number", "minimum": 0, "maximum": 1 }
            }
          }
        }
      },
      payload: {
        programId: "h8d9a111-84b2-4d1e-bd15-0d6789b52777",
        effectiveDate: "2026-03-01T00:00:00.000Z",
        quoteData: {
          proposer: { firstName: "Emily", lastName: "Watson" },
          medical: { smokerStatus: "Non-Smoker", priorHospitalization: false },
          risk: { hasChronicConditions: false }
        },
        standard_config: {
          deductible: 250,
          max_limit: 1000000,
          payment_frequency: "MONTHLY"
        },
        dynamic_config: {
          optional_covers: ["dental_care", "vision_care", "critical_illness_rider"],
          discretionary_discount: 0.08
        }
      }
    },
    mta: {
      fields: [
        { path: "effectiveDate", type: "string", required: "yes", desc: "MTA effective date" },
        { path: "changes.medical.smokerStatus", type: "string", required: "yes", desc: "Updated smoker status" },
        { path: "reason", type: "string", required: "yes", desc: "MTA statement reason" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Health Cover MTA details</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Effective Date</label><div class="visual-field-value">2026-12-01T00:00:00Z</div></div>
            <div class="visual-field"><label>Smoker Status</label><div class="visual-field-value">Smoker</div></div>
            <div class="visual-field"><label>Reason</label><div class="visual-field-value">Policyholder declared smoking status change.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["effectiveDate", "changes", "reason"],
        "properties": {
          "effectiveDate": { "type": "string" },
          "changes": {
            "type": "object",
            "properties": {
              "medical": {
                "type": "object",
                "properties": { "smokerStatus": { "type": "string" } }
              }
            }
          },
          "reason": { "type": "string" }
        }
      },
      payload: {
        effectiveDate: "2026-12-01T00:00:00Z",
        changes: { medical: { smokerStatus: "Smoker" } },
        reason: "Policyholder declared smoking status change."
      }
    },
    renewals: {
      fields: [
        { path: "unchanged", type: "boolean", required: "yes", desc: "Unchanged health status" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Health Cover Renewal</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Health & Smoker Status Unchanged</label><div class="visual-field-value">true</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["unchanged"],
        "properties": { "unchanged": { "type": "boolean" } }
      },
      payload: { unchanged: true }
    },
    claims: {
      fields: [
        { path: "policyId", type: "string", required: "yes", desc: "Bound policy ID" },
        { path: "intake.incident.type", type: "string", required: "yes", desc: "treatment/inpatient/dental" },
        { path: "intake.incident.description", type: "string", required: "yes", desc: "claim reason" }
      ],
      formHTML: `
        <div class="visual-form-group">
          <div class="visual-group-title">Health Inpatient Hospital Claim</div>
          <div class="visual-fields-grid">
            <div class="visual-field"><label>Policy ID</label><div class="visual-field-value">h8d9a111-84b2-4d1e-bd15-0d6789b52777</div></div>
            <div class="visual-field"><label>Treatment Type</label><div class="visual-field-value">inpatient</div></div>
            <div class="visual-field"><label>Description</label><div class="visual-field-value">Acute appendicitis treatment with laparoscopic appendectomy.</div></div>
          </div>
        </div>
      `,
      schema: {
        "type": "object",
        "required": ["policyId", "intake"],
        "properties": {
          "policyId": { "type": "string" },
          "intake": {
            "type": "object",
            "required": ["incident"],
            "properties": {
              "incident": {
                "type": "object",
                "required": ["type", "description"],
                "properties": {
                  "type": { "type": "string", "enum": ["inpatient", "outpatient", "dental"] },
                  "description": { "type": "string" }
                }
              }
            }
          }
        }
      },
      payload: {
        policyId: "h8d9a111-84b2-4d1e-bd15-0d6789b52777",
        intake: {
          incident: {
            type: "inpatient",
            description: "Acute appendicitis treatment with laparoscopic appendectomy."
          }
        }
      }
    }
  }
};

// ===== 4. CORE APPLICATION STATE =====

let activeMode = "rest"; // "rest" or "mcp"
let activePageType = "concept"; // "concept" or "api"
let activePageId = "overview";
let activeSnippetLang = "curl";
let activeContractProgram = "private-motor";
let activeSchemaView = "form";
let currentRenderedMode = null;
let isProgrammaticScrolling = false;
let programmaticScrollTimeout = null;

function generateGUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    let r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function updateConsoleAuthGating() {
  const apiKeyInput = document.getElementById("api-key-input");
  const tenantIdInput = document.getElementById("tenant-id-input");
  
  if (apiKeyInput && tenantIdInput) {
    if (activeAccessLevel === "public") {
      apiKeyInput.value = "••••••••••••••••••••••••••••••••";
      apiKeyInput.disabled = true;
      apiKeyInput.style.filter = "blur(2px)";
      apiKeyInput.style.opacity = "0.5";
      
      tenantIdInput.value = "••••••••••••••••";
      tenantIdInput.disabled = true;
      tenantIdInput.style.filter = "blur(2px)";
      tenantIdInput.style.opacity = "0.5";
    } else {
      apiKeyInput.value = activeAccessLevel === "production" ? "facio_prod_sk_9R3m7aLd1Mz..." : "facio_live_pk_8F2j9aKd2Lz...";
      apiKeyInput.disabled = false;
      apiKeyInput.style.filter = "";
      apiKeyInput.style.opacity = "";
      
      tenantIdInput.value = activeAccessLevel === "production" ? "tenant_prod_active" : "tenant_default";
      tenantIdInput.disabled = false;
      tenantIdInput.style.filter = "";
      tenantIdInput.style.opacity = "";
    }
  }
}

// ===== 4. DOM EVENTS & POPULATION =====

document.addEventListener("DOMContentLoaded", () => {
  // Dynamically register gated partner products in productContracts
  if (productContracts["private-motor"]) {
    productContracts["abbeygate-motor"] = {
      ...JSON.parse(JSON.stringify(productContracts["private-motor"])),
      name: "Abbeygate Cyprus Motor",
      programId: "abbeygate-motor-prod-uuid-1"
    };
    productContracts["cooper-ninove"] = {
      ...JSON.parse(JSON.stringify(productContracts["private-motor"])),
      name: "Cooper Ninove Motor",
      programId: "cooper-ninove-prod-uuid-2"
    };
  }
  if (productContracts["commercial-property"]) {
    productContracts["client-commercial"] = {
      ...JSON.parse(JSON.stringify(productContracts["commercial-property"])),
      name: "Client-specific Commercial",
      programId: "client-comm-prod-uuid-3"
    };
  }

  // Add listener for role select
  const roleSelect = document.getElementById("role-select");
  if (roleSelect) {
    roleSelect.value = activeAccessLevel;
    roleSelect.addEventListener("change", (e) => {
      activeAccessLevel = e.target.value;
      
      // Update Environment Select automatically
      const envSelect = document.getElementById("env-select");
      if (envSelect) {
        if (activeAccessLevel === "production") {
          envSelect.value = "production";
        } else {
          envSelect.value = "sandbox";
        }
      }
      
      // Re-render sidebar and current page
      populateRestSidebar();
      populateMcpSidebar();
      
      // Force reload the current active page view to apply gating overlays
      if (currentRenderedMode === "rest") {
        renderAllRestEndpoints();
      } else {
        renderAllMcpTools();
      }
      
      // Re-trigger scroll to active page to ensure we scroll to the right element after re-render
      const targetId = activePageType === "concept" ? `concept-${activePageId}` : (activePageType === "model" ? `model-${activePageId}` : `endpoint-${activePageId}`);
      const targetEl = document.getElementById(targetId);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: "instant", block: "start" });
      }
      
      const activeNav = document.querySelector(`[data-id="${activePageId}"]`);
      if (activeNav) activeNav.classList.add("active");

      updateConsoleAuthGating();
    });
  }
  
  // Add listener for env-select
  const envSelect = document.getElementById("env-select");
  if (envSelect) {
    envSelect.addEventListener("change", (e) => {
      const selectedEnv = e.target.value;
      if (selectedEnv === "production") {
        elevateRole("production");
      } else {
        if (activeAccessLevel === "production") {
          elevateRole("sandbox");
        }
      }
    });
  }
  
  // Add listener for version-select
  const versionSelect = document.getElementById("version-select");
  if (versionSelect) {
    versionSelect.value = "v1.0.6";
    versionSelect.addEventListener("change", (e) => {
      const selectedVer = e.target.value;
      if (selectedVer !== "v1.0.6") {
        alert(`Historical Version Archive: You are viewing version ${selectedVer}. Dynamic access control gating is only active in v1.0.6 (Active).`);
      }
    });
  }

  // Navigation tabs selector
  document.getElementById("nav-tab-rest").addEventListener("click", () => swapMode("rest"));
  document.getElementById("nav-tab-mcp").addEventListener("click", () => swapMode("mcp"));
  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-elevate-role]");
    if (trigger) {
      elevateRole(trigger.getAttribute("data-elevate-role"));
    }
  });

  // Initial load
  handleInitialRoute();
  updateConsoleAuthGating();

  // Search filter
  document.getElementById("search-input").addEventListener("input", handleSearch);

  // Playground lang tabs
  document.addEventListener("click", (e) => {
    if (e.target.classList.contains("tab-btn") && e.target.parentElement.id === "language-tabs") {
      document.querySelectorAll("#language-tabs .tab-btn").forEach(b => b.classList.remove("active"));
      e.target.classList.add("active");
      activeSnippetLang = e.target.getAttribute("data-lang");
      updateCodeSnippet();
    }
  });

  // Try it out click
  document.getElementById("btn-try-it-out").addEventListener("click", handleTryItOut);

  // Copy code snippet
  document.getElementById("btn-copy-code").addEventListener("click", () => {
    const code = document.getElementById("code-snippet-block").innerText;
    navigator.clipboard.writeText(code).then(() => {
      const btn = document.getElementById("btn-copy-code");
      const originalSvg = btn.innerHTML;
      btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
      setTimeout(() => btn.innerHTML = originalSvg, 1500);
    });
  });

  // Sandbox modal controls
  document.getElementById("btn-run-sandbox").addEventListener("click", openSandboxTour);
  document.getElementById("tour-close-btn").addEventListener("click", closeSandboxTour);
  document.getElementById("tour-next-btn").addEventListener("click", advanceTourStep);
  document.getElementById("tour-prev-btn").addEventListener("click", regressTourStep);

  // Confirm mutation block controls
  document.getElementById("btn-mcp-confirm-action").addEventListener("click", executeConfirmAction);

  // Scroll to navigate scrollspy on middle panel
  const contentPanel = document.getElementById("content-panel");
  if (contentPanel) {
    contentPanel.addEventListener("scroll", () => {
      if (isProgrammaticScrolling) return; // skip updates while programmatic smooth scroll is active

      const sections = contentPanel.querySelectorAll(".concept-scroll-section, .endpoint-scroll-section, .model-scroll-section");
      let activeSectionId = null;
      let activeSectionType = null;
      let minDiff = Infinity;

      sections.forEach(sec => {
        const rect = sec.getBoundingClientRect();
        const diff = Math.abs(rect.top - 120);
        if (diff < minDiff) {
          minDiff = diff;
          activeSectionId = sec.getAttribute("data-id");
          activeSectionType = sec.classList.contains("concept-scroll-section") ? "concept" : (sec.classList.contains("model-scroll-section") ? "model" : "api");
        }
      });

      if (activeSectionId && activeSectionId !== activePageId) {
        activePageId = activeSectionId;
        activePageType = activeSectionType;
        
        // Highlight active nav item
        document.querySelectorAll(".nav-item, .endpoint-nav-item").forEach(el => el.classList.remove("active"));
        const activeNav = document.querySelector(`[data-id="${activePageId}"]`);
        if (activeNav) {
          activeNav.classList.add("active");
          activeNav.scrollIntoView({ behavior: "auto", block: "nearest" });
        }

        // Sync browser URL hash silently without creating hundreds of history states
        history.replaceState(null, null, '#' + activePageId);

        // Sync console values in place
        if (activeSectionType === "concept") {
          if (activePageId === "overview" || activePageId === "mcp-overview") {
            renderOverviewConsoleCards(activeMode);
          } else {
            restoreRegularConsoleLayout();
            const sandboxTrigger = document.querySelector(".sandbox-trigger-container");
            if (sandboxTrigger) sandboxTrigger.classList.add("hidden");
            const authBox = document.querySelector(".auth-box");
            if (authBox) authBox.classList.add("hidden");
            document.getElementById("code-snippet-block").innerText = `# Conceptual Guide Page: ${activePageId}`;
            document.getElementById("response-output-block").innerText = `Select an API route in the sidebar to try it out.`;
            document.getElementById("response-meta").innerHTML = "";
            const nextAction = document.getElementById("next-action-container");
            if (nextAction) nextAction.classList.add("hidden");
          }
        } else {
          restoreRegularConsoleLayout();
          if (activeMode === "rest") {
            const endpoint = apiEndpoints.find(e => e.id === activePageId);
            if (endpoint) {
              updateConsoleForRestEndpoint(endpoint);
            }
          } else {
            const tool = mcpTools.find(t => t.id === activePageId);
            if (tool) {
              updateConsoleForMcpTool(tool);
            }
          }
        }
      }
    });
  }

  // Handle browser back/forward history or user typing in hash
  window.addEventListener("hashchange", () => {
    const hash = window.location.hash.substring(1);
    if (hash && hash !== activePageId && !isProgrammaticScrolling) {
      handleInitialRoute();
    }
  });

  // Initialize Developer Auth
  if (typeof initDeveloperAuth === 'function') {
    initDeveloperAuth();
  }
});

// Swap Mode between REST and MCP Reference
function swapMode(mode) {
  activeMode = mode;
  document.querySelectorAll(".header-tab").forEach(tab => tab.classList.remove("active"));
  
  const restNav = document.getElementById("nav-container-rest");
  const mcpNav = document.getElementById("nav-container-mcp");
  const logoBadge = document.getElementById("logo-badge-mode");
  const subcopy = document.getElementById("mode-subcopy");

  if (mode === "rest") {
    document.getElementById("nav-tab-rest").classList.add("active");
    restNav.classList.remove("hidden");
    mcpNav.classList.add("hidden");
    logoBadge.innerText = "API";
    logoBadge.className = "logo-badge-glow";
    if (subcopy) {
      subcopy.innerText = "Integrate with Facio through deterministic API endpoints.";
    }
    
    // Set REST Console language tabs
    document.getElementById("language-tabs").innerHTML = `
      <button class="tab-btn active" data-lang="curl">cURL</button>
      <button class="tab-btn" data-lang="ts">TypeScript</button>
      <button class="tab-btn" data-lang="python">Python</button>
      <button class="tab-btn" data-lang="go">Go</button>
    `;
    activeSnippetLang = "curl";
    
    // Populate REST nav
    populateRestSidebar();
    loadPage("concept", "overview");
  } else {
    document.getElementById("nav-tab-mcp").classList.add("active");
    mcpNav.classList.remove("hidden");
    restNav.classList.add("hidden");
    logoBadge.innerText = "MCP";
    logoBadge.className = "logo-badge-glow mcp-mode";
    if (subcopy) {
      subcopy.innerText = "Connect AI agents to governed insurance workflows.";
    }

    // Set MCP Console language tabs
    document.getElementById("language-tabs").innerHTML = `
      <button class="tab-btn active" data-lang="jsonrpc">JSON-RPC</button>
      <button class="tab-btn" data-lang="chatgpt">ChatGPT</button>
      <button class="tab-btn" data-lang="claude">Claude</button>
      <button class="tab-btn" data-lang="cursor">Cursor</button>
      <button class="tab-btn" data-lang="ts">TypeScript</button>
    `;
    activeSnippetLang = "jsonrpc";

    // Populate MCP nav
    populateMcpSidebar();
    loadPage("concept", "mcp-overview");
  }
}

function handleInitialRoute() {
  const hash = window.location.hash.substring(1);
  if (!hash) {
    swapMode("rest");
    return;
  }

  // Check if hash matches an MCP guide or MCP tool
  const isMcpGuide = mcpGuides.some(g => g.id === hash);
  const isMcpTool = mcpTools.some(t => t.id === hash);

  if (isMcpGuide) {
    swapMode("mcp");
    loadPage("concept", hash);
  } else if (isMcpTool) {
    swapMode("mcp");
    loadPage("api", hash);
  } else {
    // Check if it matches a REST guide, REST data model, or REST endpoint
    const isRestGuide = conceptPages.some(c => c.id === hash);
    const isDataModel = dataModels.some(m => m.id === hash);
    const isRestEndpoint = apiEndpoints.some(e => e.id === hash);

    swapMode("rest");
    if (isRestGuide) {
      loadPage("concept", hash);
    } else if (isDataModel) {
      loadPage("model", hash);
    } else if (isRestEndpoint) {
      loadPage("api", hash);
    } else {
      loadPage("concept", "overview");
    }
  }
}

// Populate REST Sidebar Items
function populateRestSidebar() {
  const gsNav = document.getElementById("nav-getting-started");
  gsNav.innerHTML = "";
  conceptPages.forEach(p => {
    const li = document.createElement("li");
    li.className = "nav-item";
    li.setAttribute("data-id", p.id);
    
    const requiredAccess = getItemAccess("concept", p.id);
    const hasAccess = checkAccess(requiredAccess, activeAccessLevel);
    const lockIcon = !hasAccess ? `<span class="sidebar-lock-icon">🔒</span>` : "";
    
    li.innerHTML = `<span>${p.title}</span>${lockIcon}`;
    li.addEventListener("click", () => loadPage("concept", p.id));
    gsNav.appendChild(li);
  });

  const apiNav = document.getElementById("api-endpoints-nav");
  apiNav.innerHTML = "";
  
  const groups = {};
  apiEndpoints.forEach(endpoint => {
    if (!groups[endpoint.tag]) groups[endpoint.tag] = [];
    groups[endpoint.tag].push(endpoint);
  });

  Object.keys(groups).forEach(tag => {
    const groupTitle = document.createElement("div");
    groupTitle.className = "nav-group-title";
    groupTitle.innerText = tag;
    apiNav.appendChild(groupTitle);

    const ul = document.createElement("ul");
    ul.className = "nav-list";
    
    groups[tag].forEach(endpoint => {
      const li = document.createElement("li");
      li.className = "endpoint-nav-item";
      li.setAttribute("data-id", endpoint.id);
      
      const requiredAccess = getItemAccess("api", endpoint.id);
      const hasAccess = checkAccess(requiredAccess, activeAccessLevel);
      const lockIcon = !hasAccess ? `<span class="sidebar-lock-icon" style="font-size: 9px; margin-left: 4px;">🔒</span>` : "";
      
      li.innerHTML = `
        <div class="endpoint-nav-header" style="display: flex; align-items: center; justify-content: space-between; gap: 4px; min-width: 0; width: 100%;">
          <div style="display: flex; align-items: center; gap: 8px; min-width: 0; flex: 1;">
            <span class="method-badge ${endpoint.method.toLowerCase()}">${endpoint.method}</span>
            <span class="endpoint-nav-path">${endpoint.path}</span>
          </div>
          ${lockIcon}
          ${endpoint.hasDynamicSchema ? `<span class="badge-dynamic-schema" style="font-size: 8px; font-weight: 700; text-transform: uppercase; color: var(--accent-cyan); border: 1px solid var(--accent-cyan); padding: 1px 4px; border-radius: 4px; line-height: 1; height: fit-content; flex-shrink: 0;">Dynamic Schema</span>` : ""}
        </div>
        <div class="endpoint-nav-summary">${endpoint.summary}</div>
      `;
      li.addEventListener("click", () => loadPage("api", endpoint.id));
      ul.appendChild(li);
    });

    apiNav.appendChild(ul);
  });

  const modelNav = document.getElementById("nav-data-models");
  if (modelNav) {
    modelNav.innerHTML = "";
    dataModels.forEach(m => {
      const li = document.createElement("li");
      li.className = "nav-item";
      li.setAttribute("data-id", m.id);
      
      const requiredAccess = getItemAccess("model", m.id);
      const hasAccess = checkAccess(requiredAccess, activeAccessLevel);
      const lockIcon = !hasAccess ? `<span class="sidebar-lock-icon">🔒</span>` : "";
      
      li.innerHTML = `<span>${m.title}</span>${lockIcon}`;
      li.addEventListener("click", () => loadPage("model", m.id));
      modelNav.appendChild(li);
    });
  }
}

// Populate MCP Sidebar Items
function populateMcpSidebar() {
  const mcpNav = document.getElementById("nav-container-mcp");
  mcpNav.innerHTML = "";

  // 1. Agent Gateway Title & List
  const gatewayTitle = document.createElement("div");
  gatewayTitle.className = "nav-group-title";
  gatewayTitle.innerText = "Agent Gateway";
  mcpNav.appendChild(gatewayTitle);

  const gatewayUl = document.createElement("ul");
  gatewayUl.className = "nav-list";
  gatewayUl.id = "nav-mcp-guides";
  mcpGuides.filter(g => g.group === "Agent Gateway").forEach(g => {
    const li = document.createElement("li");
    li.className = "nav-item";
    li.setAttribute("data-id", g.id);
    
    const requiredAccess = getItemAccess("concept", g.id);
    const hasAccess = checkAccess(requiredAccess, activeAccessLevel);
    const lockIcon = !hasAccess ? `<span class="sidebar-lock-icon">🔒</span>` : "";
    
    li.innerHTML = `<span>${g.title}</span>${lockIcon}`;
    li.addEventListener("click", () => loadPage("concept", g.id));
    gatewayUl.appendChild(li);
  });
  mcpNav.appendChild(gatewayUl);

  // 2. MCP Mounts Title & List
  const mountsTitle = document.createElement("div");
  mountsTitle.className = "nav-group-title";
  mountsTitle.innerText = "MCP Mounts";
  mcpNav.appendChild(mountsTitle);

  const mountsUl = document.createElement("ul");
  mountsUl.className = "nav-list";
  mcpGuides.filter(g => g.group === "MCP Mounts").forEach(g => {
    const li = document.createElement("li");
    li.className = "nav-item";
    li.setAttribute("data-id", g.id);
    
    const requiredAccess = getItemAccess("concept", g.id);
    const hasAccess = checkAccess(requiredAccess, activeAccessLevel);
    const lockIcon = !hasAccess ? `<span class="sidebar-lock-icon">🔒</span>` : "";
    
    li.innerHTML = `<span>${g.title}</span>${lockIcon}`;
    li.addEventListener("click", () => loadPage("concept", g.id));
    mountsUl.appendChild(li);

    // Add nested list for endpoints/methods
    const subContainer = document.createElement("div");
    subContainer.className = "mcp-sidebar-sub-list";
    subContainer.style.paddingLeft = "16px";
    subContainer.style.marginBottom = "8px";
    subContainer.style.marginTop = "2px";
    
    const isConfig = g.id.includes("config");
    const path = isConfig ? "/api/v1/mcp/config" : "/api/v1/mcp/operator";
    
    subContainer.innerHTML = `
      <div class="mcp-sub-item-code" style="font-family: var(--font-mono); font-size: 10px; color: var(--text-muted); margin-bottom: 2px; border-left: 1px solid rgba(255,255,255,0.05); padding-left: 8px;">POST ${path}</div>
      <div class="mcp-sub-item-code" style="font-family: var(--font-mono); font-size: 10px; color: var(--text-muted); margin-bottom: 2px; border-left: 1px solid rgba(255,255,255,0.05); padding-left: 8px;">tools/list</div>
      <div class="mcp-sub-item-code" style="font-family: var(--font-mono); font-size: 10px; color: var(--text-muted); margin-bottom: 2px; border-left: 1px solid rgba(255,255,255,0.05); padding-left: 8px;">tools/call</div>
    `;
    mountsUl.appendChild(subContainer);
  });
  mcpNav.appendChild(mountsUl);

  // 3 & 4. Config/Operator Agent Tools
  const toolFamilies = ["Config Agent Tools", "Operator Agent Tools"];
  
  // Create container for tools navigation search
  const toolsNav = document.createElement("div");
  toolsNav.id = "mcp-tools-nav";
  mcpNav.appendChild(toolsNav);

  toolFamilies.forEach(family => {
    const familyTitle = document.createElement("div");
    familyTitle.className = "nav-group-title";
    familyTitle.innerText = family;
    toolsNav.appendChild(familyTitle);

    const familyTools = mcpTools.filter(t => t.family === family);
    
    const phases = {};
    familyTools.forEach(t => {
      if (!phases[t.phase]) phases[t.phase] = [];
      phases[t.phase].push(t);
    });

    const familyContainer = document.createElement("div");
    familyContainer.className = "mcp-family-container";

    Object.keys(phases).forEach(phaseName => {
      const phaseTitle = document.createElement("div");
      phaseTitle.className = "mcp-sidebar-phase-title";
      phaseTitle.style.fontSize = "11px";
      phaseTitle.style.fontWeight = "600";
      phaseTitle.style.textTransform = "uppercase";
      phaseTitle.style.letterSpacing = "0.5px";
      phaseTitle.style.color = "var(--text-muted)";
      phaseTitle.style.padding = "8px 12px 4px 12px";
      phaseTitle.innerText = phaseName;
      familyContainer.appendChild(phaseTitle);

      const ul = document.createElement("ul");
      ul.className = "nav-list";

      phases[phaseName].forEach(tool => {
        const li = document.createElement("li");
        li.className = "endpoint-nav-item";
        li.setAttribute("data-id", tool.id);
        
        const requiredAccess = getItemAccess("api", tool.id);
        const hasAccess = checkAccess(requiredAccess, activeAccessLevel);
        const lockIcon = !hasAccess ? `<span class="sidebar-lock-icon" style="font-size: 9px; margin-left: 4px;">🔒</span>` : "";
        
        let badgeColor = "var(--accent-purple)";
        if (tool.auditClass === "read") badgeColor = "var(--accent-green)";
        else if (tool.auditClass === "draft") badgeColor = "var(--accent-amber)";
        else if (tool.auditClass === "simulate") badgeColor = "var(--accent-cyan)";
        else if (tool.auditClass === "comm") badgeColor = "var(--accent-magenta)";
        else if (tool.auditClass === "mutate") badgeColor = "var(--accent-magenta)";
        
        li.innerHTML = `
          <div class="endpoint-nav-header" style="display: flex; align-items: center; justify-content: space-between; gap: 4px; min-width: 0; width: 100%;">
            <div style="display: flex; align-items: center; gap: 8px; min-width: 0; flex: 1;">
              <span class="method-badge mcp-tool" style="background-color: rgba(255, 255, 255, 0.05); color: ${badgeColor}; min-width: 48px; font-size: 9px; padding: 1px 4px;">${tool.auditClass.toUpperCase()}</span>
              <span class="endpoint-nav-path" style="font-size: 11px;">${tool.canonicalName}</span>
            </div>
            ${lockIcon}
          </div>
        `;
        li.addEventListener("click", () => loadPage("api", tool.id));
        ul.appendChild(li);
      });

      familyContainer.appendChild(ul);
    });

    toolsNav.appendChild(familyContainer);
  });
}

// Universal Search Filter Engine
function handleSearch(e) {
  const query = e.target.value.toLowerCase().trim();
  const allNavItems = document.querySelectorAll(".nav-item, .endpoint-nav-item");
  const allGroupTitles = document.querySelectorAll(".nav-group-title");

  if (!query) {
    allNavItems.forEach(el => el.style.display = "");
    allGroupTitles.forEach(el => el.style.display = "");
    return;
  }

  allGroupTitles.forEach(el => el.style.display = "none");

  if (activeMode === "rest") {
    conceptPages.forEach(p => {
      const el = document.querySelector(`.nav-item[data-id="${p.id}"]`);
      if (el) {
        const match = p.title.toLowerCase().includes(query) || p.description.toLowerCase().includes(query);
        el.style.display = match ? "" : "none";
        if (match) showParentGroupTitle(el);
      }
    });

    apiEndpoints.forEach(endpoint => {
      const el = document.querySelector(`.endpoint-nav-item[data-id="${endpoint.id}"]`);
      if (el) {
        const match = endpoint.path.toLowerCase().includes(query) || 
                      endpoint.summary.toLowerCase().includes(query) || 
                      endpoint.description.toLowerCase().includes(query);
        el.style.display = match ? "" : "none";
        if (match) showParentGroupTitle(el);
      }
    });
  } else {
    mcpGuides.forEach(g => {
      const el = document.querySelector(`.nav-item[data-id="${g.id}"]`);
      if (el) {
        const match = g.title.toLowerCase().includes(query) || g.description.toLowerCase().includes(query);
        el.style.display = match ? "" : "none";
        if (match) showParentGroupTitle(el);
      }
    });

    mcpTools.forEach(tool => {
      const el = document.querySelector(`.endpoint-nav-item[data-id="${tool.id}"]`);
      if (el) {
        const match = tool.canonicalName.toLowerCase().includes(query) || 
                      tool.description.toLowerCase().includes(query) ||
                      tool.family.toLowerCase().includes(query) ||
                      tool.phase.toLowerCase().includes(query);
        el.style.display = match ? "" : "none";
        if (match) showParentGroupTitle(el);
      }
    });
  }
}

function showParentGroupTitle(navElement) {
  // Search upwards for parent elements and display them
  let parent = navElement.parentElement;
  while (parent && parent !== document.getElementById("nav-container-mcp") && parent !== document.getElementById("nav-container-rest")) {
    let prev = parent.previousElementSibling;
    if (prev && prev.classList.contains("nav-group-title")) {
      prev.style.display = "";
    }
    if (parent.classList.contains("mcp-family-container")) {
      let famTitle = parent.previousElementSibling;
      if (famTitle && famTitle.classList.contains("nav-group-title")) {
        famTitle.style.display = "";
      }
    }
    parent = parent.parentElement;
  }
  
  // Show immediate phase title or group title
  let sibling = navElement.parentElement;
  if (sibling && sibling.classList.contains("nav-list")) {
    let title = sibling.previousElementSibling;
    if (title && (title.classList.contains("nav-group-title") || title.classList.contains("mcp-sidebar-phase-title"))) {
      title.style.display = "";
    }
  }
}

const HERO_PAYLOAD_ENDPOINTS = [
  "create-quote",
  "bind-policy",
  "quote-endorsement",
  "bind-endorsement",
  "preview-renewal",
  "submit-renewal-declaration",
  "finalize-renewal",
  "submit-fnol",
  "adjust-claim-reserve",
  "create-claim-payment",
  "create-claim-recovery",
  "validate-risk-binder",
  "allocate-binder",
  "facultative-referral",
  "invoice-payment-intent",
  "mark-invoice-paid",
  "refund-invoice",
  "create-bdx-export",
  "validate-bdx-export",
  "create-bdx-submission",
  "create-webhook-endpoint",
  "update-webhook-endpoint",
  "test-webhook-endpoint",
  "replay-webhook-event",
  "create-entity",
  "create-policyholder"
];

const MINIMAL_BODY_ENDPOINTS = {
  "refresh-quote": { "effectiveDate": "2026-04-01T00:00:00Z", "reason": "Adjusted inception date" },
  "refer-quote": { "reasonCode": "HIGH_VALUE_RISK", "notes": "Escalating for underwriter review" },
  "decline-quote": { "declineReasonCode": "RISK_OUTSIDE_APPETITE", "notes": "Proposer's vehicle modifications exceed the authorized program limits." },
  "quote-payment-intent": { "paymentMethodType": "CREDIT_CARD", "provider": "CARDCORP", "successUrl": "https://your-mga-portal.com/success", "cancelUrl": "https://your-mga-portal.com/cancel" },
  "cancel-policy": { "effectiveDate": "2026-06-01T00:00:00Z", "reasonCode": "CUSTOMER_REQUEST", "refundMode": "PRO_RATA" },
  "reinstate-policy": { "reinstatementDate": "2026-06-09T00:00:00Z", "reason": "Lapsed payment resolved", "paymentDetails": { "paymentMode": "CUSTOMER_CARD", "provider": "CARDCORP", "checkoutId": "chk_9482710a" } },
  "regenerate-policy-documents": { "documentTypes": ["SCHEDULE", "CERTIFICATE_OF_INSURANCE"], "version": 2, "reason": "Insured name corrected on MTA endorsement" },
  "cancel-endorsement": { "reason": "MTA draft discarded by policyholder" },
  "create-renewal-setup": { "renewalDate": "2027-03-01T00:00:00Z", "mode": "AUTO" },
  "quote-renewal": { "vehicleValue": 20000, "driverInfo": { "licenseYears": 6, "claimsCount": 0 }, "selectedCoverages": ["COMPREHENSIVE", "ROAD_ASSISTANCE"] },
  "renewal-payment-intent": { "paymentMethodType": "CREDIT_CARD", "provider": "CARDCORP", "successUrl": "https://your-mga-portal.com/success", "cancelUrl": "https://your-mga-portal.com/cancel" },
  "lapse-renewal": { "lapseReason": "CUSTOMER_DECLINED_RENEWAL", "notes": "Insured found a cheaper alternative and declined to renew." },
  "add-claim-documents": { "documentType": "CLAIM_EVIDENCE_PHOTO", "description": "Front bumper damage photo" },
  "close-claim": { "closeReason": "SETTLED_IN_FULL", "notes": "Repairs completed and repair invoices paid in full. Remaining outstanding reserves reduced to zero." },
  "reopen-claim": { "reason": "New medical evidence received regarding claimant injuries" }
};

// Dynamic product contract response & request calculators
const SPECIAL_PRODUCT_ENDPOINTS = [
  "list-programs",
  "get-program-questions",
  "create-quote",
  "bind-policy",
  "quote-endorsement",
  "preview-renewal",
  "submit-renewal-declaration",
  "submit-fnol",
  "validate-risk-binder",
  "create-bdx-export"
];

function getProgramsResponseBody(programKey) {
  const program = productContracts[programKey] || productContracts["private-motor"];
  let productType = "MOTOR";
  let currency = "EUR";
  let carrier = "Lloyd's Syndicate 1991";
  if (programKey === "commercial-property") { productType = "PROPERTY"; currency = "EUR"; carrier = "Allianz Global Corporate"; }
  else if (programKey === "homeowners") { productType = "HOMEOWNERS"; currency = "GBP"; carrier = "Aviva Insurance"; }
  else if (programKey === "travel") { productType = "TRAVEL"; currency = "EUR"; carrier = "AXA Assistance"; }
  else if (programKey === "pet") { productType = "PET"; currency = "USD"; carrier = "RSA Insurance Group"; }
  else if (programKey === "builders-risk") { productType = "CONSTRUCTION"; currency = "EUR"; carrier = "Swiss Re Corporate Solutions"; }
  else if (programKey === "professional-indemnity") { productType = "LIABILITY"; currency = "GBP"; carrier = "Liberty Mutual"; }
  else if (programKey === "health-accident") { productType = "HEALTH"; currency = "EUR"; carrier = "Bupa Global"; }

  return {
    success: true,
    data: [
      {
        id: program.programId,
        name: program.name,
        status: "ACTIVE",
        productType: productType,
        currency: currency,
        carrier: carrier
      }
    ]
  };
}

function getPreviewRenewalResponseBody(programKey) {
  let originalPremium = 488.50;
  let projectedRenewalPremium = 512.00;
  
  if (programKey === "private-motor") { originalPremium = 450.00; projectedRenewalPremium = 485.00; }
  else if (programKey === "commercial-property") { originalPremium = 2450.00; projectedRenewalPremium = 2600.00; }
  else if (programKey === "homeowners") { originalPremium = 320.00; projectedRenewalPremium = 345.00; }
  else if (programKey === "travel") { originalPremium = 75.00; projectedRenewalPremium = 75.00; }
  else if (programKey === "pet") { originalPremium = 180.00; projectedRenewalPremium = 210.00; }
  else if (programKey === "builders-risk") { originalPremium = 5800.00; projectedRenewalPremium = 6100.00; }
  else if (programKey === "professional-indemnity") { originalPremium = 1250.00; projectedRenewalPremium = 1320.00; }
  else if (programKey === "health-accident") { originalPremium = 950.00; projectedRenewalPremium = 1050.00; }
  
  return {
    success: true,
    data: {
      originalPremium: originalPremium,
      projectedRenewalPremium: projectedRenewalPremium,
      status: "CALCULATED",
      priceDelta: +(projectedRenewalPremium - originalPremium).toFixed(2),
      currency: programKey === "homeowners" || programKey === "professional-indemnity" ? "GBP" : programKey === "pet" ? "USD" : "EUR"
    }
  };
}

function getValidateRiskRequestBody(programKey) {
  const program = productContracts[programKey] || productContracts["private-motor"];
  let riskDetails = {};
  if (programKey === "private-motor") {
    riskDetails = { vehicleValue: 15000, engineSize: 1500, licenseYears: 10 };
  } else if (programKey === "commercial-property") {
    riskDetails = { sumInsured: 2500000, occupancyType: "COMMERCIAL_RETAIL", fireProtection: "SPRINKLERS_INSTALLED" };
  } else if (programKey === "homeowners") {
    riskDetails = { buildingSumInsured: 450000, contentsSumInsured: 75000, roofType: "TILE" };
  } else if (programKey === "travel") {
    riskDetails = { destinationZone: "WORLDWIDE_EXCL_USA", tripDurationDays: 14, travelersCount: 2 };
  } else if (programKey === "pet") {
    riskDetails = { petType: "DOG", breed: "Golden Retriever", petAgeYears: 4 };
  } else if (programKey === "builders-risk") {
    riskDetails = { projectValue: 8500000, excavationDepthMeters: 4.5, contractorExpYears: 12 };
  } else if (programKey === "professional-indemnity") {
    riskDetails = { annualRevenue: 1500000, professionCategory: "IT_CONSULTING", retroactiveDate: "2020-01-01" };
  } else if (programKey === "health-accident") {
    riskDetails = { smokerStatus: "Non-Smoker", hasChronicConditions: false, inpatientLimit: 150000 };
  }
  
  return {
    programId: program.programId,
    effectiveDate: "2026-03-01T00:00:00.000Z",
    riskDetails: riskDetails
  };
}

function getBdxExportRequestBody(programKey) {
  const program = productContracts[programKey] || productContracts["private-motor"];
  let productType = "MOTOR";
  if (programKey === "commercial-property") productType = "PROPERTY";
  else if (programKey === "homeowners") productType = "HOMEOWNERS";
  else if (programKey === "travel") productType = "TRAVEL";
  else if (programKey === "pet") productType = "PET";
  else if (programKey === "builders-risk") productType = "CONSTRUCTION";
  else if (programKey === "professional-indemnity") productType = "LIABILITY";
  else if (programKey === "health-accident") productType = "HEALTH";

  return {
    month: "2026-03",
    exportType: "FULL_MGA_PACK",
    filters: {
      programId: program.programId,
      productType: productType,
      currency: programKey === "homeowners" || programKey === "professional-indemnity" ? "GBP" : programKey === "pet" ? "USD" : "EUR"
    }
  };
}

function getDynamicSuccessResponse(endpoint) {
  const programKey = activeContractProgram;
  const program = productContracts[programKey] || productContracts["private-motor"];
  
  if (endpoint.id === "list-programs") {
    return getProgramsResponseBody(programKey);
  }
  if (endpoint.id === "get-program-questions") {
    return {
      success: true,
      data: {
        programId: program.programId,
        schema: program.quotes.schema
      }
    };
  }
  if (endpoint.id === "create-quote") {
    const defaultStandardConfig = {
      deductible: 500,
      max_limit: 100000,
      payment_frequency: "ANNUALLY"
    };
    const defaultDynamicConfig = {
      optional_covers: ["roadside_assistance", "windshield_cover"],
      discretionary_discount: 0.05
    };

    return {
      success: true,
      data: {
        quoteId: "q_" + programKey + "_" + generateGUID().slice(0, 8),
        status: "QUOTED",
        premiumBreakdown: {
          grossPremium: programKey === "commercial-property" ? 2450.00 : programKey === "builders-risk" ? 5800.00 : programKey === "professional-indemnity" ? 1250.00 : programKey === "health-accident" ? 950.00 : 450.00,
          commission: programKey === "commercial-property" ? 367.50 : 67.50,
          taxes: programKey === "commercial-property" ? 122.50 : 22.50
        },
        standard_config: activeMode === "rest" && program.quotes.payload && program.quotes.payload.standard_config 
          ? program.quotes.payload.standard_config 
          : defaultStandardConfig,
        dynamic_config: activeMode === "mcp" && program.quotes.payload && program.quotes.payload.dynamic_config 
          ? program.quotes.payload.dynamic_config 
          : defaultDynamicConfig
      }
    };
  }
  if (endpoint.id === "bind-policy") {
    return {
      success: true,
      data: {
        policyId: "pol_" + programKey + "_" + generateGUID().slice(0, 8),
        policyNumber: "POL-" + programKey.toUpperCase().replace("-", "") + "-" + Math.floor(Math.random() * 90000 + 10000),
        status: "ISSUED",
        programId: program.programId
      }
    };
  }
  if (endpoint.id === "quote-endorsement") {
    return {
      success: true,
      data: {
        endorsementId: "mta_" + programKey + "_" + generateGUID().slice(0, 8),
        premiumDelta: programKey === "commercial-property" ? 150.00 : programKey === "builders-risk" ? 300.00 : 35.00,
        status: "QUOTED",
        effectiveDate: "2026-06-01T00:00:00Z"
      }
    };
  }
  if (endpoint.id === "preview-renewal") {
    return getPreviewRenewalResponseBody(programKey);
  }
  if (endpoint.id === "submit-fnol") {
    return {
      success: true,
      data: {
        claimId: "clm_" + programKey + "_" + generateGUID().slice(0, 8),
        claimNumber: "CLM-" + programKey.toUpperCase().replace("-", "") + "-" + Math.floor(Math.random() * 90000 + 10000),
        status: "SUBMITTED"
      }
    };
  }
  if (endpoint.id === "validate-risk-binder") {
    return {
      success: true,
      data: {
        decision: "ACCEPT",
        validationRef: "VAL-RE-" + programKey.toUpperCase().replace("-", "") + "-" + generateGUID().slice(0, 6).toUpperCase(),
        limitChecked: programKey === "commercial-property" ? 2500000 : programKey === "builders-risk" ? 8500000 : 500000,
        capacityUtilizedPercent: 12.35
      }
    };
  }
  if (endpoint.id === "create-bdx-export") {
    return {
      success: true,
      data: {
        exportId: "bdx_exp_2026_03_" + programKey.replace("-", "_"),
        status: "COMPLETED",
        recordsProcessed: 142,
        downloadUrl: `https://vault.facio.io/exports/BDX_2026_03_${programKey.replace("-", "_")}.xlsx`
      }
    };
  }
  return endpoint.response.body;
}

function getActiveContractSubKey(endpointId) {
  if (endpointId === "create-quote") return "quotes";
  if (endpointId === "quote-endorsement") return "mta";
  if (endpointId === "submit-fnol") return "claims";
  if (endpointId === "submit-renewal-declaration") return "renewals";
  return "quotes";
}

function getWhenToUseText(endpoint) {
  if (endpoint.extensions?.["x-facio-when-to-use"]) {
    return endpoint.extensions["x-facio-when-to-use"];
  }
  const id = endpoint.id;
  if (id === "list-programs") return "Use this endpoint to retrieve the list of active insurance programs available to your tenant, including basic product parameters and metadata.";
  if (id === "get-program") return "Retrieve complete setup details for a single insurance program by its unique ID. Perfect for configuring form interfaces dynamically.";
  if (id === "get-program-questions") return "Call this to retrieve the standard JSON Schema draft-07 of underwriting and rating questions required to request a quote under this program.";
  if (id === "get-program-coverages") return "Use this to discover which base coverages and optional extensions are supported by this program's delegated authority limits.";
  if (id === "get-program-rules") return "Retrieve active underwriting rule configurations and compliance limits. Helpful for predicting referral and decline states pre-submission.";
  if (id === "get-program-documents") return "Query the layout maps and PDF templates generated during policy lifecycle bindings for this program.";
  if (id === "get-program-jurisdictions") return "Get region-specific tax codes, premium taxes, and local regulatory mandates required for transaction submissions.";
  if (id === "create-quote") return "Call this when a prospective customer submits a complete questionnaire to calculate firm risk premiums, taxes, and fees. This returns a quote token.";
  if (id === "get-quote") return "Retrieve detailed calculations, premium breakdowns, and underwriting status of a previously generated quote.";
  if (id === "refresh-quote") return "Trigger a recalculation of an existing quote, updating inception dates or applying updated rating schemas without changing the risk profile.";
  if (id === "refer-quote") return "Refer a quote manually to an underwriter with notes. Puts the quote status into UNDERWRITER_REFERRAL.";
  if (id === "decline-quote") return "Explicitly decline a quote due to compliance or underwriting exclusion rules, setting status to DECLINED.";
  if (id === "quote-payment-intent") return "Generate a checkout link or payment token (e.g. CardCorp payload) for the customer to authorize premium collection.";
  if (id === "bind-policy") return "Trigger this once the client accepts the quote and a valid payment token is captured. Officially binds coverage and issues the policy contract.";
  if (id === "get-policy") return "Retrieve the master profile of a bound policy contract, including its active coverages, premium totals, and inception/expiration times.";
  if (id === "get-policy-timeline") return "Retrieve the chronological audit log of all versioned changes, endorsements, claims, and invoice statuses on this policy.";
  if (id === "get-policy-documents") return "Get links to PDF files (schedule, certificate of insurance, invoice statement) currently issued under this policy.";
  if (id === "get-policy-financials") return "Retrieve accounting-level totals of payments, refunds, taxes, and outstanding balance for a given policy contract.";
  if (id === "cancel-policy") return "Initiate policy cancellation. Calculates short-rate or pro-rata return premium based on effective date and refund mode.";
  if (id === "reinstate-policy") return "Reverse a cancellation event and reinstate active cover, restoring policy terms to full force.";
  if (id === "regenerate-policy-documents") return "Force-regenerate policy PDF documents to incorporate endorsement changes or correct clerical details.";
  if (id === "list-endorsements") return "Retrieve all draft or bound endorsements associated with a policy.";
  if (id === "quote-endorsement") return "Create a draft Mid-Term Adjustment (MTA). Computes the pro-rata premium delta for the remaining policy term.";
  if (id === "get-endorsement") return "Retrieve the details and pro-rata cost adjustments of a specific endorsement (MTA).";
  if (id === "bind-endorsement") return "Commit and issue a drafted endorsement. This updates the active policy terms and generates a revised schedule.";
  if (id === "cancel-endorsement") return "Discard a draft endorsement quote, unlocking the policy from adjustment state.";
  if (id === "create-renewal-setup") return "Set up or toggle renewal automation terms, specifying renewal notification lead times and automated rating parameters.";
  if (id === "get-renewal") return "Retrieve a versioned renewal offer status, showing renewal terms, premiums, and declaration responses.";
  if (id === "preview-renewal") return "Generate a draft renewal quote based on historical policy risk answers and current program rates.";
  if (id === "quote-renewal") return "Calculate final renewal premium rates, applying updated risk declarations.";
  if (id === "submit-renewal-declaration") return "Submit renewal compliance questions. Prompts the client to declare claims history and material changes.";
  if (id === "renewal-payment-intent") return "Initialize payment capture flow specifically for a renewal invoice.";
  if (id === "finalize-renewal") return "Authorize the issuance of the renewed policy contract, initiating the subsequent coverage term.";
  if (id === "lapse-renewal") return "Manually lapse a renewal offer if the customer declines renewal terms or payment fails.";
  if (id === "submit-fnol") return "File a First Notice of Loss. Triggers automated coverage verification and starts the claim file.";
  if (id === "get-claim") return "Retrieve a comprehensive claim file status, detailing outstanding reserves, payments, and adjuster assignments.";
  if (id === "get-claim-contract") return "Inspect the specific coverages and limits active on the policy at the exact timestamp of the loss event.";
  if (id === "get-claim-timeline") return "Retrieve history of claims handling steps, status changes, and adjuster updates.";
  if (id === "adjust-claim-reserve") return "Adjust claim reserves for outstanding indemnity or expense. Essential for carrier balance sheet integrity.";
  if (id === "create-claim-payment") return "Disburse a claim payment to the policyholder, vendor, or third-party claimant. Deducts from reserve bucket.";
  if (id === "create-claim-recovery") return "Log a claims recovery (subrogation, salvage, or third-party contribution) and reconcile financials.";
  if (id === "add-claim-documents") return "Upload claims evidence (police reports, damage photos, medical receipts) associated with a claim.";
  if (id === "close-claim") return "Mark a claim as closed (settled, withdrawn, or without payment), releasing any remaining outstanding reserves.";
  if (id === "reopen-claim") return "Reopen a closed claim due to subsequent disputes, new evidence, or supplementary billing.";
  if (id === "validate-risk-binder") return "Validate a risk against binder authority bounds, exclusions, and territorial scope rules before quoting.";
  if (id === "get-binder") return "Retrieve coverholder binder parameters, agreement numbers, and reporting versions.";
  if (id === "get-binder-capacity") return "Check remaining capacity balances and utilization percentages for a binder capacity agreement.";
  if (id === "allocate-binder") return "Commit and lock a policy's premium exposure allocation under a binder limit.";
  if (id === "facultative-referral") return "Escalate a risk exceeding standard binder capacity bounds for manual underwriter review.";
  if (id === "get-policy-binder-allocation") return "Retrieve how bound policy risks are allocated to active coverholder binders.";
  if (id === "get-claim-binder-recovery") return "Retrieve claim reinsurance recovery calculations under a binder.";
  if (id === "list-binders") return "List all coverholder binders configured for the tenant account.";
  if (id === "get-binder-financials") return "Retrieve the financial parameters of a coverholder binder.";
  if (id === "list-entities") return "List all insured subjects registered in the system.";
  if (id === "create-entity") return "Create a new insured subject Entity profile.";
  if (id === "list-policyholders") return "List all policyholders responsible for billing.";
  if (id === "create-policyholder") return "Create a new policyholder contracting party profile.";
  if (id === "get-policy-sanctions") return "Retrieve sanctions screening runs executed for a policy.";
  if (id === "list-bdx-periods") return "List BDX reporting periods and locking status.";
  if (id === "lock-bdx-period") return "Lock a BDX reporting period to freeze transaction entries.";
  if (id === "get-policy-ledger") return "Inspect the complete double-entry transaction history of debits and credits on a policy's balance ledger.";
  if (id === "get-policy-invoices") return "Retrieve standard PDF invoices and payment status lists associated with a policy.";
  if (id === "invoice-payment-intent") return "Generate payment checkout credentials for a specific outstanding invoice.";
  if (id === "mark-invoice-paid") return "Record manual payment reconciliation (e.g. bank wire received) on an outstanding invoice.";
  if (id === "refund-invoice") return "Initiate partial or complete premium refunds, generating ledger offset transactions.";
  if (id === "list-risk-transactions") return "Query the global risk transaction journal across all policy accounts.";
  if (id === "get-risk-transaction") return "Inspect details of a single risk transaction entry, showing balanced debit/credit legs and tax lines.";
  if (id === "get-account-balance") return "Retrieve the current running balance and cash flow totals for a specific ledger account.";
  if (id === "get-bdx-risk") return "Generate or query Bordereau Risk exports listing active policy exposures for carrier audit.";
  if (id === "get-bdx-premium") return "Generate Bordereau Premium exports matching transactional ledger entries for carriers.";
  if (id === "get-bdx-claims") return "Generate Bordereau Claims logs detailing outstanding reserves and payments for carrier reporting.";
  if (id === "create-bdx-export") return "Trigger a new Bordereau export process, selecting date bounds, programs, and reporting formats.";
  if (id === "get-bdx-export") return "Retrieve status and download links of a previously compiled Bordereau export file.";
  if (id === "validate-bdx-export") return "Run automated structural validation check on a Bordereau file output to prevent carrier rejects.";
  if (id === "create-bdx-submission") return "Transmit validated Bordereau files to carrier systems or central Lloyd's repositories.";
  if (id === "list-bdx-submissions") return "List past Bordereau transmissions, showing acknowledgement states and carrier receipt logs.";
  if (id === "list-webhook-endpoints") return "List all registered webhook listener URLs in your tenant workspace.";
  if (id === "create-webhook-endpoint") return "Register a new webhook listener URL, selecting event subscriptions and generating signing secrets.";
  if (id === "get-webhook-endpoint") return "Retrieve signing keys and active configuration of a registered webhook endpoint.";
  if (id === "update-webhook-endpoint") return "Update event subscriptions or URL parameters of a registered webhook listener.";
  if (id === "test-webhook-endpoint") return "Trigger a simulated mock webhook event delivery to test your listener implementation.";
  if (id === "list-webhook-events") return "Query the historical webhook delivery ledger, showing event logs and HTTP status responses.";
  if (id === "get-webhook-event") return "Retrieve detailed payload and dispatch details of a specific webhook event.";
  if (id === "replay-webhook-event") return "Force delivery retry of an event that failed due to listener server downtime.";

  return `Use this endpoint to interact with the ${endpoint.tag || 'Facio'} resource and perform downstream integrations.`;
}

function getLifecycleImpactText(endpoint) {
  const stage = endpoint.extensions?.["x-facio-lifecycle-stage"];
  if (stage === "DISCOVER") return "Retrieves read-only configuration schemas. Does not advance the policy lifecycle state.";
  if (stage === "QUOTE") return "Generates a transient, non-binding quote record. Reserves the pricing parameters and limits for up to 30 days.";
  if (stage === "BIND") return "Officially binds coverage. Transitions status from QUOTED to ISSUED (Active policy). Generates PDF policy documents and registers ledger accounts.";
  if (stage === "SERVICING") return "Performs non-financial adjustments to policy metadata or regenerates document packs.";
  if (stage === "MTA") return "Creates a mid-term adjustment (endorsement) version of the policy. Transitions status to ENDORSED upon binding.";
  if (stage === "RENEWAL") return "Initiates renewal offer generation, calculating a premium draft for the subsequent coverage period.";
  if (stage === "CLAIM") return "Enters the claims pipeline. Receives incident details, checks coverage dates, and locks outstanding reserve funds.";
  if (stage === "LEDGER") return "Performs accounting movements, reconciling invoices, payment collections, or initiating refunds.";
  if (stage === "BDX") return "Compiles Bordereau exposures, premiums, or claims into compliant format logs for carrier verification.";
  if (stage === "WEBHOOK") return "Registers, tests, or replays event streams to sync tenant databases in real-time.";
  return "No lifecycle state changes occur for this operation.";
}

function getLedgerImpactText(endpoint) {
  const id = endpoint.id;
  const programKey = activeContractProgram;
  const isGbp = programKey === "homeowners" || programKey === "professional-indemnity";
  const isUsd = programKey === "pet";
  const currencySymbol = isGbp ? "£" : isUsd ? "$" : "€";
  
  if (id === "bind-policy") {
    const gross = programKey === "commercial-property" ? "2,450.00" : programKey === "builders-risk" ? "5,800.00" : programKey === "professional-indemnity" ? "1,250.00" : programKey === "health-accident" ? "950.00" : "450.00";
    return `Creates double-entry balance ledger transactions: Debits <strong>Premium Receivable</strong> and Credits <strong>Carrier Payable</strong> (${currencySymbol}${gross}). Credits MGA Commission revenue account.`;
  }
  if (id === "adjust-claim-reserve") return "Adjusts carrier balance sheets: Debits <strong>Claims Expense</strong> and Credits <strong>Claims Reserves (Outstanding Loss Reserve)</strong> by the net change value (€15,000.00).";
  if (id === "create-claim-payment") return "Processes financial disbursement: Debits <strong>Claims Reserves (Outstanding Loss)</strong> and Credits <strong>Cash/Bank Account</strong> (€4,500.00). Reductions are reflected in active capacities.";
  if (id === "create-claim-recovery") return "Logs recoveries: Debits <strong>Cash/Bank Account</strong> and Credits <strong>Claims Expense</strong> or Reinsurance Recovery receivables (€2,500.00).";
  if (id === "mark-invoice-paid") {
    const gross = programKey === "commercial-property" ? "2,450.00" : programKey === "builders-risk" ? "5,800.00" : programKey === "professional-indemnity" ? "1,250.00" : programKey === "health-accident" ? "950.00" : "450.00";
    return `Performs invoice payment clearing: Debits <strong>Cash/Bank Account</strong> and Credits <strong>Premium Receivable</strong> by the invoice amount (${currencySymbol}${gross}), resolving outstanding balance.`;
  }
  if (id === "refund-invoice") return "Performs reverse ledger adjustments: Debits <strong>Premium Revenue / Return Premium</strong> and Credits <strong>Cash/Bank Account</strong> for the return amount.";
  if (id === "bind-endorsement") {
    const delta = programKey === "commercial-property" ? "150.00" : programKey === "builders-risk" ? "300.00" : "35.00";
    return `Performs endorsement pro-rata ledger entry: Debits/Credits <strong>Premium Receivable</strong> and Credits/Debits <strong>Carrier Payable</strong> based on the premium delta (${currencySymbol}${delta}).`;
  }
  if (id === "finalize-renewal") {
    const renewalGross = programKey === "commercial-property" ? "2,600.00" : programKey === "builders-risk" ? "6,100.00" : programKey === "professional-indemnity" ? "1,320.00" : programKey === "health-accident" ? "1,050.00" : "485.00";
    return `Renews active ledger flow: Debits <strong>Premium Receivable</strong> and Credits <strong>Carrier Payable</strong> for the new term premium (${currencySymbol}${renewalGross}).`;
  }

  if (endpoint.method === "GET" || endpoint.method === "DELETE") {
    return "Read-only operation. No balance ledger impact or double-entry movements registered.";
  }
  return "No direct financial accounting entries or ledger transactions generated for this action.";
}

function getWebhookEventsHTML(endpoint) {
  const webhooks = endpoint.extensions?.["x-facio-related-webhooks"];
  if (webhooks && webhooks.length > 0) {
    return `<div class="extension-badges-container">${webhooks.map(w => `<span class="extension-badge webhook">${w}</span>`).join('')}</div>`;
  }
  return "<p class='no-webhooks-text'>No webhook events are dispatched by this action.</p>";
}

function getRelatedEndpointsHTML(endpoint) {
  let related = [];
  if (endpoint.extensions?.["x-facio-next-actions"]) {
    endpoint.extensions["x-facio-next-actions"].forEach(act => {
      const target = apiEndpoints.find(e => e.path === act.path && e.method === act.method);
      if (target && !related.find(r => r.id === target.id)) {
        related.push(target);
      }
    });
  }
  const tagEndpoints = apiEndpoints.filter(e => e.tag === endpoint.tag && e.id !== endpoint.id);
  tagEndpoints.slice(0, 3).forEach(e => {
    if (!related.find(r => r.id === e.id)) {
      related.push(e);
    }
  });

  if (related.length > 0) {
    return `
      <div class="related-endpoints-links">
        ${related.map(e => `
          <a href="#" class="related-endpoint-link" data-id="${e.id}">
            <span class="method-badge mini ${e.method.toLowerCase()}">${e.method}</span>
            <span class="path-link">${e.path}</span>
            <span class="desc-link">— ${e.summary}</span>
          </a>
        `).join('')}
      </div>
    `;
  }
  return "<p>No related endpoints configured.</p>";
}

// Render Quote Data Contract Box Views
function renderQuoteContractBox() {
  const activeSection = document.getElementById(`endpoint-${activePageId}`);
  if (!activeSection) return;
  const container = activeSection.querySelector(".schema-view-box");
  if (!container) return;

  const programAccess = (activeContractProgram === 'abbeygate-motor' || activeContractProgram === 'cooper-ninove' || activeContractProgram === 'client-commercial') ? 'partner' : 'public';
  const hasProgramAccess = checkAccess(programAccess, activeAccessLevel);
  if (!hasProgramAccess) {
    container.classList.add("gated-overlay-container");
    container.style.minHeight = "240px";
    container.style.position = "relative";
    container.innerHTML = `
      <div style="filter: blur(4px); pointer-events: none; user-select: none; padding: 24px; text-align: center; color: var(--text-muted);">
        <h3>Real Production-Grade Program Contract</h3>
        <p>This program profile uses production-grade rules and underwriting criteria matching Abbeygate/Cooper binding agreements.</p>
      </div>
      ${getGatedOverlayHTML('partner')}
    `;
    return;
  } else {
    container.classList.remove("gated-overlay-container");
    container.style.minHeight = "";
    container.style.position = "";
  }

  const program = productContracts[activeContractProgram];
  if (!program) return;

  const subKey = getActiveContractSubKey(activePageId);
  const contract = program[subKey];
  if (!contract) return;

  if (activeSchemaView === "form") {
    container.innerHTML = contract.formHTML;
  } else if (activeSchemaView === "table") {
    container.innerHTML = `
      <table class="contract-table">
        <thead>
          <tr>
            <th>Field Path</th>
            <th>Type</th>
            <th>Required</th>
            <th>Validation</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          ${contract.fields.map(f => `
            <tr>
              <td class="field-path">${f.path}</td>
              <td class="field-type">${f.type}</td>
              <td><span class="field-required ${f.required}">${f.required}</span></td>
              <td class="field-validation">${f.validation}</td>
              <td class="field-desc">${f.desc}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>`;
  } else if (activeSchemaView === "schema") {
    container.innerHTML = `<pre class="schema-pre"><code class="language-json">${JSON.stringify(contract.schema, null, 2)}</code></pre>`;
  } else if (activeSchemaView === "payload") {
    container.innerHTML = `<pre class="schema-pre"><code class="language-json">${JSON.stringify(contract.payload, null, 2)}</code></pre>`;
  }
}

function restoreRegularConsoleLayout() {
  const overviewPanel = document.getElementById("console-overview-panel");
  if (overviewPanel) overviewPanel.classList.add("hidden");
  
  const header = document.querySelector(".console-header");
  if (header) header.classList.remove("hidden");
  
  const codeViewer = document.querySelector(".code-viewer-container");
  if (codeViewer) codeViewer.classList.remove("hidden");
  
  const authBox = document.querySelector(".auth-box");
  if (authBox) authBox.classList.remove("hidden");
  
  const sandboxTrigger = document.querySelector(".sandbox-trigger-container");
  if (sandboxTrigger) sandboxTrigger.classList.remove("hidden");
  
  const outputContainer = document.querySelector(".console-output-container");
  if (outputContainer) outputContainer.classList.remove("hidden");
}

function updateConsoleForRestEndpoint(endpoint) {
  updateCodeSnippet();

  const responseBlock = document.getElementById("response-output-block");
  if (responseBlock) {
    responseBlock.innerText = `Click "Try It Out" to see raw HTTP response metrics and JSON payload.`;
  }
  const responseMeta = document.getElementById("response-meta");
  if (responseMeta) {
    responseMeta.innerHTML = "";
  }

  const idempotencyRow = document.getElementById("auth-row-idempotency");
  if (idempotencyRow) {
    const isWrite = ["POST", "PUT", "PATCH", "DELETE"].includes(endpoint.method);
    if (isWrite) {
      idempotencyRow.classList.remove("hidden");
      const keyInput = document.getElementById("idempotency-key-input");
      if (keyInput && !keyInput.value) {
        keyInput.value = generateGUID().slice(0, 18);
      }
    } else {
      idempotencyRow.classList.add("hidden");
    }
  }

  const confirmContainer = document.getElementById("mcp-confirm-container");
  if (confirmContainer) {
    confirmContainer.classList.add("hidden");
  }

  const nextAction = document.getElementById("next-action-container");
  if (nextAction) {
    nextAction.classList.add("hidden");
  }
}

function updateConsoleForMcpTool(tool) {
  updateCodeSnippet();

  const responseBlock = document.getElementById("response-output-block");
  if (responseBlock) {
    responseBlock.innerText = `Click "Try It Out" to execute JSON-RPC tool transaction.`;
  }
  const responseMeta = document.getElementById("response-meta");
  if (responseMeta) {
    responseMeta.innerHTML = "";
  }

  const idempotencyRow = document.getElementById("auth-row-idempotency");
  if (idempotencyRow) {
    const isMutation = ["mutate", "comm", "publish"].includes(tool.auditClass);
    if (isMutation) {
      idempotencyRow.classList.remove("hidden");
      const keyInput = document.getElementById("idempotency-key-input");
      if (keyInput && !keyInput.value) {
        keyInput.value = "lock_" + generateGUID().slice(0, 10);
      }
    } else {
      idempotencyRow.classList.add("hidden");
    }
  }

  const confirmContainer = document.getElementById("mcp-confirm-container");
  if (confirmContainer) {
    confirmContainer.classList.add("hidden");
  }

  const nextAction = document.getElementById("next-action-container");
  if (nextAction) {
    nextAction.classList.add("hidden");
  }
}

function renderOverviewConsoleCards(mode) {
  const panel = document.getElementById("console-overview-panel");
  if (!panel) return;
  panel.classList.remove("hidden");
  
  const header = document.querySelector(".console-header");
  if (header) header.classList.add("hidden");
  
  const codeViewer = document.querySelector(".code-viewer-container");
  if (codeViewer) codeViewer.classList.add("hidden");
  
  const authBox = document.querySelector(".auth-box");
  if (authBox) authBox.classList.add("hidden");
  
  const sandboxTrigger = document.querySelector(".sandbox-trigger-container");
  if (sandboxTrigger) sandboxTrigger.classList.add("hidden");
  
  const outputContainer = document.querySelector(".console-output-container");
  if (outputContainer) outputContainer.classList.add("hidden");

  const confirmContainer = document.getElementById("mcp-confirm-container");
  if (confirmContainer) confirmContainer.classList.add("hidden");

  const nextAction = document.getElementById("next-action-container");
  if (nextAction) nextAction.classList.add("hidden");

  if (mode === "rest") {
    panel.innerHTML = `
      <div class="overview-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: var(--border-radius-md); padding: 20px;">
        <h4 style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; font-weight: 700; letter-spacing: 0.5px;">Base URL</h4>
        <div style="font-family: var(--font-mono); font-size: 12.5px; color: var(--accent-cyan); word-break: break-all;">https://sandbox.facio.io/api</div>
      </div>
      
      <div class="overview-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: var(--border-radius-md); padding: 20px;">
        <h4 style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; font-weight: 700; letter-spacing: 0.5px;">Authentication</h4>
        <div style="font-family: var(--font-mono); font-size: 12px; color: var(--text-secondary); margin-bottom: 6px;">x-api-key: facio_test_...</div>
        <div style="font-size: 12.5px; color: var(--text-muted);">Pass your secret API key in custom HTTP request headers.</div>
      </div>

      <div class="overview-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: var(--border-radius-md); padding: 20px;">
        <h4 style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; font-weight: 700; letter-spacing: 0.5px;">Current Version</h4>
        <div style="font-family: var(--font-mono); font-size: 12px; color: var(--text-secondary);">v1.0.5</div>
      </div>

      <div class="overview-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: var(--border-radius-md); padding: 20px;">
        <h4 style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; font-weight: 700; letter-spacing: 0.5px;">Core Workflow</h4>
        <p style="font-size: 12.5px; color: var(--text-secondary); line-height: 1.5; margin: 0;">
          Discover Program &rarr; Dynamic Quote &rarr; Bind Policy &rarr; Service MTA &rarr; Process Claim &rarr; Report BDX
        </p>
      </div>

      <div class="overview-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: var(--border-radius-md); padding: 20px;">
        <h4 style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; font-weight: 700; letter-spacing: 0.5px;">Dynamic Product Schemas</h4>
        <p style="font-size: 12.5px; color: var(--text-secondary); line-height: 1.5; margin: 0;">
          Facio programs publish dynamic <code>quoteData</code> specifications as standard JSON Schema. Select your product profile under endpoints to view.
        </p>
      </div>
    `;
  } else {
    panel.innerHTML = `
      <div class="overview-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: var(--border-radius-md); padding: 20px;">
        <h4 style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; font-weight: 700; letter-spacing: 0.5px;">MCP Mount URLs</h4>
        <div style="margin-bottom: 12px;">
          <span style="font-size: 11px; font-weight: 600; color: var(--text-muted);">Config Agent</span>
          <div style="font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan); word-break: break-all;">https://sandbox.facio.io/api/v1/mcp/config</div>
        </div>
        <div>
          <span style="font-size: 11px; font-weight: 600; color: var(--text-muted);">Operator Agent</span>
          <div style="font-family: var(--font-mono); font-size: 12px; color: var(--accent-cyan); word-break: break-all;">https://sandbox.facio.io/api/v1/mcp/operator</div>
        </div>
      </div>

      <div class="overview-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: var(--border-radius-md); padding: 20px;">
        <h4 style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; font-weight: 700; letter-spacing: 0.5px;">Agent Auths</h4>
        <div style="font-family: var(--font-mono); font-size: 12px; color: var(--text-secondary); margin-bottom: 6px;">FACIO_API_KEY: facio_live_pk_...</div>
        <div style="font-size: 12.5px; color: var(--text-muted);">Configured server transports run discovery protocols governed by tenant secret keys.</div>
      </div>

      <div class="overview-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: var(--border-radius-md); padding: 20px;">
        <h4 style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; font-weight: 700; letter-spacing: 0.5px;">JSON-RPC Transport</h4>
        <p style="font-size: 12.5px; color: var(--text-secondary); line-height: 1.5; margin: 0;">
          Controlled operations execute over standard JSON-RPC 2.0. The server exposes structured tools, accepts parameters, and generates auditable event logs.
        </p>
      </div>

      <div class="overview-card" style="background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: var(--border-radius-md); padding: 20px;">
        <h4 style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; font-weight: 700; letter-spacing: 0.5px;">Governance & Safety</h4>
        <p style="font-size: 12.5px; color: var(--text-secondary); line-height: 1.5; margin: 0;">
          All mutating tools require user confirmations and issue explicit audit events logged directly on the ledger.
        </p>
      </div>
    `;
  }
}

function loadPage(type, id) {
  activePageType = type;
  activePageId = id;

  document.querySelectorAll(".nav-item, .endpoint-nav-item").forEach(el => el.classList.remove("active"));
  const activeNav = document.querySelector(`[data-id="${id}"]`);
  if (activeNav) activeNav.classList.add("active");

  const confirmContainer = document.getElementById("mcp-confirm-container");
  if (confirmContainer) confirmContainer.classList.add("hidden");

  // Sync browser URL hash
  history.replaceState(null, null, '#' + id);

  if (activeMode === "rest") {
    if (currentRenderedMode !== "rest") {
      currentRenderedMode = "rest";
      renderAllRestEndpoints();
    }

    const targetId = type === "concept" ? `concept-${id}` : (type === "model" ? `model-${id}` : `endpoint-${id}`);
    const targetEl = document.getElementById(targetId);
    if (targetEl) {
      isProgrammaticScrolling = true;
      if (programmaticScrollTimeout) clearTimeout(programmaticScrollTimeout);
      targetEl.scrollIntoView({ behavior: "smooth", block: "start" });
      programmaticScrollTimeout = setTimeout(() => {
        isProgrammaticScrolling = false;
      }, 800);
    }

    if (type === "concept" || type === "model") {
      if (id === "overview") {
        renderOverviewConsoleCards("rest");
      } else {
        restoreRegularConsoleLayout();
        const sandboxTrigger = document.querySelector(".sandbox-trigger-container");
        if (sandboxTrigger) sandboxTrigger.classList.add("hidden");
        const authBox = document.querySelector(".auth-box");
        if (authBox) authBox.classList.add("hidden");
        document.getElementById("code-snippet-block").innerText = type === "model" ? `# Data Model Schema: ${id.toUpperCase()}` : `# Conceptual Guide Page: ${id}`;
        document.getElementById("response-output-block").innerText = type === "model" ? `This is a typed schema definition for the ${id.toUpperCase()} object. Reference it when constructing request payloads or parsing responses.` : `Select an API route in the sidebar to try it out.`;
        document.getElementById("response-meta").innerHTML = "";
        const nextAction = document.getElementById("next-action-container");
        if (nextAction) nextAction.classList.add("hidden");
      }
    } else {
      restoreRegularConsoleLayout();
      const endpoint = apiEndpoints.find(e => e.id === id);
      if (endpoint) {
        updateConsoleForRestEndpoint(endpoint);
      }
    }
  } else {
    if (currentRenderedMode !== "mcp") {
      currentRenderedMode = "mcp";
      renderAllMcpTools();
    }

    const targetId = type === "concept" ? `concept-${id}` : `endpoint-${id}`;
    const targetEl = document.getElementById(targetId);
    if (targetEl) {
      isProgrammaticScrolling = true;
      if (programmaticScrollTimeout) clearTimeout(programmaticScrollTimeout);
      targetEl.scrollIntoView({ behavior: "smooth", block: "start" });
      programmaticScrollTimeout = setTimeout(() => {
        isProgrammaticScrolling = false;
      }, 800);
    }

    if (type === "concept") {
      if (id === "mcp-overview") {
        renderOverviewConsoleCards("mcp");
      } else {
        restoreRegularConsoleLayout();
        const sandboxTrigger = document.querySelector(".sandbox-trigger-container");
        if (sandboxTrigger) sandboxTrigger.classList.add("hidden");
        const authBox = document.querySelector(".auth-box");
        if (authBox) authBox.classList.add("hidden");
        document.getElementById("code-snippet-block").innerText = `# MCP Configuration Guide: ${id}`;
        document.getElementById("response-output-block").innerText = `Select an MCP tool in the sidebar to try it out.`;
        document.getElementById("response-meta").innerHTML = "";
        const nextAction = document.getElementById("next-action-container");
        if (nextAction) nextAction.classList.add("hidden");
      }
    } else {
      restoreRegularConsoleLayout();
      const tool = mcpTools.find(t => t.id === id);
      if (tool) {
        updateConsoleForMcpTool(tool);
      }
    }
  }
}

function renderAllRestEndpoints() {
  const docContent = document.getElementById("doc-content");
  docContent.innerHTML = "";

  const container = document.createElement("div");
  container.className = "endpoints-scroll-container";

  conceptPages.forEach(concept => {
    const section = document.createElement("section");
    const requiredAccess = getItemAccess("concept", concept.id);
    const hasAccess = checkAccess(requiredAccess, activeAccessLevel);
    section.className = "concept-scroll-section" + (!hasAccess ? " gated-overlay-container" : "");
    section.id = `concept-${concept.id}`;
    section.setAttribute("data-id", concept.id);
    section.innerHTML = `
      <article class="doc-section" style="${!hasAccess ? 'filter: blur(4px); pointer-events: none; user-select: none;' : ''}">
        <h1>${concept.title} ${getAccessBadgeHTML(requiredAccess)}</h1>
        <div>${concept.description}</div>
      </article>
      ${!hasAccess ? getGatedOverlayHTML(requiredAccess) : ''}
      <hr style="border: none; border-bottom: 1px solid var(--border-color); margin: 40px 0;">
    `;
    container.appendChild(section);
  });

  apiEndpoints.forEach(endpoint => {
    const requiredAccess = getItemAccess("api", endpoint.id);
    const hasAccess = checkAccess(requiredAccess, activeAccessLevel);

    const section = document.createElement("section");
    section.className = "endpoint-scroll-section" + (!hasAccess ? " gated-overlay-container" : "");
    section.id = `endpoint-${endpoint.id}`;
    section.setAttribute("data-id", endpoint.id);
    
    const isDynamic = !!endpoint.hasDynamicSchema;
    
    const headersHTML = `
      <div class="endpoint-subsection-container" style="margin-bottom: 20px;">
        <div class="endpoint-subsection-title" style="font-size: 11px; font-weight: 700; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Headers</div>
        <table class="parameter-table">
          <tbody>
            <tr class="parameter-row">
              <td class="parameter-meta"><span class="param-name">x-api-key</span><span class="param-type">header · string</span><span class="param-required">Required</span></td>
              <td class="param-description">Developer API key issued for your Facio environment.</td>
            </tr>
            <tr class="parameter-row">
              <td class="parameter-meta"><span class="param-name">x-tenant-id</span><span class="param-type">header · string</span><span>Optional</span></td>
              <td class="param-description">Dedicated tenant context when Facio has provisioned one.</td>
            </tr>
            <tr class="parameter-row">
              <td class="parameter-meta"><span class="param-name">x-correlation-id</span><span class="param-type">header · UUID</span><span>Optional</span></td>
              <td class="param-description">Caller-supplied trace identifier for support and audit correlation.</td>
            </tr>
            ${(endpoint.method === "POST" || endpoint.method === "PATCH" || endpoint.method === "PUT") ? `
            <tr class="parameter-row">
              <td class="parameter-meta"><span class="param-name">x-idempotency-key</span><span class="param-type">header · string</span><span>Optional</span></td>
              <td class="param-description">Unique UUID token to prevent execution of duplicate state-mutating actions.</td>
            </tr>
            ` : ''}
          </tbody>
        </table>
      </div>
    `;

    const pathParams = endpoint.parameters ? endpoint.parameters.filter(p => p.in === "path") : [];
    const pathParamsHTML = `
      <div class="endpoint-subsection-container" style="margin-bottom: 20px;">
        <div class="endpoint-subsection-title" style="font-size: 11px; font-weight: 700; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Path Parameters</div>
        ${pathParams.length > 0 ? `
          <table class="parameter-table">
            <tbody>
              ${pathParams.map(p => `
                <tr class="parameter-row">
                  <td class="parameter-meta"><span class="param-name">${p.name}</span><span class="param-type">${p.type}</span>${p.required ? `<span class="param-required">Required</span>` : ""}</td>
                  <td class="param-description">${p.description}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : `<p class="endpoint-section-desc" style="color: var(--text-muted); font-size: 13.5px; margin: 4px 0 0 0;">No path parameters required.</p>`}
      </div>
    `;

    const queryParams = endpoint.parameters ? endpoint.parameters.filter(p => p.in === "query") : [];
    const queryParamsHTML = `
      <div class="endpoint-subsection-container" style="margin-bottom: 20px;">
        <div class="endpoint-subsection-title" style="font-size: 11px; font-weight: 700; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Query Parameters</div>
        ${queryParams.length > 0 ? `
          <table class="parameter-table">
            <tbody>
              ${queryParams.map(p => `
                <tr class="parameter-row">
                  <td class="parameter-meta"><span class="param-name">${p.name}</span><span class="param-type">${p.type}</span>${p.required ? `<span class="param-required">Required</span>` : ""}</td>
                  <td class="param-description">${p.description}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : `<p class="endpoint-section-desc" style="color: var(--text-muted); font-size: 13.5px; margin: 4px 0 0 0;">No query parameters required.</p>`}
      </div>
    `;

    let requestBodyHTML = "";
    if (isDynamic) {
      let envelopeRows = "";
      if (endpoint.id === "create-quote") {
        envelopeRows = `
          <tr class="parameter-row"><td class="parameter-meta"><span class="param-name">programId</span><span class="param-type">string</span><span class="param-required">Required</span></td><td class="param-description">The UUID of the distribution program.</td></tr>
          <tr class="parameter-row"><td class="parameter-meta"><span class="param-name">effectiveDate</span><span class="param-type">string</span><span class="param-required">Required</span></td><td class="param-description">ISO 8601 timestamp for policy inception start.</td></tr>
          <tr class="parameter-row"><td class="parameter-meta"><span class="param-name">quoteData</span><span class="param-type">object</span><span class="param-required">Required</span></td><td class="param-description">Dynamic product contract containing risk answers.</td></tr>
        `;
      } else if (endpoint.id === "quote-endorsement") {
        envelopeRows = `
          <tr class="parameter-row"><td class="parameter-meta"><span class="param-name">effectiveDate</span><span class="param-type">string</span><span class="param-required">Required</span></td><td class="param-description">ISO 8601 timestamp for endorsement start.</td></tr>
          <tr class="parameter-row"><td class="parameter-meta"><span class="param-name">changes</span><span class="param-type">object</span><span class="param-required">Required</span></td><td class="param-description">Dynamic product contract adjustments.</td></tr>
        `;
      } else if (endpoint.id === "submit-fnol") {
        envelopeRows = `
          <tr class="parameter-row"><td class="parameter-meta"><span class="param-name">policyId</span><span class="param-type">string</span><span class="param-required">Required</span></td><td class="param-description">The UUID of the policy where the loss incident occurred.</td></tr>
          <tr class="parameter-row"><td class="parameter-meta"><span class="param-name">intake</span><span class="param-type">object</span><span class="param-required">Required</span></td><td class="param-description">Dynamic product contract containing loss details.</td></tr>
        `;
      } else if (endpoint.id === "submit-renewal-declaration") {
        envelopeRows = `
          <tr class="parameter-row"><td class="parameter-meta"><span class="param-name">unchanged</span><span class="param-type">boolean</span><span class="param-required">Required</span></td><td class="param-description">Confirmation that the risk factors match active declarations.</td></tr>
        `;
      }

      requestBodyHTML = `
        <div class="endpoint-subsection-container" style="margin-bottom: 20px;">
          <div class="endpoint-subsection-title" style="font-size: 11px; font-weight: 700; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Request Body</div>
          <table class="parameter-table" style="margin-bottom: 24px;"><tbody>${envelopeRows}</tbody></table>
          <div class="quote-contract-container">
            <div class="quote-contract-header">
              <div class="contract-title">quoteData contract (Example Templates)</div>
              <div class="contract-program-selector-row">
                <label for="contract-program-select-${endpoint.id}">Product Profile (Demo Templates)</label>
                <select id="contract-program-select-${endpoint.id}" class="contract-select contract-program-select-field">
                  <option value="private-motor" ${activeContractProgram === 'private-motor' ? 'selected' : ''}>Private Motor Demo</option>
                  <option value="pet" ${activeContractProgram === 'pet' ? 'selected' : ''}>Pet Demo</option>
                  <option value="travel" ${activeContractProgram === 'travel' ? 'selected' : ''}>Travel Demo</option>
                  <option value="builders-risk" ${activeContractProgram === 'builders-risk' ? 'selected' : ''}>Builder’s Risk Demo</option>
                  <option value="commercial-property" ${activeContractProgram === 'commercial-property' ? 'selected' : ''}>Commercial Property Demo</option>
                  <option value="abbeygate-motor" ${activeContractProgram === 'abbeygate-motor' ? 'selected' : ''}>🔒 Abbeygate Cyprus Motor (Partner)</option>
                  <option value="cooper-ninove" ${activeContractProgram === 'cooper-ninove' ? 'selected' : ''}>🔒 Cooper Ninove (Partner)</option>
                  <option value="client-commercial" ${activeContractProgram === 'client-commercial' ? 'selected' : ''}>🔒 Client-specific Commercial (Partner)</option>
                </select>
              </div>
            </div>
            
            <div style="font-size: 12px; font-weight: 600; color: var(--accent-cyan); padding: 8px 16px 0 16px;">
              ${(activeContractProgram === 'abbeygate-motor' || activeContractProgram === 'cooper-ninove' || activeContractProgram === 'client-commercial') ? 
                '🔒 Production-grade partner contract schema.' : 
                '👉 Illustrative sandbox program. Not a live insurance product.'}
            </div>
            
            <p style="font-size: 12.5px; color: var(--text-helper); margin: 12px 16px; line-height: 1.5;">
              <strong>Note on Custom Products:</strong> These product profiles are standard templates provided for demonstration and testing purposes. Because each Facio tenant maintains their own unique products and custom JSON-Schema fields, your actual dedicated environment will expect schemas and answers matched specifically to your coverholder binding contracts.
            </p>
            
            <div class="schema-tabs-row">
              <div class="schema-tabs">
                <button class="schema-tab-btn ${activeSchemaView === 'form' ? 'active' : ''}" data-view="form">Visual Form</button>
                <button class="schema-tab-btn ${activeSchemaView === 'table' ? 'active' : ''}" data-view="table">Field Table</button>
                <button class="schema-tab-btn ${activeSchemaView === 'schema' ? 'active' : ''}" data-view="schema">JSON Schema</button>
                <button class="schema-tab-btn ${activeSchemaView === 'payload' ? 'active' : ''}" data-view="payload">Example Payload</button>
              </div>
            </div>

            <div class="schema-view-box">
              <!-- Dynamically populated schema view box -->
            </div>
          </div>
        </div>
      `;
    } else {
      if (endpoint.requestBody) {
        requestBodyHTML = `
          <div class="endpoint-subsection-container" style="margin-bottom: 20px;">
            <div class="endpoint-subsection-title" style="font-size: 11px; font-weight: 700; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Request Body</div>
            <p style="font-size: 13.5px; color: var(--text-secondary); margin-bottom: 12px;">
              ${endpoint.requestBody.description || "JSON request payload."}
            </p>
            <pre class="tour-code-block"><code class="language-json">${JSON.stringify(endpoint.requestBody.fields, null, 2)}</code></pre>
          </div>
        `;
      }
    }

    const responseHTML = `
      <div class="endpoint-subsection-container" style="margin-bottom: 20px;">
        <div class="endpoint-subsection-title" style="font-size: 11px; font-weight: 700; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Response</div>
        <p style="font-size: 13.5px; color: var(--text-secondary); margin-bottom: 12px;">
          Returns a JSON envelope containing the resource state or operation results.
        </p>
        <details class="response-collapsed-details">
          <summary class="response-collapsed-summary">View Response Payload (${endpoint.response.status} OK)</summary>
          <pre class="tour-code-block"><code class="language-json">${JSON.stringify(endpoint.response.body, null, 2)}</code></pre>
        </details>
      </div>
    `;

    const relatedHTML = `
      <div class="endpoint-subsection-container" style="margin-bottom: 20px;">
        <div class="endpoint-subsection-title" style="font-size: 11px; font-weight: 700; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Related Endpoints</div>
        ${getRelatedEndpointsHTML(endpoint)}
      </div>
    `;

    const errorCodesHTML = endpoint.errorCodes && endpoint.errorCodes.length > 0 ? `
      <div class="endpoint-subsection-container" style="margin-bottom: 20px;">
        <div class="endpoint-subsection-title" style="font-size: 11px; font-weight: 700; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Error Codes</div>
        <table class="parameter-table">
          <tbody>
            ${endpoint.errorCodes.map(err => `
              <tr class="parameter-row">
                <td class="parameter-meta" style="width: 200px;">
                  <span class="status-badge status-4xx" style="margin-right: 8px;">${err.status}</span>
                  <code style="font-family: var(--font-mono); font-size: 11.5px; font-weight: 600; color: var(--text-primary);">${err.code}</code>
                </td>
                <td class="param-description">${err.description}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    ` : '';

    section.innerHTML = `
      <div style="${!hasAccess ? 'filter: blur(4px); pointer-events: none; user-select: none;' : ''}">
        <div class="api-header-card">
          <div class="api-route-badge-row">
            <span class="method-badge ${endpoint.method.toLowerCase()}">${endpoint.method}</span>
            <span class="route-path-title">${endpoint.path}</span>
            ${isDynamic ? `<span class="badge-dynamic-schema" style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: var(--accent-purple); border: 1px solid var(--accent-purple); padding: 2px 6px; border-radius: 4px; line-height: 1;">Dynamic Schema</span>` : ""}
            ${endpoint.deprecated ? `<span class="badge-deprecated" style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: var(--text-muted); border: 1px solid var(--border-color); padding: 2px 6px; border-radius: 4px; line-height: 1;">Deprecated</span>` : ""}
            ${getAccessBadgeHTML(requiredAccess)}
          </div>
          <h2 style="margin-top: 0; border-bottom: none; padding-bottom: 0;">${endpoint.summary}</h2>
          <p style="font-size: 14.5px; color: var(--text-secondary); line-height: 1.6; margin-top: 8px;">
            ${endpoint.description}
          </p>
        </div>

        ${headersHTML}
        ${pathParamsHTML}
        ${queryParamsHTML}
        ${requestBodyHTML}
        ${responseHTML}
        ${errorCodesHTML}
        ${relatedHTML}
      </div>
      ${!hasAccess ? getGatedOverlayHTML(requiredAccess) : ''}
      <hr style="border: none; border-bottom: 1px solid var(--border-color); margin: 40px 0;">
    `;

    container.appendChild(section);
  });

  // Append Data Models Sections
  dataModels.forEach(model => {
    const requiredAccess = getItemAccess("model", model.id);
    const hasAccess = checkAccess(requiredAccess, activeAccessLevel);

    const section = document.createElement("section");
    section.className = "model-scroll-section" + (!hasAccess ? " gated-overlay-container" : "");
    section.id = `model-${model.id}`;
    section.setAttribute("data-id", model.id);
    section.innerHTML = `
      <article class="doc-section" style="${!hasAccess ? 'filter: blur(4px); pointer-events: none; user-select: none;' : ''}">
        <h1 style="display: flex; align-items: center; gap: 8px;">
          <span style="font-size: 11px; font-weight: 700; color: var(--accent-purple); border: 1px solid var(--accent-purple); padding: 2px 6px; border-radius: 4px; text-transform: uppercase;">Schema</span>
          ${model.title}
          ${getAccessBadgeHTML(requiredAccess)}
        </h1>
        <p style="font-size: 14.5px; color: var(--text-secondary); line-height: 1.6; margin-top: 8px;">
          ${model.description}
        </p>
        <div style="margin-top: 20px; overflow-x: auto;">
          <table class="parameter-table">
            <thead>
              <tr style="border-bottom: 2px solid var(--border-color); text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted);">
                <th style="padding: 10px; min-width: 150px;">Field Name</th>
                <th style="padding: 10px; min-width: 150px;">Type / Properties</th>
                <th style="padding: 10px; min-width: 120px;">Lloyd's CR Code</th>
                <th style="padding: 10px;">Description</th>
              </tr>
            </thead>
            <tbody>
              ${model.fields.map(f => `
                <tr class="parameter-row" style="border-bottom: 1px solid var(--border-color);">
                  <td class="parameter-meta" style="padding: 10px; font-weight: 600; font-family: var(--font-mono); font-size: 13px; color: var(--accent-cyan);">${f.name}</td>
                  <td style="padding: 10px; white-space: nowrap;">
                    <span class="param-type" style="font-family: var(--font-mono); font-size: 12px;">${f.type}</span>
                    ${f.nullable ? `<span class="badge-nullable">Nullable</span>` : `<span class="badge-nullable" style="color: var(--accent-cyan); border-color: rgba(6, 182, 212, 0.2); background: rgba(6, 182, 212, 0.02);">Required</span>`}
                    ${f.enum ? `<span class="badge-enum">Enum</span>` : ""}
                  </td>
                  <td style="padding: 10px; font-family: var(--font-mono); font-size: 12px;">
                    ${f.lloydsCRCode ? `<span style="display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.3); color: var(--accent-purple);">${f.lloydsCRCode}</span>` : `<span style="color: var(--text-muted); font-style: italic;">—</span>`}
                  </td>
                  <td style="padding: 10px; color: var(--text-secondary); font-size: 13.5px; line-height: 1.5;">
                    ${f.description}
                    ${f.enum ? `<div style="margin-top: 6px; font-size: 11.5px; color: var(--text-muted); font-family: var(--font-mono);">Allowed Values: ${f.enum.map(e => `<code>${e}</code>`).join(' | ')}</div>` : ""}
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </article>
      ${!hasAccess ? getGatedOverlayHTML(requiredAccess) : ''}
      <hr style="border: none; border-bottom: 1px solid var(--border-color); margin: 40px 0;">
    `;
    container.appendChild(section);
  });

  docContent.appendChild(container);

  // Hook up event listeners for newly rendered dropdowns or tabs
  container.querySelectorAll(".contract-program-select-field").forEach(sel => {
    sel.addEventListener("change", (e) => {
      activeContractProgram = e.target.value;
      container.querySelectorAll(".contract-program-select-field").forEach(s => {
        s.value = activeContractProgram;
      });
      // Re-render active contract box and code snippets
      renderQuoteContractBox();
      updateCodeSnippet();
    });
  });

  container.querySelectorAll(".schema-tab-btn").forEach(btn => {
    btn.addEventListener("click", (e) => {
      e.target.closest(".schema-tabs").querySelectorAll(".schema-tab-btn").forEach(b => b.classList.remove("active"));
      e.target.classList.add("active");
      activeSchemaView = e.target.getAttribute("data-view");
      renderQuoteContractBox();
    });
  });

  renderQuoteContractBox();
}

function renderAllMcpTools() {
  const docContent = document.getElementById("doc-content");
  docContent.innerHTML = "";

  const container = document.createElement("div");
  container.className = "mcp-scroll-container";

  // 1. Render all MCP guides
  mcpGuides.forEach(guide => {
    const requiredAccess = getItemAccess("concept", guide.id);
    const hasAccess = checkAccess(requiredAccess, activeAccessLevel);

    const section = document.createElement("section");
    section.className = "concept-scroll-section" + (!hasAccess ? " gated-overlay-container" : "");
    section.id = `concept-${guide.id}`;
    section.setAttribute("data-id", guide.id);
    section.innerHTML = `
      <article class="doc-section" style="${!hasAccess ? 'filter: blur(4px); pointer-events: none; user-select: none;' : ''}">
        <h1>${guide.title} ${getAccessBadgeHTML(requiredAccess)}</h1>
        <div>${guide.description}</div>
      </article>
      ${!hasAccess ? getGatedOverlayHTML(requiredAccess) : ''}
      <hr style="border: none; border-bottom: 1px solid var(--border-color); margin: 40px 0;">
    `;
    container.appendChild(section);
  });

  // 2. Render all MCP tools
  mcpTools.forEach(tool => {
    const requiredAccess = getItemAccess("api", tool.id);
    const hasAccess = checkAccess(requiredAccess, activeAccessLevel);

    const section = document.createElement("section");
    section.className = "endpoint-scroll-section" + (!hasAccess ? " gated-overlay-container" : "");
    section.id = `endpoint-${tool.id}`;
    section.setAttribute("data-id", tool.id);

    // 15-Point Tool specification HTML
    const specHTML = `
      <div class="mcp-tool-card" style="margin-top: 24px; margin-bottom: 24px; padding: 20px;">
        <div class="mcp-tool-card-header" style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;">
          <div class="mcp-tool-name" style="font-family: var(--font-mono); font-size: 14px; font-weight: 700; color: var(--text-primary);">${tool.wireName}</div>
          <div class="mcp-tool-card-badges" style="display: flex; gap: 8px;">
            <span class="audit-class-badge ${tool.auditClass}">${tool.auditClass}</span>
            <span class="badge-risk" style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: var(--text-secondary); border: 1px solid var(--border-color); padding: 2px 6px; border-radius: 4px;">${tool.riskLevel}</span>
          </div>
        </div>
        <p class="mcp-tool-desc" style="font-size: 13.5px; color: var(--text-secondary); margin-bottom: 16px;">${tool.description}</p>
        
        <div style="font-size: 13px; color: var(--text-secondary); margin-bottom: 16px;">
          <span style="font-weight: 600; color: var(--text-primary);">Canonical Name:</span> <code>${tool.canonicalName}</code>
        </div>
        
        <div class="mcp-schema-container" style="background-color: var(--bg-input); border: 1px solid var(--border-color); border-radius: var(--border-radius-sm); padding: 12px; margin-bottom: 12px;">
          <div class="mcp-schema-title" style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; letter-spacing: 0.5px;">Input Schema</div>
          <pre class="mcp-schema-code" style="margin: 0; overflow-x: auto;"><code style="font-family: var(--font-mono); font-size: 11.5px; color: #a5b4fc;">${tool.inputSchema}</code></pre>
        </div>

        <div class="mcp-schema-container" style="background-color: var(--bg-input); border: 1px solid var(--border-color); border-radius: var(--border-radius-sm); padding: 12px; margin-bottom: 12px;">
          <div class="mcp-schema-title" style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; letter-spacing: 0.5px;">Output Schema</div>
          <pre class="mcp-schema-code" style="margin: 0; overflow-x: auto;"><code style="font-family: var(--font-mono); font-size: 11.5px; color: #a5b4fc;">${tool.outputSchema}</code></pre>
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px; font-size: 13px; color: var(--text-secondary);">
          <div><strong>Required Scope:</strong> <code>${tool.requiredPermission}</code></div>
          <div><strong>Audit Event:</strong> <code>${tool.auditEventProduced}</code></div>
        </div>

        <div style="margin-top: 12px; font-size: 13px; color: var(--text-secondary);">
          <strong>Governance checks:</strong> <span style="color: var(--accent-magenta); font-weight: 500;">${tool.governance}</span>
        </div>
        <div style="margin-top: 6px; font-size: 13px; color: var(--text-secondary);">
          <strong>Typical Flow:</strong> <span style="font-family: var(--font-mono); color: var(--accent-cyan); font-size: 12px;">${tool.typicalFlow}</span>
        </div>
      </div>
    `;

    const errorCodesHTML = tool.errorCodes && tool.errorCodes.length > 0 ? `
      <div class="endpoint-subsection-container" style="margin-top: 24px; margin-bottom: 20px;">
        <div class="endpoint-subsection-title" style="font-size: 11px; font-weight: 700; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Error Codes</div>
        <table class="parameter-table">
          <tbody>
            ${tool.errorCodes.map(err => `
              <tr class="parameter-row">
                <td class="parameter-meta" style="width: 200px;">
                  <span class="status-badge status-4xx" style="margin-right: 8px;">${err.status}</span>
                  <code style="font-family: var(--font-mono); font-size: 11.5px; font-weight: 600; color: var(--text-primary);">${err.code}</code>
                </td>
                <td class="param-description">${err.description}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    ` : '';

    section.innerHTML = `
      <div style="${!hasAccess ? 'filter: blur(4px); pointer-events: none; user-select: none;' : ''}">
        <div class="api-header-card">
          <div class="api-route-badge-row">
            <span class="method-badge mcp-tool">MCP TOOL</span>
            <span class="route-path-title">${tool.canonicalName}</span>
            ${tool.auditClass === 'mutate' || tool.auditClass === 'comm' ? `<span class="badge-requires-confirm" style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: var(--accent-magenta); border: 1px solid var(--accent-magenta); padding: 2px 6px; border-radius: 4px; line-height: 1;">Requires confirmation</span>` : ""}
            ${getAccessBadgeHTML(requiredAccess)}
          </div>
          <h2 style="margin-top: 0; border-bottom: none; padding-bottom: 0;">${tool.canonicalName}</h2>
          <p style="font-size: 14.5px; color: var(--text-secondary); line-height: 1.6; margin-top: 8px;">
            ${tool.description}
          </p>
        </div>

        ${specHTML}
        ${errorCodesHTML}
      </div>
      ${!hasAccess ? getGatedOverlayHTML(requiredAccess) : ''}
      <hr style="border: none; border-bottom: 1px solid var(--border-color); margin: 40px 0;">
    `;

    container.appendChild(section);
  });

  docContent.appendChild(container);
}

function updateCodeSnippet() {
  if (activePageType !== "api") return;

  if (activeMode === "rest") {
    const endpoint = apiEndpoints.find(e => e.id === activePageId);
    if (!endpoint) return;
    document.getElementById("code-snippet-block").innerText = generateCodeSnippet(endpoint, activeSnippetLang);
  } else {
    const tool = mcpTools.find(t => t.id === activePageId);
    if (!tool) return;
    document.getElementById("code-snippet-block").innerText = generateMcpCodeSnippet(tool, activeSnippetLang);
  }
}

// Generate REST code snippets
function generateCodeSnippet(endpoint, lang) {
  const baseUrl = "https://developers.facio.io/api/sandbox";
  const url = baseUrl + endpoint.path;
  const method = endpoint.method;
  
  let payloadObj = null;
  if (method === "POST" || method === "PATCH" || method === "PUT") {
    if (endpoint.id === "create-quote") {
      payloadObj = productContracts[activeContractProgram]?.quotes?.payload || null;
    } else if (endpoint.id === "quote-endorsement") {
      payloadObj = productContracts[activeContractProgram]?.mta?.payload || null;
    } else if (endpoint.id === "submit-fnol") {
      payloadObj = productContracts[activeContractProgram]?.claims?.payload || null;
    } else if (endpoint.id === "submit-renewal-declaration") {
      payloadObj = productContracts[activeContractProgram]?.renewals?.payload || null;
    } else if (endpoint.id === "validate-risk-binder") {
      payloadObj = getValidateRiskRequestBody(activeContractProgram);
    } else if (endpoint.id === "create-bdx-export") {
      payloadObj = getBdxExportRequestBody(activeContractProgram);
    } else if (MINIMAL_BODY_ENDPOINTS[endpoint.id]) {
      payloadObj = MINIMAL_BODY_ENDPOINTS[endpoint.id];
    } else {
      payloadObj = endpoint.requestBody?.fields || null;
    }
  }

  const payloadString = payloadObj ? JSON.stringify(payloadObj, null, 2) : "";

  switch (lang) {
    case "curl":
      let curlCmd = `curl -X ${method} "${url}" \\\n  -H "Content-Type: application/json" \\\n  -H "x-api-key: facio_dev_key_..." \\\n  -H "x-tenant-id: tenant_default"`;
      if (payloadString) {
        curlCmd += ` \\\n  -d '${payloadString.split('\n').join('\n  ')}'`;
      }
      return curlCmd;

    case "ts": {
      let tsCode = `const headers: HeadersInit = {\n  "Content-Type": "application/json",\n  "x-api-key": process.env.FACIO_API_KEY ?? "",\n  "x-tenant-id": "tenant_default"\n};\n\nconst request: RequestInit = {\n  method: "${method}",\n  headers`;
      if (payloadString) {
        tsCode += `,\n  body: JSON.stringify(${payloadString.split('\n').join('\n  ')})`;
      }
      tsCode += `\n};\n\nconst response: Response = await fetch("${url}", request);\nconst data: unknown = await response.json();\n\nif (!response.ok) {\n  throw new Error(\`Facio API returned \${response.status}\`);\n}\n\nconsole.log(data);`;
      return tsCode;
    }

    case "python":
      let pyCode = `import requests\n\nurl = "${url}"\nheaders = {\n    "Content-Type": "application/json",\n    "x-api-key": "facio_dev_key_...",\n    "x-tenant-id": "tenant_default"\n}\n`;
      if (payloadString) {
        pyCode += `\npayload = ${payloadString.split('\n').join('\n    ')}\n\nresponse = requests.${method.toLowerCase()}(url, headers=headers, json=payload)`;
      } else {
        pyCode += `\nresponse = requests.${method.toLowerCase()}(url, headers=headers)`;
      }
      return pyCode;

    case "go":
      let goCode = `package main\n\nimport (\n\t"bytes"\n\t"encoding/json"\n\t"net/http"\n)\n\nfunc main() {\n`;
      if (payloadString) {
        goCode += `\tpayload := map[string]interface{}{\n\t\t// ... payload mapping\n\t}\n\t// marshal JSON and issue request ...\n}`;
      } else {
        goCode += `\treq, _ := http.NewRequest("${method}", "${url}", nil)\n\treq.Header.Set("Content-Type", "application/json")\n\tclient := &http.Client{}\n\tclient.Do(req)\n}`;
      }
      return goCode;
  }
}

// Generate MCP code snippets
function generateMcpCodeSnippet(tool, lang) {
  const mountName = tool.family.includes("Config") ? "config" : "operator";
  const requestString = JSON.stringify({
    jsonrpc: "2.0",
    id: "req_002",
    method: "tools/call",
    params: {
      name: tool.wireName,
      arguments: tool.jsonRpcRequest.params.arguments || {}
    }
  }, null, 2);

  const header = `// Canonical name: ${tool.canonicalName}\n// Wire name: ${tool.wireName}\n`;

  switch (lang) {
    case "jsonrpc":
      return header + requestString;

    case "chatgpt":
      return header + `{\n  "name": "${tool.wireName}",\n  "description": "${tool.description}",\n  "parameters": ${tool.inputSchema.split('\n').join('\n  ')}\n}`;

    case "claude":
      return header + `// Add this to your Claude Desktop config file (claude_desktop_config.json):\n{\n  "mcpServers": {\n    "facio-${mountName}": {\n      "command": "npx",\n      "args": ["-y", "@facio/mcp-server", "--family", "${mountName}"],\n      "env": {\n        "FACIO_API_KEY": "facio_live_pk_..."\n      }\n    }\n  }\n}`;

    case "cursor":
      return header + `// Go to Cursor settings -> Features -> MCP -> Add New MCP Server\nName: facio-${mountName}\nType: HTTP\nURL: https://developers.facio.io/api/sandbox/v1/mcp/${mountName}`;

    case "ts":
      return header + `const response = await fetch("https://developers.facio.io/api/sandbox/v1/mcp/${mountName}", {\n  method: "POST",\n  headers: {\n    "Content-Type": "application/json",\n    "x-api-key": "facio_dev_key_..."\n  },\n  body: JSON.stringify(${requestString.split('\n').join('\n  ')})\n});\n\nconst result = await response.json();`;
  }
}

// Try It Out Engine
function showConsoleGatingAlert(access) {
  document.getElementById("response-meta").innerHTML = `
    <span class="status-badge status-4xx" style="background-color: rgba(239, 68, 68, 0.15); color: var(--accent-red); border: 1px solid rgba(239, 68, 68, 0.3);">403 Forbidden</span>
  `;
  document.getElementById("response-output-block").innerHTML = JSON.stringify({
    error: "forbidden",
    status: 403,
    message: `Interactive console execution requires standard '${access}' role credentials.`,
    required_role: access,
    resolved_action: `Upgrade your access level to '${access}' using the dropdown in the header to unlock interactive sandbox API triggers.`
  }, null, 2);
}

function getEndpointRequestBody(endpoint) {
  if (!["POST", "PATCH", "PUT"].includes(endpoint.method)) {
    return undefined;
  }

  if (endpoint.id === "create-quote") return productContracts[activeContractProgram]?.quotes?.payload || {};
  if (endpoint.id === "quote-endorsement") return productContracts[activeContractProgram]?.mta?.payload || {};
  if (endpoint.id === "submit-fnol") return productContracts[activeContractProgram]?.claims?.payload || {};
  if (endpoint.id === "submit-renewal-declaration") return productContracts[activeContractProgram]?.renewals?.payload || {};
  if (endpoint.id === "validate-risk-binder") return getValidateRiskRequestBody(activeContractProgram);
  if (endpoint.id === "create-bdx-export") return getBdxExportRequestBody(activeContractProgram);
  if (MINIMAL_BODY_ENDPOINTS[endpoint.id]) return MINIMAL_BODY_ENDPOINTS[endpoint.id];

  return endpoint.requestBody?.fields || {};
}

function resolveSandboxPath(path) {
  return path
    .replace("{programId}", "e9f8a000-84b2-4d1e-bd15-0d6789b52000")
    .replace("{quoteId}", "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001")
    .replace("{policyId}", "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02")
    .replace("{claimId}", "clm_c8c3d400-55c1-5f1b-c926-2e7890ddcb03");
}

function setConsoleResponse(result) {
  const statusClass = result.ok ? "status-2xx" : "status-4xx";
  document.getElementById("response-meta").innerHTML = `
    <span class="latency-label">${result.latencyMs}ms</span>
    <span class="status-badge ${statusClass}">${result.status} ${result.statusText || ""}</span>
  `;
  document.getElementById("response-output-block").innerText = JSON.stringify(result.body, null, 2);
}

let pendingMcpConfirmation = null;

async function handleTryItOut() {
  if (activePageType !== "api") return;

  // 1. General check: interactive execution requires at least Sandbox role
  if (!checkAccess("sandbox", activeAccessLevel)) {
    showConsoleGatingAlert("sandbox");
    return;
  }

  // 2. Specific check: check if the endpoint/tool itself requires a higher role than current
  const requiredAccess = getItemAccess("api", activePageId);
  if (!checkAccess(requiredAccess, activeAccessLevel)) {
    showConsoleGatingAlert(requiredAccess);
    return;
  }

  const loader = document.getElementById("output-loader");
  loader.classList.remove("hidden");

  try {
    // Dynamic message
    if (activeMode === "rest") {
      const endpoint = apiEndpoints.find(e => e.id === activePageId);
      if (!endpoint) return;

      loader.querySelector("span").innerText = "Executing sandbox REST request...";
      const result = await runSandboxRequest({
        method: endpoint.method,
        path: resolveSandboxPath(endpoint.path),
        body: getEndpointRequestBody(endpoint),
        idempotencyKey: ["POST", "PATCH", "PUT", "DELETE"].includes(endpoint.method) ? createIdempotencyKey("docs_rest") : undefined
      });

      setConsoleResponse(result);

      // Next recommended actions
      const nextContainer = document.getElementById("next-action-container");
      const btnContainer = document.getElementById("next-action-buttons");
      btnContainer.innerHTML = "";

      if (endpoint.extensions?.["x-facio-next-actions"]) {
        nextContainer.classList.remove("hidden");
        endpoint.extensions["x-facio-next-actions"].forEach(act => {
          const btn = document.createElement("button");
          btn.className = "btn btn-next-action";
          btn.innerText = `${act.method} ${act.path} - ${act.label}`;
          btn.addEventListener("click", () => {
            const target = apiEndpoints.find(e => e.path === act.path && e.method === act.method);
            if (target) loadPage("api", target.id);
          });
          btnContainer.appendChild(btn);
        });
      } else {
        nextContainer.classList.add("hidden");
      }
    } else {
      // MCP tool Try It Out runner
      const tool = mcpTools.find(t => t.id === activePageId);
      if (!tool) return;

      loader.querySelector("span").innerText = `Sending JSON-RPC call to ${tool.wireName}...`;
      const mountName = tool.family.includes("Config") ? "config" : "operator";
      const result = await runSandboxRequest({
        method: "POST",
        path: `/v1/mcp/${mountName}`,
        body: {
          jsonrpc: "2.0",
          id: "call_001",
          method: "tools/call",
          params: {
            name: tool.wireName,
            arguments: tool.jsonRpcRequest.params.arguments || {}
          }
        },
        idempotencyKey: createIdempotencyKey("docs_mcp")
      });

      setConsoleResponse(result);

      const responseBody = result.body || {};
      const confirmationToken = responseBody?.result?.confirmation_token;
      const confirmContainer = document.getElementById("mcp-confirm-container");
      if (confirmationToken) {
        pendingMcpConfirmation = { mountName, wireName: tool.wireName, confirmationToken };
        confirmContainer.classList.remove("hidden");
        document.getElementById("confirm-preview-payload-block").innerText = JSON.stringify({
          method: "confirm_tool_invocation",
          arguments: {
            wire_name: tool.wireName,
            confirmation_token: confirmationToken,
            args: tool.jsonRpcRequest.params.arguments || {}
          }
        }, null, 2);
      } else {
        pendingMcpConfirmation = null;
        confirmContainer.classList.add("hidden");
      }
    }
  } catch (err) {
    document.getElementById("response-meta").innerHTML = `
      <span class="status-badge status-4xx">500 Console Error</span>
    `;
    document.getElementById("response-output-block").innerText = JSON.stringify({
      error: "sandbox_console_error",
      message: err.message || "Sandbox request failed."
    }, null, 2);
  } finally {
    loader.classList.add("hidden");
  }
}

// Execute confirmation action
async function executeConfirmAction() {
  const loader = document.getElementById("output-loader");
  loader.classList.remove("hidden");
  loader.querySelector("span").innerText = "Confirming sandbox MCP transaction...";

  if (!pendingMcpConfirmation) {
    loader.classList.add("hidden");
    document.getElementById("response-meta").innerHTML = `
      <span class="status-badge status-4xx">409 No Pending Confirmation</span>
    `;
    document.getElementById("response-output-block").innerText = JSON.stringify({
      error: "no_pending_confirmation",
      message: "Run a mutating MCP tool first to receive a confirmation token."
    }, null, 2);
    return;
  }

  try {
    const result = await runSandboxRequest({
      method: "POST",
      path: `/v1/mcp/${pendingMcpConfirmation.mountName}`,
      body: {
        jsonrpc: "2.0",
        id: "confirm_002",
        method: "tools/call",
        params: {
          name: "confirm_tool_invocation",
          arguments: {
            wire_name: pendingMcpConfirmation.wireName,
            confirmation_token: pendingMcpConfirmation.confirmationToken
          }
        }
      },
      idempotencyKey: createIdempotencyKey("docs_mcp_confirm")
    });

    setConsoleResponse(result);
    pendingMcpConfirmation = null;
    document.getElementById("mcp-confirm-container").classList.add("hidden");
  } catch (err) {
    document.getElementById("response-meta").innerHTML = `
      <span class="status-badge status-4xx">500 Confirm Error</span>
    `;
    document.getElementById("response-output-block").innerText = JSON.stringify({
      error: "sandbox_confirm_error",
      message: err.message || "Sandbox confirmation failed."
    }, null, 2);
  } finally {
    loader.classList.add("hidden");
  }
}

// ===== 6. INTERACTIVE SANDBOX LIFECYCLE FLOW TOUR =====

const tourSteps = [
  {
    step: 1,
    title: "1. Discover Available Programs",
    desc: "First, query the catalog to discover products, binder authorities, regional taxes, and underwriting limits linked to your credentials.",
    method: "GET",
    path: "/v1/programs",
    request: `curl -X GET "https://sandbox.facio.io/api/v1/programs" \\\n  -H "x-api-key: facio_live_pk_..."`,
    response: `{
  "success": true,
  "data": [
    {
      "id": "e9f8a000-84b2-4d1e-bd15-0d6789b52000",
      "name": "EU Motor Scheme v2",
      "productType": "MOTOR",
      "status": "ACTIVE",
      "carrier": "Lloyd's Syndicate 1991",
      "metadata": {
        "jurisdictions": ["CY", "PT", "ES", "GR"],
        "authorityLimitEur": 1000000
      }
    }
  ]
}`
  },
  {
    step: 2,
    title: "2. Generate Underwriting Quote",
    desc: "Submit driving answers against the product schema to trigger real-time rating algorithms. The response provides premium breakdown details, taxes, commissions, and a cryptographically signed bind token.",
    method: "POST",
    path: "/v1/quotes",
    request: `curl -X POST "https://sandbox.facio.io/api/v1/quotes" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "programId": "e9f8a000-84b2-4d1e-bd15-0d6789b52000",\n    "quoteData": {\n      "proposer": {\n        "firstName": "Jane",\n        "lastName": "Doe",\n        "dateOfBirth": "1985-06-15T00:00:00Z",\n        "email": "jane.doe@example.com"\n      },\n      "vehicleValue": 18000,\n      "engineSize": 1600,\n      "licenseYears": 10\n    },\n    "selectedCoverages": ["BASE_LIABILITY", "CV_172"]\n  }'`,
    response: `{
  "success": true,
  "data": {
    "quoteId": "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001",
    "quoteToken": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ2IjoxLCJwdCI6Ik1PVE9SIiwicHJlbWl1bSI6NDg4LjV9...",
    "status": "QUOTED",
    "premiumBreakdown": {
      "grossPremium": 488.50,
      "netPremium": 400.00,
      "commission": 60.00,
      "taxes": { "insurancePremiumTax": 40.00, "stampDuty": 2.00 },
      "fees": { "underwritingFee": 46.50 }
    },
    "expiresAt": "2026-03-31T23:59:59Z"
  }
}`
  },
  {
    step: 3,
    title: "3. Bind Quote & Issue Policy",
    desc: "Post the quote ID and signed JWT token. The database locks the transaction (using x-idempotency-key), performs sanctions screen checks, registers double-entry premiums inside the accounting ledgers, and generates PDF schedules.",
    method: "POST",
    path: "/v1/policies",
    request: `curl -X POST "https://sandbox.facio.io/api/v1/policies" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "quoteId": "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001",\n    "quoteToken": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ2IjoxLCJwdCI6Ik1PVE9SIiwicHJlbWl1bSI6NDg4LjV9...",\n    "paymentMethod": { "type": "CREDIT_CARD", "provider": "CARDCORP", "checkoutId": "cc_chk_facio_test_9482" }\n  }'`,
    response: `{
  "success": true,
  "data": {
    "policyId": "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
    "policyNumber": "POL-2026-84729",
    "certificateNumber": "CERT-CY-48201",
    "status": "ISSUED",
    "effectiveDate": "2026-03-01T00:00:00Z",
    "expirationDate": "2027-02-28T23:59:59Z",
    "ledgerInvoiceId": "inv_2026_948271"
  }
}`
  },
  {
    step: 4,
    title: "4. Perform Mid-Term Adjustment (MTA)",
    desc: "Draft a mid-term adjustment. Altering coverage parameters recalculates pro-rata premium differences over the remaining term. Binding the MTA generates updated endorsement documents.",
    method: "POST",
    path: "/v1/policies/{policyId}/endorsements/quote",
    request: `curl -X POST "https://sandbox.facio.io/api/v1/policies/pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02/endorsements/quote" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "effectiveDate": "2026-06-01T00:00:00Z",\n    "changes": { "vehicleValue": 25000 },\n    "reason": "Upgraded vehicle Trim."\n  }'`,
    response: `{
  "success": true,
  "data": {
    "endorsementId": "mta_948201a0-44c2",
    "status": "QUOTED",
    "premiumDifference": 84.30,
    "effectiveDate": "2026-06-01T00:00:00Z"
  }
}`
  },
  {
    step: 5,
    title: "5. Log Loss Claim (FNOL)",
    desc: "File a claim against the active policy. The claims engine runs validation contracts, sets outstanding Loss Reserves in the ledger, and exposes event logs to adjusters for settlement payment processing.",
    method: "POST",
    path: "/v1/claims/fnol",
    request: `curl -X POST "https://sandbox.facio.io/api/v1/claims/fnol" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "policyId": "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",\n    "intake": {\n      "incident": {\n        "type": "collision",\n        "date": "2026-05-15",\n        "description": "Rear-ended at traffic light by third party."\n      },\n      "driver": { "kind": "named", "id": "driver-jane-doe" }\n    }\n  }'`,
    response: `{
  "success": true,
  "data": {
    "claimId": "clm_c8c3d400-55c1-5f1b-c926-2e7890ddcb03",
    "claimNumber": "CLM-2026-00102",
    "status": "FNOL_RECEIVED",
    "reserves": {
      "outstandingLoss": 3500.00,
      "outstandingExpense": 400.00
    }
  }
}`
  },
  {
    step: 6,
    title: "6. Compile Reinsurance & BDX Report",
    desc: "At the end of the month, query the BDX export to compile risk, premium, and claims ledgers. The engine aggregates data into Lloyd's-compliant BDX files for audit transmissions.",
    method: "GET",
    path: "/v1/bdx/premium",
    request: `curl -X GET "https://sandbox.facio.io/api/v1/bdx/premium?month=2026-03" \\\n  -H "x-api-key: facio_live_pk_..."`,
    response: `{
  "success": true,
  "data": [
    {
      "policyNumber": "POL-2026-84729",
      "umr": "B09482619A01",
      "grossPremium": 488.50,
      "brokerageAmount": 60.00,
      "iptAmount": 40.00,
      "netToLondon": 388.50,
      "mgaFees": 46.50
    }
  ]
}`
  }
];

let activeTourStep = 1;

const tourRequestBodies = {
  2: {
    programId: "e9f8a000-84b2-4d1e-bd15-0d6789b52000",
    quoteData: {
      proposer: {
        firstName: "Jane",
        lastName: "Doe",
        dateOfBirth: "1985-06-15T00:00:00Z",
        email: "jane.doe@example.com"
      },
      vehicleValue: 18000,
      engineSize: 1600,
      licenseYears: 10
    },
    selectedCoverages: ["BASE_LIABILITY", "CV_172"]
  },
  3: {
    quoteId: "q_d4b1a201-44b0-4e0a-bf15-1d6789c52001",
    quoteToken: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.sandbox-bind-token",
    paymentMethod: { type: "CREDIT_CARD", provider: "CARDCORP", checkoutId: "cc_chk_facio_test_9482" }
  },
  4: {
    effectiveDate: "2026-06-01T00:00:00Z",
    changes: { vehicleValue: 25000 },
    reason: "Upgraded vehicle Trim."
  },
  5: {
    policyId: "pol_a7b2c300-44b0-4e0a-bf15-1d6789ccba02",
    intake: {
      incident: {
        type: "collision",
        date: "2026-05-15",
        description: "Rear-ended at traffic light by third party."
      },
      driver: { kind: "named", id: "driver-jane-doe" }
    }
  }
};

function openSandboxTour() {
  const modal = document.getElementById("sandbox-tour-overlay");
  modal.classList.remove("hidden");
  activeTourStep = 1;
  renderTourStep();
}

function closeSandboxTour() {
  document.getElementById("sandbox-tour-overlay").classList.add("hidden");
}

function advanceTourStep() {
  if (activeTourStep < 6) {
    activeTourStep++;
    renderTourStep();
  } else {
    closeSandboxTour();
    loadPage("concept", "overview");
  }
}

function regressTourStep() {
  if (activeTourStep > 1) {
    activeTourStep--;
    renderTourStep();
  }
}

async function executeTourStep(stepData) {
  const responseBlock = document.getElementById("tour-response-code");
  if (!responseBlock) return;

  responseBlock.innerText = "Executing sandbox request...";

  try {
    const path = stepData.step === 6 ? `${resolveSandboxPath(stepData.path)}?month=2026-03` : resolveSandboxPath(stepData.path);
    const result = await runSandboxRequest({
      method: stepData.method,
      path,
      body: tourRequestBodies[stepData.step],
      idempotencyKey: stepData.method === "GET" ? undefined : createIdempotencyKey("tour")
    });

    responseBlock.innerText = JSON.stringify({
      status: result.status,
      latencyMs: result.latencyMs,
      body: result.body
    }, null, 2);
  } catch (err) {
    responseBlock.innerText = JSON.stringify({
      error: "sandbox_tour_error",
      message: err.message || "Sandbox request failed."
    }, null, 2);
  }
}

function renderTourStep() {
  document.querySelectorAll(".timeline-step").forEach(step => {
    const stepNum = parseInt(step.getAttribute("data-step"));
    step.classList.remove("active", "completed");
    
    if (stepNum === activeTourStep) {
      step.classList.add("active");
    } else if (stepNum < activeTourStep) {
      step.classList.add("completed");
    }
  });

  const stepData = tourSteps.find(s => s.step === activeTourStep);
  if (!stepData) return;

  const contentContainer = document.getElementById("tour-step-content");
  contentContainer.innerHTML = `
    <div class="tour-step-title">${stepData.title}</div>
    <div class="tour-step-desc">${stepData.desc}</div>
    
    <div class="tour-columns">
      <div class="tour-request-col">
        <div class="tour-block-label">HTTP Request (${stepData.method} ${stepData.path})</div>
        <pre class="tour-code-block"><code>${stepData.request}</code></pre>
      </div>
      <div class="tour-response-col">
        <div class="tour-block-label">Live Sandbox Response Envelope</div>
        <pre class="tour-code-block"><code id="tour-response-code" style="color: #34d399;">Executing sandbox request...</code></pre>
      </div>
    </div>
  `;

  executeTourStep(stepData);

  const prevBtn = document.getElementById("tour-prev-btn");
  const nextBtn = document.getElementById("tour-next-btn");

  prevBtn.disabled = activeTourStep === 1;

  if (activeTourStep === 6) {
    nextBtn.innerText = "Finish Sandbox Flow";
  } else {
    const nextStepName = tourSteps.find(s => s.step === activeTourStep + 1).title.split('. ')[1];
    nextBtn.innerText = `Next: ${nextStepName} →`;
  }
}

// ===== 5. DEVELOPER PORTAL ACCESS KEY AUTHENTICATION =====
function initDeveloperAuth() {
  const btnAuth = document.getElementById("btn-auth-developer");
  const btnLogout = document.getElementById("btn-auth-logout");
  const devAuthModal = document.getElementById("dev-auth-modal");
  const closeBtn = document.getElementById("dev-auth-close-btn");
  const cancelBtn = document.getElementById("dev-auth-cancel-btn");
  const submitBtn = document.getElementById("dev-auth-submit-btn");
  const keyInput = document.getElementById("dev-auth-key-input");
  const errorDiv = document.getElementById("dev-auth-error");
  const spinner = document.getElementById("dev-auth-spinner");
  const emailBadge = document.getElementById("dev-auth-badge");
  const emailSpan = document.getElementById("dev-auth-email");
  const roleSelect = document.getElementById("role-select");
  const requestSubmitBtn = document.getElementById("dev-request-submit-btn");
  const requestEmailInput = document.getElementById("dev-request-email");
  const requestNameInput = document.getElementById("dev-request-name");
  const requestOrgInput = document.getElementById("dev-request-organization");
  const requestUseCaseInput = document.getElementById("dev-request-use-case");
  let requestedAccessTier = "sandbox";

  if (!btnAuth || !btnLogout || !devAuthModal || !submitBtn || !keyInput) {
    console.warn("Developer Auth DOM elements not found");
    return;
  }

  // Open modal
  btnAuth.addEventListener("click", () => {
    openDevAuthModal();
  });

  window.openDevAuthModal = function(targetRole = "sandbox") {
    requestedAccessTier = targetRole;
    errorDiv.classList.add("hidden");
    errorDiv.innerText = "";
    keyInput.value = "";
    devAuthModal.classList.remove("hidden");
    devAuthModal.style.display = "flex";
    keyInput.focus();
  };

  function showAuthMessage(message, isSuccess = false) {
    errorDiv.innerText = message;
    errorDiv.style.color = isSuccess ? "#50dc8c" : "#ff58a8";
    errorDiv.style.background = isSuccess ? "rgba(80,220,140,0.1)" : "rgba(255,88,168,0.1)";
    errorDiv.style.borderColor = isSuccess ? "rgba(80,220,140,0.25)" : "rgba(255,88,168,0.25)";
    errorDiv.classList.remove("hidden");
  }

  // Close modal
  const closeModal = () => {
    devAuthModal.classList.add("hidden");
    devAuthModal.style.display = "none";
  };

  if (closeBtn) closeBtn.addEventListener("click", closeModal);
  if (cancelBtn) cancelBtn.addEventListener("click", closeModal);
  devAuthModal.addEventListener("click", (e) => {
    if (e.target === devAuthModal) closeModal();
  });

  // Submit key authentication
  submitBtn.addEventListener("click", async () => {
    const key = keyInput.value.trim();
    if (!key) {
      showAuthMessage("Please enter an access key.");
      return;
    }

    errorDiv.classList.add("hidden");
    spinner.classList.remove("hidden");
    submitBtn.disabled = true;

    try {
      const response = await fetch("/api/developer/verify-key", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ accessKey: key })
      });

      const data = await response.json();

      if (!response.ok || !data.valid) {
        throw new Error(data.error || "Authentication failed. Invalid key.");
      }

      const accessTier = normalizeAccessTier(data.accessTier);

      // Save to localStorage
      localStorage.setItem("facio_dev_key", key);
      localStorage.setItem("facio_dev_email", data.email);
      localStorage.setItem("facio_dev_tier", accessTier);

      // Apply authentication
      applyAuth(key, data.email, accessTier);
      closeModal();
    } catch (err) {
      showAuthMessage(err.message || "Failed to connect to authentication server.");
    } finally {
      spinner.classList.add("hidden");
      submitBtn.disabled = false;
    }
  });

  if (requestSubmitBtn) {
    requestSubmitBtn.addEventListener("click", async () => {
      const payload = {
        email: requestEmailInput?.value.trim() || "",
        name: requestNameInput?.value.trim() || "",
        organization: requestOrgInput?.value.trim() || "",
        useCase: requestUseCaseInput?.value.trim() || "",
        targetAccessTier: requestedAccessTier,
        currentPage: `${activePageType}:${activePageId}`
      };

      if (!payload.email) {
        showAuthMessage("Please enter your work email so we can route the request in CRM.");
        return;
      }

      requestSubmitBtn.disabled = true;
      requestSubmitBtn.innerText = "Sending request...";
      errorDiv.classList.add("hidden");

      try {
        const response = await fetch("/api/crm/access-request", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        });
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || "Access request could not be submitted.");
        }

        showAuthMessage(data.message || "Access request received. We will follow up with credentials.", true);
      } catch (err) {
        showAuthMessage(err.message || "Failed to submit access request.");
      } finally {
        requestSubmitBtn.disabled = false;
        requestSubmitBtn.innerText = "Request Access via CRM";
      }
    });
  }

  // Logout
  btnLogout.addEventListener("click", () => {
    localStorage.removeItem("facio_dev_key");
    localStorage.removeItem("facio_dev_email");
    localStorage.removeItem("facio_dev_tier");

    // Revert to public
    applyAuth(null, null, "public");
  });

  // Apply authentication state helper
  function applyAuth(key, email, tier) {
    const accessTier = normalizeAccessTier(tier, "public");
    activeAccessLevel = accessTier;
    
    if (roleSelect) {
      roleSelect.value = accessTier;
      roleSelect.dispatchEvent(new Event("change"));
    }

    if (key && email) {
      emailSpan.innerText = `${email} (${accessTier})`;
      emailBadge.classList.remove("hidden");
      btnLogout.classList.remove("hidden");
      btnAuth.classList.add("hidden");
    } else {
      emailBadge.classList.add("hidden");
      btnLogout.classList.add("hidden");
      btnAuth.classList.remove("hidden");
    }
  }

  // Check storage on load and verify
  async function checkStoredAuth() {
    const key = localStorage.getItem("facio_dev_key");
    const email = localStorage.getItem("facio_dev_email");
    const tier = localStorage.getItem("facio_dev_tier");

    if (key && email && tier) {
      const storedTier = normalizeAccessTier(tier, "public");
      // Optimistically apply first to avoid flicker
      applyAuth(key, email, storedTier);

      // Then verify in background
      try {
        const response = await fetch("/api/developer/verify-key", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ accessKey: key })
        });
        const data = await response.json();
        if (!response.ok || !data.valid) {
          throw new Error("Invalid stored key");
        }
        const accessTier = normalizeAccessTier(data.accessTier);
        // If tier changed, update it
        if (accessTier !== storedTier || data.email !== email) {
          localStorage.setItem("facio_dev_email", data.email);
          localStorage.setItem("facio_dev_tier", accessTier);
          applyAuth(key, data.email, accessTier);
        }
      } catch (e) {
        console.warn("Stored developer key verification failed, logging out:", e);
        localStorage.removeItem("facio_dev_key");
        localStorage.removeItem("facio_dev_email");
        localStorage.removeItem("facio_dev_tier");
        applyAuth(null, null, "public");
      }
    } else {
      applyAuth(null, null, "public");
    }
  }

  checkStoredAuth();
}
